
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import './engine.css';

// ─── STATIC DATA ────────────────────────────────────────────────
const MONTHS=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const MN={JAN:'Jan',FEB:'Feb',MAR:'Mar',APR:'Apr',MAY:'May',JUN:'Jun',JUL:'Jul',AUG:'Aug',SEP:'Sep',OCT:'Oct',NOV:'Nov',DEC:'Dec'};
const MW={JAN:.0528,FEB:.0537,MAR:.0881,APR:.0889,MAY:.0837,JUN:.0648,JUL:.0699,AUG:.0714,SEP:.1076,OCT:.1155,NOV:.1130,DEC:.0906};
const DC={15:.60,20:.75,30:1.00,45:1.30,60:1.60};
const DURO=[5,10,15,20,25,30,35,40,45,50,55,60,65,70,75,80,85,90];
const RC=[[0,0,0],[5,4.2,0],[10,7.7,0],[20,13,.2],[30,16.5,1.2],[40,19.4,2.5],[50,22,3.6],[60,23.5,5.1],[70,24.9,6.5],[80,26.6,7.5],[100,28.7,9.7],[150,31.4,12.7],[200,33.5,15.9],[300,35.2,18.2],[500,37,21],[1000,39.3,24.5]];

// ─── SEASONS & DAYPART ──────────────────────────────────────────
const SEASONS=['spring','summer','autumn','winter'];
const SEASON_LABELS={spring:'Spring (Mar–May)',summer:'Summer (Jun–Aug)',autumn:'Autumn (Sep–Nov)',winter:'Winter (Dec–Feb)'};
const DAYPARTS=['pt','ot'];
const DAYPART_LABELS={pt:'Prime Time',ot:'Off Prime'};
// Month index → season key
function monthToSeason(mi){// mi: 0=Jan..11=Dec
  if(mi>=2&&mi<=4)return'spring';
  if(mi>=5&&mi<=7)return'summer';
  if(mi>=8&&mi<=10)return'autumn';
  return'winter';// 0,1,11
}
// Week (1-52) → month index (0-11)
function weekToMonthIdx(week){return Math.max(0,Math.min(11,Math.floor((week-1)/4.33)));}
// Get weighted season for a flight spanning multiple weeks
// Returns {spring:0.3, summer:0.7} etc
function flightSeasonWeights(startWeek,nWeeks){
  const sw={spring:0,summer:0,autumn:0,winter:0};
  for(let w=0;w<nWeeks;w++){
    const mi=weekToMonthIdx(startWeek+w);
    sw[monthToSeason(mi)]+=1/nWeeks;
  }
  return sw;
}
// Get seasonal AMR for a channel/audience — weighted across flight weeks
function getSeasonalAMR(audSeasonData,chId,ptSplit,startWeek,nWeeks){
  // audSeasonData: {chId: {spring_pt, spring_ot, summer_pt, ...}} or null (use legacy amr)
  if(!audSeasonData||!audSeasonData[chId])return null;
  const d=audSeasonData[chId];
  // If no seasonal data, return null to fall back to legacy
  const hasSeasonal=SEASONS.some(s=>d[s+'_pt']!=null||d[s+'_ot']!=null);
  if(!hasSeasonal)return null;
  const sw=flightSeasonWeights(startWeek,nWeeks);
  let ptAmr=0,otAmr=0;
  SEASONS.forEach(s=>{
    const wt=sw[s]||0;
    ptAmr+=wt*(d[s+'_pt']!=null?d[s+'_pt']:(d.amr||0));
    otAmr+=wt*(d[s+'_ot']!=null?d[s+'_ot']:(d.amr||0));
  });
  return{ptAmr,otAmr};
}
// Get seasonal channel share for a channel/audience — weighted
function getSeasonalShr(audSeasonData,chId,startWeek,nWeeks){
  if(!audSeasonData||!audSeasonData[chId])return null;
  const d=audSeasonData[chId];
  const hasSeasonal=SEASONS.some(s=>d[s+'_shr']!=null);
  if(!hasSeasonal)return null;
  const sw=flightSeasonWeights(startWeek,nWeeks);
  let shr=0;
  SEASONS.forEach(s=>{
    const wt=sw[s]||0;
    shr+=wt*(d[s+'_shr']!=null?d[s+'_shr']:(d.shr||0));
  });
  return shr;
}

// ─── DATE HELPERS ───────────────────────────────────────────────
const YEAR=2026;
function dateToWeek(dateStr){
  if(!dateStr)return null;
  const d=new Date(dateStr);
  const jan1=new Date(YEAR,0,1);
  const dayOfYear=Math.floor((d-jan1)/(86400000));
  return Math.max(1,Math.min(52,Math.ceil((dayOfYear+1)/7)));
}
function weekToDate(week,dayOfWeek=1){// dayOfWeek: 1=Mon
  const jan1=new Date(YEAR,0,1);
  const day=(week-1)*7+dayOfWeek-jan1.getDay()+1;
  const d=new Date(YEAR,0,day);
  return d.toISOString().slice(0,10);
}
function daysInRange(startDate,endDate){
  if(!startDate||!endDate)return null;
  const s=new Date(startDate),e=new Date(endDate);
  return Math.max(1,Math.round((e-s)/86400000)+1);
}
function calcWeeksFromDates(startDate,endDate){
  const days=daysInRange(startDate,endDate);
  return days?Math.max(1,Math.ceil(days/7)):null;
}
// Get week-level day fractions for partial first/last weeks
function getWeekDayFracs(startDate,endDate,nWeeks){
  if(!startDate||!endDate)return Array(nWeeks).fill(1);
  const s=new Date(startDate),e=new Date(endDate);
  const fracs=[];
  for(let w=0;w<nWeeks;w++){
    // Monday of this week in the flight
    const wStart=new Date(s.getTime()+w*7*86400000);
    const wEnd=new Date(wStart.getTime()+6*86400000);
    const overlapStart=wStart<s?s:wStart;
    const overlapEnd=wEnd>e?e:wEnd;
    const days=Math.max(0,Math.round((overlapEnd-overlapStart)/86400000)+1);
    fracs.push(days/7);
  }
  const sum=fracs.reduce((a,b)=>a+b,0)||1;
  return fracs.map(f=>f/sum);// normalize to sum=1
}

function rch(trp){if(trp<=0)return[0,0];const last=RC[RC.length-1];if(trp>=last[0])return[last[1],last[2]];for(let i=1;i<RC.length;i++){if(trp<=RC[i][0]){const t=(trp-RC[i-1][0])/(RC[i][0]-RC[i-1][0]);return[RC[i-1][1]+t*(RC[i][1]-RC[i-1][1]),RC[i-1][2]+t*(RC[i][2]-RC[i-1][2])];}}return[0,0];}

// Monthly discount scales (per-channel-monthly-avg-budget)
const MS={
  C1:[[800,.20],[1000,.27],[1200,.30],[1400,.33],[1700,.36],[2000,.39],[2500,.41],[3000,.42],[3500,.43],[4500,.44],[6000,.45],[9000,.46],[12000,.47],[15000,.48],[20000,.49],[30000,.50]],
  '7TV':[[600,.20],[700,.27],[800,.30],[900,.33],[1100,.36],[1300,.39],[1500,.41],[1700,.42],[2000,.43],[2400,.44],[3000,.45],[4000,.46],[5000,.47],[6000,.48],[7000,.49],[12000,.50]],
  TV8:[[800,.20],[1000,.27],[1200,.30],[1400,.33],[1700,.36],[2000,.39],[2500,.41],[3000,.42],[3500,.43],[4500,.44],[6000,.45],[9000,.46],[12000,.47],[15000,.48],[20000,.49],[30000,.50]],
  DEF:[[500,.20],[600,.27],[700,.30],[800,.33],[900,.36],[1000,.39],[1200,.41],[1400,.42],[1600,.43],[2000,.44],[2500,.45],[3500,.46],[5000,.47],[7000,.48],[10000,.49],[20000,.50]],
};
const TS={
  C1:[[8000,.05],[16000,.07],[24000,.08],[32000,.09],[40000,.10],[50000,.11],[60000,.12],[70000,.13],[80000,.14],[100000,.15],[200000,.20]],
  '7TV':[[4000,.05],[7000,.07],[10000,.09],[14000,.11],[18000,.14],[22000,.16],[26000,.18],[30000,.20],[35000,.22],[40000,.24],[80000,.25]],
  TV8:[[8000,.05],[16000,.07],[24000,.08],[32000,.09],[40000,.10],[50000,.11],[60000,.12],[70000,.13],[80000,.14],[100000,.15],[200000,.20]],
  DEF:[[5000,.05],[10000,.07],[15000,.09],[20000,.11],[25000,.14],[30000,.16],[35000,.18],[40000,.20],[45000,.22],[50000,.24],[100000,.25]],
};
function scl(tbl,v){if(!v||v<=0)return 0;let r=0;for(const[t,p]of tbl){if(v>=t)r=p;else break;}return r;}

const SH_LIST=['Nova TV','Media Sales Hub','Exclusive Sales House','Mediadivision','Direct Buying'];
const SH_GROUP_TS=['Nova TV','Media Sales Hub','Exclusive Sales House','Mediadivision'];
const CH=[
  {id:'C1', n:'Cinema 1',        sh:'Nova TV',              pt:235,otr:.5,defAff:54.545,defShr:.0528,defAmr:.0114},
  {id:'7TV',n:'TV 7',            sh:'Nova TV',              pt:205,otr:.5,defAff:75.000,defShr:.0234,defAmr:.0051},
  {id:'STA',n:'Star TV',         sh:'Media Sales Hub',      pt:200,otr:.5,defAff:89.855,defShr:.0285,defAmr:.0062},
  {id:'ONE',n:'One TV',          sh:'Media Sales Hub',      pt:200,otr:.5,defAff:91.892,defShr:.0312,defAmr:.0068},
  {id:'NEX',n:'Next TV',         sh:'Media Sales Hub',      pt:200,otr:.5,defAff:89.286,defShr:.0348,defAmr:.0075},
  {id:'PRE',n:'Premiera TV',     sh:'Media Sales Hub',      pt:200,otr:.5,defAff:105.128,defShr:.0187,defAmr:.0041},
  {id:'EXC',n:'Exclusiv TV',     sh:'Exclusive Sales House',pt:200,otr:.5,defAff:89.000,defShr:.0410,defAmr:.0089},
  {id:'TV9',n:'TV 9',            sh:'Exclusive Sales House',pt:200,otr:.5,defAff:82.353,defShr:.0063,defAmr:.0014},
  {id:'N4', n:'N4',              sh:'Mediadivision',        pt:200,otr:.5,defAff:100.000,defShr:.0157,defAmr:.0034},
  {id:'G24',n:'Global 24',       sh:'Mediadivision',        pt:200,otr:.5,defAff:100.000,defShr:.0043,defAmr:.0009},
  {id:'RLV',n:'R Live',          sh:'Mediadivision',        pt:200,otr:.5,defAff:100.000,defShr:.0095,defAmr:.0021},
  {id:'NOR',n:'Noroc TV',        sh:'Mediadivision',        pt:200,otr:.5,defAff:100.000,defShr:.0072,defAmr:.0016},
  {id:'VOC',n:'Vocea Basarabiei',sh:'Mediadivision',        pt:200,otr:.5,defAff:100.000,defShr:.0119,defAmr:.0026},
  {id:'WSP',n:'We Sport',        sh:'Mediadivision',        pt:200,otr:.5,defAff:82.353, defShr:.0063,defAmr:.0014},
  {id:'TV8',n:'TV 8',            sh:'Direct Buying',        pt:200,otr:.5,defAff:100.000,defShr:.0363,defAmr:.0079},
  {id:'UNU',n:'Unu TV',          sh:'Direct Buying',        pt:200,otr:.5,defAff:100.000,defShr:.0296,defAmr:.0064},
  {id:'JUR',n:'Jurnal TV',       sh:'Direct Buying',        pt:200,otr:.5,defAff:100.000,defShr:.0515,defAmr:.0112},
  {id:'PTV',n:'Pro TV',          sh:'Direct Buying',        pt:200,otr:.5,defAff:100.000,defShr:.0346,defAmr:.0075},
  {id:'MD1',n:'Moldova 1',       sh:'Direct Buying',        pt:200,otr:.5,defAff:100.000,defShr:.0372,defAmr:.0081},
  {id:'MD2',n:'Moldova 2',       sh:'Direct Buying',        pt:200,otr:.5,defAff:100.000,defShr:.0110,defAmr:.0024},
];
const FLIGHT_COLORS=['#3B7BFF','#2DD4A0','#F5A623','#A78BFA','#22D3EE','#FF5C5C','#FB923C','#34D399','#818CF8','#F472B6','#4ADE80','#FACC15'];
const CH_COLORS=['#3B7BFF','#2DD4A0','#F5A623','#A78BFA','#22D3EE','#FF5C5C','#FB923C','#34D399','#818CF8','#F472B6','#4ADE80','#FACC15','#60A5FA','#86EFAC','#FCD34D','#C4B5FD','#67E8F9','#FCA5A5','#A3E635','#E879F9'];

// ─── DEFAULT AUDIENCE DATA ───────────────────────────────────────
function mkAudData(){
  const ba={},b1={};
  CH.forEach(c=>{
    ba[c.id]={amr:c.defAmr,shr:c.defShr,aff:c.defAff,edited:false};
    b1[c.id]={amr:c.defAmr*.85,shr:c.defShr,aff:+(c.defAmr*.85/c.defAmr*100).toFixed(2),edited:false};
  });
  return[
    {id:'ba',name:'Buying Audience',label:'(GRP base)',color:'#3B7BFF',locked:true,data:ba},
    {id:'brand1',name:'Brand Audience 1',label:'',color:'#A78BFA',locked:false,data:b1},
  ];
}

// ─── DISCOUNT ROWS ───────────────────────────────────────────────
function mkDiscRows(chId){
  return[
    {id:'monthly',label:'Monthly avg budget',auto:true,on:true,v:0,note:''},
    {id:'total',  label:'Total budget',       auto:true,on:true,v:0,note:''},
    {id:'offprime',label:'Off-prime ≥25%',   auto:true,on:true,v:.07,note:''},
    {id:'lowpri', label:'Low priority',       auto:false,on:true,v:.30,note:''},
    {id:'payment',label:'Payment schedule',   auto:false,on:true,v:.10,note:''},
    {id:'adroom', label:'Via ADROOM',         auto:false,on:true,v:.30,note:''},
    {id:'keeping',label:'Budget keeping',     auto:false,on:true,v:.50, note:''},
    {id:'welcome',label:'Welcome disc',       auto:false,on:true,v:.30,note:''},
    {id:'special',label:'Special disc',       auto:false,on:true,v:.15, note:''},
  ];
}
function refreshDiscGrouped(rows,chId,chBud,tsBud,nMonths,opShr,extMS,extTS){
  const ms=(extMS&&extMS[chId])||MS[chId]||MS.DEF;
  const ts=(extTS&&extTS[chId])||TS[chId]||TS.DEF;
  const avg=nMonths>0?chBud/nMonths:0,dm=scl(ms,avg),dt=scl(ts,tsBud),ok=opShr>=.225;
  return rows.map(r=>{
    if(!r.auto)return r;
    if(r.id==='monthly')return{...r,v:dm,on:true,note:'avg €'+Math.round(avg).toLocaleString()};
    if(r.id==='total')return{...r,v:dt,on:true,note:'€'+Math.round(tsBud).toLocaleString()+(tsBud!==chBud?' (SH)':'')};
    if(r.id==='offprime')return{...r,v:ok?0.07:0,on:ok,note:'OP '+(opShr*100).toFixed(0)+'%'};
    return r;
  });
}
function refreshDisc(rows,chId,chBud,nMonths,opShr){
  return refreshDiscGrouped(rows,chId,chBud,chBud,nMonths,opShr,null,null);
}function stackDisc(rows){const v=(rows||[]).filter(r=>r.on&&r.v>0).map(r=>r.v);if(!v.length)return 0;return 1-v.reduce((a,x)=>a*(1-x),1);}

// ─── DEFAULT POSITIONING / CO-BRAND ─────────────────────────────
function defPcData(){return{pos:{Any:1.00,First:1.50,Second:1.30,Penultimate:1.30,Last:1.50},cb:{0:1.00,1:1.07,2:1.15,3:1.25}};}
function initPc(){const o={};CH.forEach(c=>{o[c.id]=defPcData();});return o;}

// ─── FLIGHT ─────────────────────────────────────────────────────
function mkFlight(idx,overrides={}){
  const colors=FLIGHT_COLORS;
  const chCfg={};CH.forEach(c=>{chCfg[c.id]={on:true,wOverride:null};});
  const discRows={};CH.forEach(c=>{discRows[c.id]=mkDiscRows(c.id);});
  return{
    id:'f'+Date.now()+idx,
    brand:'Brand 1',
    label:'Flight '+(idx+1),
    color:colors[idx%colors.length],
    startDate:'',endDate:'',  // ISO date strings, used when set
    startWeek:1,weeks:4,
    month:'JAN',
    mode:'budget',  // 'budget' | 'trp'
    budget:50000,
    trpTarget:200,
    weeklyOverrides:{},  // week_index(0-based) -> {bud?:n, trp?:n} override fractions
    reachOverride:{r1:null,r3:null},  // manual reach override per flight
    dm:[{d:30,sh:100}],
    ptSplit:75,
    audId:'ba',
    pos:'Any',
    cb:0,
    chCfg,
    discRows,
    posOverrides:{},  // chId -> position key override
    excludedChannels:[],  // chIds excluded from this flight's mix
    cbOverrides:{},   // chId -> cb count override
    ...overrides,
  };
}

// ─── CORE CALC ENGINE ────────────────────────────────────────────
function calcFlight(flight,globalMix,audData,pcData,planDiscRows,globalChannels,extras){
  const actChs=CH.filter(c=>flight.chCfg[c.id]&&flight.chCfg[c.id].on&&(globalChannels?globalChannels[c.id]!==false:true)&&!(flight.excludedChannels||[]).includes(c.id));
  if(!actChs.length)return null;

  const xPrices=(extras&&extras.chPrices)||{};
  const xBuyAudSel=(extras&&extras.chBuyAudSel)||{};
  const xAllAudiences=(extras&&extras.audiences)||[];
  const xMonthExcl=flight.monthExclusions||{};
  const xMS=(extras&&extras.discScales&&extras.discScales.MS)||{};
  const xTS=(extras&&extras.discScales&&extras.discScales.TS)||{};
  const xSeason=(extras&&extras.seasonCoeffs)||{};
  function chPt(ch){return xPrices[ch.id]?xPrices[ch.id].pt:ch.pt;}
  function chOtr(ch){return xPrices[ch.id]?xPrices[ch.id].otr:ch.otr;}
  function seasonC(chId){
    const mi=Math.max(0,Math.min(11,Math.floor((flight.startWeek-1)/4.33)));
    return(xSeason[chId]&&xSeason[chId][MONTHS[mi]])||1;
  }

  const ptSpl=flight.ptSplit/100,opShr=1-ptSpl;
  const durC=flight.dm.reduce((s,x)=>s+(x.sh/100)*(DC[x.d]||1),0);
  const nWeeks=Math.max(1,(flight.startDate&&flight.endDate)?
    calcWeeksFromDates(flight.startDate,flight.endDate)||flight.weeks:flight.weeks);
  const dayFracs=(flight.startDate&&flight.endDate)?
    getWeekDayFracs(flight.startDate,flight.endDate,nWeeks):null;
  const nMonths=Math.max(1,nWeeks/4.33);

  // weight distribution
  function chMonthExclFrac(chId){
    const excl=xMonthExcl[chId];if(!excl||!excl.length)return 0;
    let exclWeeks=0;
    for(let w=0;w<nWeeks;w++){const mi=Math.floor(((flight.startWeek||1)-1+w)/4.33)%12;if(excl.includes(mi))exclWeeks++;}
    return nWeeks>0?exclWeeks/nWeeks:0;
  }
  const mixWeights={};
  const shrSum=actChs.reduce((s,c)=>{
    const d=audData[c.id];
    const baseShr=d?d.shr:c.defShr;
    const ov=flight.chCfg[c.id].wOverride;
    mixWeights[c.id]=ov!=null?ov:((globalMix[c.id]!=null?globalMix[c.id]:baseShr*100));
    return s+mixWeights[c.id];
  },0)||1;

  function getAff(chId){
    const ch=CH.find(c=>c.id===chId);
    const d=audData[chId];
    const buyAudId=(xBuyAudSel&&xBuyAudSel[chId])||'ba';
    const buyAud=xAllAudiences&&xAllAudiences.find(a=>a.id===buyAudId);
    const buyAmr=buyAud&&buyAud.data&&buyAud.data[chId]&&buyAud.data[chId].amr>0?buyAud.data[chId].amr:null;
    if(buyAmr&&d&&d.amr&&d.amr!==buyAmr)return d.amr/buyAmr;
    return d?(d.aff/100):(ch?ch.defAff/100:1);
  }
  // New: get effective affinity split by PT/OT using seasonal data
  function getEffAff(chId){
    const seasonal=getSeasonalAMR(extras&&extras.audSeasonData,chId,ptSpl,flight.startWeek||1,nWeeks);
    if(seasonal){
      // Effective AMR = weighted by budget split (ptSpl for PT, 1-ptSpl for OT)
      const ptAmr=seasonal.ptAmr,otAmr=seasonal.otAmr;
      const baAmr=audData['ba']&&audData['ba'][chId]?audData['ba'][chId].amr:(CH.find(c=>c.id===chId).defAmr);
      // Effective audience AMR weighted by time split
      const effAmr=ptAmr*ptSpl+otAmr*opShr;
      // Effective BA AMR — if ba has seasonal data use it, else use static
      const baSeasonal=getSeasonalAMR(extras&&extras.audSeasonData&&extras.audSeasonData.__ba?{'ba':extras.audSeasonData.__ba}:null,'ba',ptSpl,flight.startWeek||1,nWeeks);
      const effBaAmr=baSeasonal?(baSeasonal.ptAmr*ptSpl+baSeasonal.otAmr*opShr):baAmr;
      return effBaAmr>0?effAmr/effBaAmr:getAff(chId);
    }
    return getAff(chId);
  }
  // Get effective seasonal share override
  function getEffShr(chId){
    const seasonal=getSeasonalShr(extras&&extras.audSeasonData,chId,flight.startWeek||1,nWeeks);
    if(seasonal!=null)return seasonal;
    const d=audData[chId];
    return d?d.shr:(CH.find(c=>c.id===chId).defShr);
  }
  function getDisc(ch,chBud){
    const rows=planDiscRows[ch.id]||mkDiscRows(ch.id);
    const lr=refreshDisc(rows,ch.id,chBud,nMonths,opShr);
    return{disc:stackDisc(lr),lr};
  }
  function getDiscGrouped(ch,chBud,tsBud){
    const rows=planDiscRows[ch.id]||mkDiscRows(ch.id);
    const lr=refreshDiscGrouped(rows,ch.id,chBud,tsBud,nMonths,opShr,xMS,xTS);
    return{disc:stackDisc(lr),lr};
  }
  function getCosts(chId){
    const posKey=flight.posOverrides[chId]||flight.pos;
    const cbKey=flight.cbOverrides[chId]!=null?flight.cbOverrides[chId]:flight.cb;
    const pd=pcData[chId]||defPcData();
    return{posC:pd.pos[posKey]||1,cbC:pd.cb[cbKey]||1};
  }

  if(flight.mode==='budget'){
    const totalBud=flight.budget;
    const chBuds={};actChs.forEach(ch=>{chBuds[ch.id]=totalBud*(mixWeights[ch.id]/shrSum);});
    const shGroupBud={};
    SH_GROUP_TS.forEach(sh=>{shGroupBud[sh]=actChs.filter(x=>x.sh===sh).reduce((s,x)=>s+chBuds[x.id],0);});
    const results=actChs.map(ch=>{
      const _exclFrac=chMonthExclFrac(ch.id);
    const chBud=chBuds[ch.id]*(1-_exclFrac);
      const ns=mixWeights[ch.id]/shrSum;
      const tsBud=SH_GROUP_TS.includes(ch.sh)?shGroupBud[ch.sh]:chBud;
      const{disc,lr}=getDiscGrouped(ch,chBud,tsBud);
      const{posC,cbC}=getCosts(ch.id);
      const aff=getAff(ch.id);
      const ptCpp=chPt(ch)*seasonC(ch.id),otCpp=ptCpp*chOtr(ch);
      const gross=otCpp*(1-ptSpl)+ptCpp*ptSpl;
      const cpp30T=gross*(1-disc)*posC*cbC/aff;
      const wtrp=cpp30T>0?chBud/cpp30T:0;
      const wgrp=wtrp/aff;
      const trp=wtrp/durC,grp=wgrp/durC;
      return{ch,ns,b:chBud,wtrp,wgrp,trp,grp,disc,lr,posC,cbC,cpp30T,aff};
    });
    const tot=results.reduce((a,r)=>({bud:a.bud+r.b,wtrp:a.wtrp+r.wtrp,wgrp:a.wgrp+r.wgrp,trp:a.trp+r.trp,grp:a.grp+r.grp}),{bud:0,wtrp:0,wgrp:0,trp:0,grp:0});
    const[r1raw,r3raw]=rch(tot.wtrp);
    const r1=flight.reachOverride&&flight.reachOverride.r1!=null?flight.reachOverride.r1:r1raw;
    const r3=flight.reachOverride&&flight.reachOverride.r3!=null?flight.reachOverride.r3:r3raw;
    const cpp30T=tot.wtrp>0?tot.bud/tot.wtrp:0;
    // weekly accumulation
    // Build weekly distribution respecting overrides
    const woKeys=Object.keys(flight.weeklyOverrides||{}).map(Number);
    const hasBudOv=woKeys.some(k=>(flight.weeklyOverrides[k]&&flight.weeklyOverrides[k].bud!=null));
    const hasTrpOv=woKeys.some(k=>(flight.weeklyOverrides[k]&&flight.weeklyOverrides[k].trp!=null));
    // Compute fractions: override weeks get explicit values, rest share remaining proportionally
    function buildFracs(total,field){
      const fracs=Array(nWeeks).fill(null);
      let fixedSum=0,freeCount=0;
      for(let i=0;i<nWeeks;i++){
        const ov=flight.weeklyOverrides&&flight.weeklyOverrides[i];
        if(ov&&ov[field]!=null){fracs[i]=ov[field]/total;fixedSum+=fracs[i];}
        else freeCount++;
      }
      const freeShare=Math.max(0,(1-fixedSum)/(freeCount||1));
      for(let i=0;i<nWeeks;i++){if(fracs[i]===null)fracs[i]=freeShare;}
      return fracs;
    }
    const budFracs=(hasBudOv||hasTrpOv)?buildFracs(tot.bud,'bud'):Array(nWeeks).fill(1/nWeeks);
    const trpFracs=(hasTrpOv||hasBudOv)?buildFracs(tot.wtrp,'trp'):Array(nWeeks).fill(1/nWeeks);
    // Apply day-level fracs for partial weeks
    const effBudFracs=dayFracs?budFracs.map((f,i)=>f*dayFracs[i]):budFracs;
    const effTrpFracs=dayFracs?trpFracs.map((f,i)=>f*dayFracs[i]):trpFracs;
    const bfSum=effBudFracs.reduce((a,b)=>a+b,0)||1;
    const tfSum=effTrpFracs.reduce((a,b)=>a+b,0)||1;
    let cumT=0;
    const weekly=Array.from({length:nWeeks},(_,i)=>{
      const wb=tot.bud*(effBudFracs[i]/bfSum),wt=tot.wtrp*(effTrpFracs[i]/tfSum);cumT+=wt;
      const affR=tot.wtrp>0?tot.wgrp/tot.wtrp:1;
      const wg=wt*affR,rawTrp=wt/durC,rawGrp=wg/durC;
      return{week:i+1,bud:wb,wtrp:wt,wgrp:wg,trp:rawTrp,grp:rawGrp,cumTrp:cumT,
        chs:results.map(r=>({id:r.ch.id,wtrp:r.wtrp*(effTrpFracs[i]/tfSum)}))};
    });
    return{mode:'budget',chs:results,tot:{...tot,r1,r3,cpp30T,cpp30G:tot.wgrp>0?tot.bud/tot.wgrp:0},weekly,nWeeks};
  } else {
    // TRP mode: back-calculate budget
    const targetWtrp=flight.trpTarget;
    const results=actChs.map(ch=>{
      const ns=mixWeights[ch.id]/shrSum;
      const aff=getEffAff(ch.id);
      const{posC,cbC}=getCosts(ch.id);
      const ptCpp=chPt(ch)*seasonC(ch.id),otCpp=ptCpp*chOtr(ch);
      const gross=otCpp*(1-ptSpl)+ptCpp*ptSpl;
      // Estimate chBud first pass (no discount)
      const cpp30T_nodis=gross*posC*cbC/aff;
      const chWtrp=targetWtrp*ns;
      const chBudEst=chWtrp*cpp30T_nodis;
      const{disc,lr}=getDisc(ch,chBudEst);
      const cpp30T=gross*(1-disc)*posC*cbC/aff;
      const chBud=chWtrp*cpp30T;
      const wtrp=cpp30T>0?chBud/cpp30T:0;
      const wgrp=wtrp/aff,trp=wtrp/durC,grp=wgrp/durC;
      return{ch,ns,b:chBud,wtrp,wgrp,trp,grp,disc,lr,posC,cbC,cpp30T,aff};
    });
    const tot=results.reduce((a,r)=>({bud:a.bud+r.b,wtrp:a.wtrp+r.wtrp,wgrp:a.wgrp+r.wgrp,trp:a.trp+r.trp,grp:a.grp+r.grp}),{bud:0,wtrp:0,wgrp:0,trp:0,grp:0});
    const[r1,r3]=rch(tot.wtrp);
    const cpp30T=tot.wtrp>0?tot.bud/tot.wtrp:0;
    const woKeys2=Object.keys(flight.weeklyOverrides||{}).map(Number);
    const hasBudOv2=woKeys2.some(k=>(flight.weeklyOverrides[k]&&flight.weeklyOverrides[k].bud!=null));
    const hasTrpOv2=woKeys2.some(k=>(flight.weeklyOverrides[k]&&flight.weeklyOverrides[k].trp!=null));
    function buildFracs2(total,field){
      const fracs=Array(nWeeks).fill(null);let fixedSum=0,freeCount=0;
      for(let i=0;i<nWeeks;i++){const ov=flight.weeklyOverrides&&flight.weeklyOverrides[i];
        if(ov&&ov[field]!=null){fracs[i]=ov[field]/total;fixedSum+=fracs[i];}else freeCount++;}
      const freeShare=Math.max(0,(1-fixedSum)/(freeCount||1));
      for(let i=0;i<nWeeks;i++){if(fracs[i]===null)fracs[i]=freeShare;}return fracs;
    }
    const bf2=(hasBudOv2||hasTrpOv2)?buildFracs2(tot.bud,'bud'):Array(nWeeks).fill(1/nWeeks);
    const tf2=(hasTrpOv2||hasBudOv2)?buildFracs2(tot.wtrp,'trp'):Array(nWeeks).fill(1/nWeeks);
    const effBf2=dayFracs?bf2.map((f,i)=>f*dayFracs[i]):bf2;
    const effTf2=dayFracs?tf2.map((f,i)=>f*dayFracs[i]):tf2;
    const bf2Sum=effBf2.reduce((a,b)=>a+b,0)||1;
    const tf2Sum=effTf2.reduce((a,b)=>a+b,0)||1;
    let cumT2=0;
    const weekly=Array.from({length:nWeeks},(_,i)=>{
      const wb=tot.bud*(effBf2[i]/bf2Sum),wt=tot.wtrp*(effTf2[i]/tf2Sum);cumT2+=wt;
      const affR2=tot.wtrp>0?tot.wgrp/tot.wtrp:1;
      const wg2=wt*affR2,rawTrp2=wt/durC,rawGrp2=wg2/durC;
      return{week:i+1,bud:wb,wtrp:wt,wgrp:wg2,trp:rawTrp2,grp:rawGrp2,cumTrp:cumT2,
        chs:results.map(r=>({id:r.ch.id,wtrp:r.wtrp*(effTf2[i]/tf2Sum)}))};
    });
    return{mode:'trp',chs:results,tot:{...tot,r1,r3,cpp30T,cpp30G:tot.wgrp>0?tot.bud/tot.wgrp:0},weekly,nWeeks};
  }
}

// ─── OPTIMISER: find cheapest mix to hit TRP target ──────────────
function optimiseMix(flight,globalMix,audData,pcData,planDiscRows,gChannels,extras){
  // Sort channels by CPP/30"TRP ascending, return suggested weights
  const actChs=CH.filter(c=>flight.chCfg[c.id]&&flight.chCfg[c.id].on&&(gChannels?gChannels[c.id]!==false:true)&&!(flight.excludedChannels||[]).includes(c.id));
  const ptSpl=flight.ptSplit/100,opShr=1-ptSpl;
  const nMonths=Math.max(1,flight.weeks/4.33);
  const xPO=(extras&&extras.chPrices)||{};
  const xMO=(extras&&extras.discScales&&extras.discScales.MS)||{};
  const xTO=(extras&&extras.discScales&&extras.discScales.TS)||{};
  const xSO=(extras&&extras.seasonCoeffs)||{};
  function getCpp(ch){
    const aff=(audData[ch.id]?audData[ch.id].aff:ch.defAff)/100;
    const posKey=flight.posOverrides[ch.id]||flight.pos;
    const cbKey=flight.cbOverrides[ch.id]!=null?flight.cbOverrides[ch.id]:flight.cb;
    const pd=pcData[ch.id]||defPcData();
    const posC=pd.pos[posKey]||1,cbC=pd.cb[cbKey]||1;
    const ptO=xPO[ch.id]?xPO[ch.id].pt:ch.pt;
    const otrO=xPO[ch.id]?xPO[ch.id].otr:ch.otr;
    const miO=Math.max(0,Math.min(11,Math.floor((flight.startWeek-1)/4.33)));
    const scO=(xSO[ch.id]&&xSO[ch.id][MONTHS[miO]])||1;
    const gross=(ptO*scO*otrO)*(1-ptSpl)+(ptO*scO)*ptSpl;
    // Use avg discount estimate
    const estBud=(flight.mode==='budget'?flight.budget:flight.trpTarget*gross*posC*cbC/aff)/actChs.length;
    const rows=planDiscRows[ch.id]||mkDiscRows(ch.id);
    const lr=refreshDiscGrouped(rows,ch.id,estBud,estBud,nMonths,opShr,xMO,xTO);
    const disc=stackDisc(lr);
    return{id:ch.id,n:ch.n,cpp:gross*(1-disc)*posC*cbC/aff};
  }
  const cpps=actChs.map(getCpp).sort((a,b)=>a.cpp-b.cpp);
  // Greedy: top 60% budget to cheapest half, remaining 40% spread across rest
  const half=Math.ceil(cpps.length/2);
  const suggested={};
  const totalShr=actChs.reduce((s,c)=>{const d=audData[c.id];return s+(d?d.shr:c.defShr)*100;},0);
  cpps.forEach((item,i)=>{
    const ch=CH.find(c=>c.id===item.id);
    const baseShr=(audData[item.id]?audData[item.id].shr:ch.defShr)*100;
    suggested[item.id]=i<half?baseShr*1.4:baseShr*0.65;
  });
  return{cpps,suggested};
}

// ─── FORMATTERS ─────────────────────────────────────────────────
const fe2=n=>(!isFinite(n)||isNaN(n)||n===0)?'—':'€'+n.toFixed(2);
const fe=n=>(!isFinite(n)||isNaN(n)||n===0)?'—':'€'+Math.round(n).toLocaleString();
const f0=n=>(!isFinite(n)||isNaN(n)||n===0)?'—':Math.round(n).toLocaleString();
const fp1=n=>(!isFinite(n)||isNaN(n))?'—':n.toFixed(1)+'%';
const fp2=n=>(!isFinite(n)||isNaN(n))?'—':n.toFixed(2)+'%';
const fp=n=>(!isFinite(n)||isNaN(n))?'—':n.toFixed(1)+'%';

// ─── SMALL COMPONENTS ────────────────────────────────────────────
function Tog({on,cb}){return(
  <div className="tog" style={{background:on?'var(--blue)':'var(--surface3)'}} onClick={cb}>
    <div className="tok" style={{left:on?'15px':'2px'}}/>
  </div>
);}

function ModeSw({val,onChange}){return(
  <div className="mode-sw">
    <button className={'mode-btn'+(val==='budget'?' on':'')} onClick={()=>onChange('budget')}>€ Budget</button>
    <button className={'mode-btn'+(val==='trp'?' on':'')} onClick={()=>onChange('trp')}>TRP Target</button>
  </div>
);}

function Bdg({v,t}){
  const cls=t==='green'?'bdg-green':t==='amber'?'bdg-amber':t==='red'?'bdg-red':t==='blue'?'bdg-blue':t==='purple'?'bdg-purple':t==='gold'?'bdg-gold':'bdg-gray';
  return <span className={'bdg '+cls}>{v}</span>;
}

// ─── GANTT FLOWCHART ─────────────────────────────────────────────
// Rows: TRP, wTRP, GRP, Budget (weekly), Budget (monthly), + channel detail rows
// Columns: each week of the flight
function FlightFlowchart({result,flight,flights,allResults,viewMode}){
  const[detail,setDetail]=useState('summary'); // 'summary'|'channels'|'durations'|'positioning'
  const[hovWeek,setHovWeek]=useState(null);
  if(!result||!result.weekly)return null;
  const nW=result.nWeeks;
  const weeks=result.weekly;
  const actChs=result.chs;

  // For annual view: show all 52 weeks across all flights
  // For flight view: show weeks of selected flight
  const isAnnual=viewMode==='annual';

  if(isAnnual){
    // Build 52-week grid across all flights
    const allWeeks=Array.from({length:52},(_,i)=>i+1);
    const weekData={};
    allWeeks.forEach(w=>{weekData[w]={bud:0,wtrp:0,flights:[]};});
    flights.forEach((f,fi)=>{
      const r=allResults[f.id];if(!r)return;
      for(let wi=0;wi<r.nWeeks;wi++){
        const w=f.startWeek+wi;if(w>52)break;
        const wd=r.weekly[wi]||{wtrp:0,bud:0};
        weekData[w].wtrp+=wd.wtrp;
        weekData[w].bud+=wd.bud;
        weekData[w].flights.push({f,fi,perW:wd.wtrp,perWB:wd.bud});
      }
    });
    const maxTrp=Math.max(...Object.values(weekData).map(x=>x.wtrp),1);
    const maxBud=Math.max(...Object.values(weekData).map(x=>x.bud),1);

    return(
      <div>
        <div style={{overflowX:'auto'}}>
          <table className="fc-table" style={{minWidth:900}}>
            <thead>
              <tr>
                <th className="row-hdr" style={{width:140}}>Metric</th>
                {MONTHS.map((m,mi)=>{
                  const startW=Math.round(mi*4.33)+1,endW=Math.round((mi+1)*4.33);
                  const wCount=endW-startW+1;
                  return <th key={m} colSpan={wCount} style={{background:'var(--text)',color:'var(--gold)',borderColor:'#3A3A3A'}}>{MN[m]}</th>;
                })}
              </tr>
              <tr>
                <th className="row-hdr">Week</th>
                {allWeeks.map(w=><th key={w} style={{fontSize:8,padding:'3px 2px',minWidth:20}}>{w}</th>)}
              </tr>
            </thead>
            <tbody>
              {(()=>{
                // Group flights by brand
                const brandOrder=[];const brandMap={};
                flights.forEach(f=>{
                  const b=f.brand||'Brand 1';
                  if(!brandMap[b]){brandMap[b]=[];brandOrder.push(b);}
                  brandMap[b].push(f);
                });
                const rows=[];
                brandOrder.forEach((brand,bi)=>{
                  const bFlights=brandMap[brand];
                  // Brand header row
                  rows.push(
                    <tr key={'bh_'+brand}>
                      <td colSpan={53} style={{padding:'4px 8px',background:'var(--text)',color:'var(--gold)',fontSize:9,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',borderTop:'2px solid var(--gold-border)'}}>
                        {brand}
                      </td>
                    </tr>
                  );
                  // Build brand-level weekly data
                  const bWeekData={};
                  allWeeks.forEach(w=>{bWeekData[w]={wtrp:0,bud:0};});
                  bFlights.forEach(f=>{
                    const r=allResults[f.id];if(!r)return;
                    for(let wi=0;wi<r.nWeeks;wi++){
                      const w=f.startWeek+wi;if(w>52)break;
                      const wd=r.weekly[wi]||{wtrp:0,bud:0};
                      bWeekData[w].wtrp+=wd.wtrp;
                      bWeekData[w].bud+=wd.bud;
                    }
                  });
                  const bMaxTrp=Math.max(...Object.values(bWeekData).map(x=>x.wtrp),1);
                  const bMaxBud=Math.max(...Object.values(bWeekData).map(x=>x.bud),1);
                  // Campaigns row (colored blocks)
                  rows.push(
                    <tr key={'bc_'+brand}>
                      <td className="row-hdr" style={{fontSize:9,paddingLeft:8}}>Campaigns</td>
                      {allWeeks.map(w=>{
                        const fHere=bFlights.filter(f=>{const r=allResults[f.id];return r&&w>=f.startWeek&&w<f.startWeek+r.nWeeks;});
                        if(!fHere.length)return<td key={w}><div className="fc-cell empty">·</div></td>;
                        const mult=fHere.length>1;
                        return<td key={w} style={{padding:0}}>
                          <div className="fc-cell active-gold" title={fHere.map(x=>x.label).join(', ')}
                            style={{background:mult?'linear-gradient(135deg,'+fHere.map((x,i)=>x.color+(i<fHere.length-1?' 50%,':'')).join('')+')':fHere[0].color,fontSize:8}}>
                            {mult?'▪▪':' '}
                          </div>
                        </td>;
                      })}
                    </tr>
                  );
                  // TRP/week row (brand combined)
                  rows.push(
                    <tr key={'bt_'+brand}>
                      <td className="row-hdr" style={{fontSize:9,paddingLeft:8}}>TRP / week</td>
                      {allWeeks.map(w=>{
                        const v=Math.round(bWeekData[w].wtrp);
                        if(!v)return<td key={w}><div className="fc-cell empty"></div></td>;
                        const pct=bWeekData[w].wtrp/bMaxTrp;
                        return<td key={w} style={{padding:0}}>
                          <div className="fc-cell" style={{background:pct>.7?'var(--gold-dark)':pct>.4?'var(--gold)':'var(--gold-light)',color:pct>.4?'#fff':'var(--text)',fontWeight:700,fontSize:9}}>{v}</div>
                        </td>;
                      })}
                    </tr>
                  );
                  // Budget/week row (brand combined)
                  rows.push(
                    <tr key={'bb_'+brand}>
                      <td className="row-hdr" style={{fontSize:9,paddingLeft:8}}>Budget / week</td>
                      {allWeeks.map(w=>{
                        const v=Math.round(bWeekData[w].bud/1000);
                        if(!v)return<td key={w}><div className="fc-cell empty"></div></td>;
                        const pct=bWeekData[w].bud/bMaxBud;
                        return<td key={w} style={{padding:0}}>
                          <div className="fc-cell" style={{background:pct>.7?'var(--gold-dark)':pct>.4?'var(--gold)':'var(--gold-light)',color:pct>.4?'#fff':'var(--text)',fontSize:8}}>{v+'K'}</div>
                        </td>;
                      })}
                    </tr>
                  );
                  // TRP total per flight — full flight total shown once, colspanning the flight weeks
                  rows.push(
                    <tr key={'bft_'+brand}>
                      <td className="row-sub" style={{paddingLeft:8}}>TRP total / flight</td>
                      {(()=>{
                        // Build week→flight map: first week of flight gets colSpan, rest are skipped
                        const cells=[];
                        let w=1;
                        while(w<=52){
                          const f=bFlights.find(fl=>{const r=allResults[fl.id];return r&&fl.startWeek===w;});
                          if(f){
                            const r=allResults[f.id];
                            const span=Math.min(r.nWeeks,53-w);
                            if(r.nWeeks>=52){
                              MONTHS.forEach((m,mi)=>{const s2=Math.round(mi*4.33)+1,e2=Math.min(52,Math.round((mi+1)*4.33));const mT=r.weekly?r.weekly.slice(s2-1,e2).reduce((a,x)=>a+(x?x.wtrp:0),0):r.tot.wtrp/12;cells.push(<td key={'t'+mi} colSpan={e2-s2+1} style={{padding:'2px 3px',textAlign:'center',background:'var(--surface)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{Math.round(mT)}</span></td>);});
                              w=53;
                            } else {
                              cells.push(<td key={w} colSpan={span} style={{padding:'2px 6px',textAlign:'center',background:'var(--surface)',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{Math.round(r.tot.wtrp)}</span></td>);
                              w+=span;
                            }
                          } else {
                            // check if this week is inside any flight (not the start) — skip with empty
                            const inside=bFlights.some(fl=>{const r=allResults[fl.id];return r&&w>fl.startWeek&&w<fl.startWeek+r.nWeeks;});
                            if(!inside) cells.push(<td key={w} style={{borderLeft:'1px solid transparent'}}></td>);
                            w+=1;
                          }
                        }
                        return cells;
                      })()}
                    </tr>
                  );
                  // Budget total per flight — full flight total shown once, colspanning the flight weeks
                  rows.push(
                    <tr key={'bfb_'+brand}>
                      <td className="row-sub" style={{paddingLeft:8}}>€ total / flight</td>
                      {(()=>{
                        const cells=[];
                        let w=1;
                        while(w<=52){
                          const f=bFlights.find(fl=>{const r=allResults[fl.id];return r&&fl.startWeek===w;});
                          if(f){
                            const r=allResults[f.id];
                            const span=Math.min(r.nWeeks,53-w);
                            if(r.nWeeks>=52){
                              MONTHS.forEach((m,mi)=>{const s2=Math.round(mi*4.33)+1,e2=Math.min(52,Math.round((mi+1)*4.33));const mB=r.weekly?r.weekly.slice(s2-1,e2).reduce((a,x)=>a+(x?x.bud:0),0):r.tot.bud/12;cells.push(<td key={'b'+mi} colSpan={e2-s2+1} style={{padding:'2px 3px',textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{Math.round(mB/1000)+'K'}</span></td>);});
                              w=53;
                            } else {
                              cells.push(<td key={w} colSpan={span} style={{padding:'2px 6px',textAlign:'center',background:'var(--gold-bg)',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)'}}><span style={{fontSize:9,color:f.color,fontWeight:700}}>{Math.round(r.tot.bud/1000)+'K'}</span></td>);
                              w+=span;
                            }
                          } else {
                            const inside=bFlights.some(fl=>{const r=allResults[fl.id];return r&&w>fl.startWeek&&w<fl.startWeek+r.nWeeks;});
                            if(!inside) cells.push(<td key={w} style={{borderLeft:'1px solid transparent'}}></td>);
                            w+=1;
                          }
                        }
                        return cells;
                      })()}
                    </tr>
                  );
                  // Reach 1+ per flight
                  rows.push(
                    <tr key={'bfr_'+brand}>
                      <td className="row-sub" style={{paddingLeft:8}}>Reach 1+ / flight</td>
                      {(()=>{
                        const cells=[];
                        let w=1;
                        while(w<=52){
                          const f=bFlights.find(fl=>{const r=allResults[fl.id];return r&&fl.startWeek===w;});
                          if(f){
                            const r=allResults[f.id];
                            const span=Math.min(r.nWeeks,53-w);
                            if(r.nWeeks>=52){
                              MONTHS.forEach((m,mi)=>{const s2=Math.round(mi*4.33)+1,e2=Math.min(52,Math.round((mi+1)*4.33));const mR=r.weekly?r.weekly.slice(s2-1,e2).reduce((a,x)=>a+(x?x.r1||0:0),0)/(e2-s2+1):null;cells.push(<td key={'r'+mi} colSpan={e2-s2+1} style={{padding:'2px 3px',textAlign:'center',background:'var(--surface)',borderLeft:'1px solid var(--border)'}}><span style={{fontSize:9,color:'var(--gold-accent)',fontWeight:700}}>{mR!=null?mR.toFixed(1)+'%':'-'}</span></td>);});
                              w=53;
                            } else {
                              cells.push(<td key={w} colSpan={span} style={{padding:'2px 6px',textAlign:'center',background:'var(--surface)',borderLeft:'1px solid var(--border)',borderRight:'1px solid var(--border)'}}><span style={{fontSize:9,color:'var(--gold-accent)',fontWeight:700}}>{r.tot&&r.tot.r1!=null?r.tot.r1.toFixed(1)+'%':'-'}</span></td>);
                              w+=span;
                            }
                          } else {
                            const inside=bFlights.some(fl=>{const r=allResults[fl.id];return r&&w>fl.startWeek&&w<fl.startWeek+r.nWeeks;});
                            if(!inside) cells.push(<td key={w} style={{borderLeft:'1px solid transparent'}}></td>);
                            w+=1;
                          }
                        }
                        return cells;
                      })()}
                    </tr>
                  );
                  // Monthly budget total for brand
                  rows.push(
                    <tr key={'bm_'+brand}>
                      <td className="row-sub" style={{paddingLeft:8,color:'var(--gold-dark)',fontWeight:600}}>Monthly budget</td>
                      {MONTHS.map((m,mi)=>{
                        const startW=Math.round(mi*4.33)+1,endW=Math.min(52,Math.round((mi+1)*4.33));
                        const mBud=allWeeks.slice(startW-1,endW).reduce((sum,w)=>sum+bWeekData[w].bud,0);
                        return<td key={m} colSpan={endW-startW+1} style={{padding:'2px 4px',textAlign:'center',background:mBud>0?'var(--gold-bg)':'transparent',borderLeft:'1px solid var(--border)'}}>
                          {mBud>0&&<span className="mono" style={{fontSize:9,fontWeight:700,color:'var(--gold-dark)'}}>{Math.round(mBud/1000)}K</span>}
                        </td>;
                      })}
                    </tr>
                  );
                });
                // ─── TOTAL section ───
                rows.push(
                  <tr key="total_hdr">
                    <td colSpan={53} style={{padding:'4px 8px',background:'var(--text)',color:'var(--gold)',fontSize:9,fontWeight:700,letterSpacing:'.08em',textTransform:'uppercase',borderTop:'3px solid var(--gold)'}}>
                      TOTAL (all brands)
                    </td>
                  </tr>
                );
                rows.push(
                  <tr key="total_trp">
                    <td className="row-hdr" style={{fontSize:9}}>Total TRP / month</td>
                    {MONTHS.map((m,mi)=>{
                      const startW=Math.round(mi*4.33)+1,endW=Math.min(52,Math.round((mi+1)*4.33));
                      const mTrp=allWeeks.slice(startW-1,endW).reduce((sum,w)=>sum+weekData[w].wtrp,0);
                      return<td key={m} colSpan={endW-startW+1} style={{padding:'2px 4px',textAlign:'center',background:mTrp>0?'var(--gold-bg)':'transparent',borderLeft:'1px solid var(--border)'}}>
                        {mTrp>0&&<span className="mono" style={{fontSize:9,fontWeight:700,color:'var(--gold-dark)'}}>{Math.round(mTrp)}</span>}
                      </td>;
                    })}
                  </tr>
                );
                rows.push(
                  <tr key="total_bud">
                    <td className="row-hdr" style={{fontSize:9}}>Total Budget / month</td>
                    {MONTHS.map((m,mi)=>{
                      const startW=Math.round(mi*4.33)+1,endW=Math.min(52,Math.round((mi+1)*4.33));
                      const mBud=allWeeks.slice(startW-1,endW).reduce((sum,w)=>sum+weekData[w].bud,0);
                      return<td key={m} colSpan={endW-startW+1} style={{padding:'2px 4px',textAlign:'center',background:mBud>0?'var(--gold-bg)':'transparent',borderLeft:'1px solid var(--border)'}}>
                        {mBud>0&&<span className="mono" style={{fontSize:9,fontWeight:700,color:'var(--gold-dark)'}}>{Math.round(mBud/1000)}K</span>}
                      </td>;
                    })}
                  </tr>
                );
                return rows;
              })()}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── FLIGHT DETAIL VIEW ──
  const maxTrp=Math.max(...weeks.map(w=>w.trp||w.wtrp||0),1);
  const maxBud=Math.max(...weeks.map(w=>w.bud),1);
  const totalBud=result.tot.bud;
  const totalTrp=result.tot.wtrp;

  return(
    <div>
      {/* Detail toggle */}
      <div style={{display:'flex',gap:4,marginBottom:8,alignItems:'center'}}>
        <span style={{fontSize:10,color:'var(--text3)',fontWeight:600,textTransform:'uppercase',letterSpacing:'.06em'}}>Show detail:</span>
        {[['summary','Summary'],['channels','By Channel'],['durations','By Duration'],['positioning','Pos / CB']].map(([k,l])=>(
          <button key={k} className={'btn btn-xs '+(detail===k?'btn-primary':'btn-ghost')} onClick={()=>setDetail(k)}>{l}</button>
        ))}
      </div>

      <div style={{overflowX:'auto'}}>
        <table className="fc-table">
          <thead>
            <tr>
              <th className="row-hdr" style={{width:160}}>Metric</th>
              {weeks.map(w=><th key={w.week} style={{minWidth:52}}>W{w.week}</th>)}
              <th style={{background:'var(--text)',color:'var(--gold)',minWidth:72}}>TOTAL</th>
            </tr>
          </thead>
          <tbody>
            {/* TRP SECTION */}
            <tr><td className="section-hdr" colSpan={nW+2}>TRP / GRP</td></tr>
            <tr>
              <td className="row-hdr" style={{color:'var(--gold-dark)'}}>TRP / week</td>
              {weeks.map(w=>{
                const v=Math.round(w.trp||0);const pct=maxTrp>0?(w.trp||0)/maxTrp:0;
                return<td key={w.week} style={{padding:0}} onMouseEnter={()=>setHovWeek(w.week)} onMouseLeave={()=>setHovWeek(null)}>
                  <div className="fc-cell" style={{background:pct>.6?'var(--gold-dark)':pct>.3?'var(--gold)':'var(--gold-light)',color:pct>.3?'#fff':'var(--text)',height:24,fontWeight:700}}>{v||'—'}</div>
                </td>;
              })}
              <td style={{padding:0}}><div className="fc-cell active-dark" style={{fontWeight:800,height:24}}>{f0(result.tot.trp)}</div></td>
            </tr>
            <tr>
              <td className="row-sub">30″ TRP / week</td>
              {weeks.map(w=>{
                const v=Math.round(w.wtrp);const pct=w.wtrp/maxTrp;
                return<td key={w.week} style={{padding:0}} title={'W'+w.week+': '+Math.round(w.wtrp)+' 30″TRP'}>
                  <div className="fc-cell" style={{background:pct>.6?'var(--gold-dark)':pct>.3?'var(--gold)':'var(--gold-light)',color:pct>.3?'#fff':'var(--text)',height:20,fontSize:8,opacity:.8}}>{v||''}</div>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}><span className="mono" style={{fontSize:9,fontWeight:600,color:'var(--gold-dark)'}}>{Math.round(totalTrp)}</span></td>
            </tr>
            <tr>
              <td className="row-sub">Cumul. TRP</td>
              {weeks.map(w=>{
                const v=Math.round(w.cumTrp);
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,color:'var(--gold-dark)',fontWeight:600}}>{v}</span>
                </td>;
              })}
              <td/>
            </tr>
            <tr>
              <td className="row-sub">30″ GRP / week</td>
              {weeks.map(w=>{
                const v=Math.round(w.wgrp||result.tot.wgrp/nW);
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,color:'var(--text2)'}}>{v||'—'}</span>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}><span className="mono" style={{fontSize:10,fontWeight:600,color:'var(--text2)'}}>{Math.round(result.tot.wgrp)}</span></td>
            </tr>

            {/* BUDGET SECTION */}
            <tr><td className="section-hdr" colSpan={nW+2}>Budget</td></tr>
            <tr>
              <td className="row-hdr">Budget / week (EUR)</td>
              {weeks.map(w=>{
                const v=Math.round(w.bud);const pct=w.bud/maxBud;
                return<td key={w.week} style={{padding:0}}>
                  <div className="fc-cell" style={{background:pct>.6?'var(--gold-dark)':pct>.3?'var(--gold)':'var(--gold-light)',color:pct>.3?'#fff':'var(--text)',height:24}}>{Math.round(v/1000)+'K'}</div>
                </td>;
              })}
              <td style={{padding:0}}><div className="fc-cell" style={{background:'var(--gold-dark)',color:'#fff',height:24,fontWeight:800}}>{Math.round(totalBud/1000)+'K'}</div></td>
            </tr>
            <tr>
              <td className="row-sub">% of flight budget</td>
              {weeks.map(w=>{
                const pct=totalBud>0?w.bud/totalBud*100:0;
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,color:'var(--text3)'}}>{pct.toFixed(0)}%</span>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}><span className="mono" style={{fontSize:9,color:'var(--text3)'}}>100%</span></td>
            </tr>

            {/* CHANNEL DETAIL */}
            {detail==='channels'&&<>
              <tr><td className="section-hdr" colSpan={nW+2}>By Channel — 30″ TRP/week</td></tr>
              {actChs.map((r,ci)=>(
                <tr key={r.ch.id}>
                  <td className="row-hdr" style={{paddingLeft:12}}>
                    <span style={{display:'inline-block',width:8,height:8,borderRadius:2,background:CH_COLORS[ci%20],marginRight:5}}/>
                    {r.ch.n}
                  </td>
                  {weeks.map(w=>{
                    const v=Math.round(r.wtrp/nW);
                    const pct=result.tot.wtrp>0?r.wtrp/result.tot.wtrp:0;
                    return<td key={w.week} style={{padding:0}}>
                      <div className="fc-cell" style={{background:CH_COLORS[ci%20]+'33',color:'var(--text)',height:22,fontSize:9}}>{v>0?v:''}</div>
                    </td>;
                  })}
                  <td style={{padding:'3px 6px',textAlign:'center'}}>
                    <span className="mono" style={{fontSize:9,fontWeight:600}}>{Math.round(r.wtrp)}</span>
                    <span style={{fontSize:8,color:'var(--text3)',marginLeft:3}}>{fp1(r.ns*100)}</span>
                  </td>
                </tr>
              ))}
            </>}

            {/* DURATION DETAIL */}
            {detail==='durations'&&<>
              <tr><td className="section-hdr" colSpan={nW+2}>Ad Duration Split</td></tr>
              {flight.dm.map(x=>(
                <tr key={x.d}>
                  <td className="row-hdr" style={{paddingLeft:12}}>{x.d}" spot — {x.sh}%</td>
                  {weeks.map(w=>(
                    <td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                      <div className="fc-cell active-light" style={{height:22,fontSize:9}}>{Math.round(w.wtrp*x.sh/100*(DC[x.d]||1))}</div>
                    </td>
                  ))}
                  <td style={{padding:'3px 6px',textAlign:'center'}}><span className="mono" style={{fontSize:9,fontWeight:600}}>{Math.round(totalTrp*x.sh/100*(DC[x.d]||1))}</span></td>
                </tr>
              ))}
            </>}

            {/* POSITIONING DETAIL */}
            {detail==='positioning'&&<>
              <tr><td className="section-hdr" colSpan={nW+2}>Positioning & Co-brand — effective CPP multiplier</td></tr>
              {actChs.map((r,ci)=>{
                const posLbl=flight.posOverrides[r.ch.id]||flight.pos;
                const cbN=flight.cbOverrides[r.ch.id]!=null?flight.cbOverrides[r.ch.id]:flight.cb;
                const mult=(r.posC*r.cbC);
                return(
                  <tr key={r.ch.id}>
                    <td className="row-hdr" style={{paddingLeft:12}}>{r.ch.n}</td>
                    {weeks.map(w=>(
                      <td key={w.week} style={{padding:0}}>
                        <div className="fc-cell" style={{height:22,fontSize:8,background:mult>1.1?'var(--amber-dim)':'var(--green-dim)',color:'var(--text)'}}>
                          {posLbl.slice(0,3)}{cbN>0?'+'+cbN:''}
                        </div>
                      </td>
                    ))}
                    <td style={{padding:'3px 6px',textAlign:'center'}}>
                      <span className="mono" style={{fontSize:9,color:mult>1.1?'var(--amber)':'var(--green)',fontWeight:600}}>×{mult.toFixed(2)}</span>
                    </td>
                  </tr>
                );
              })}
            </>}

            {/* REACH */}
            <tr><td className="section-hdr" colSpan={nW+2}>Reach (flight total)</td></tr>
            <tr>
              <td className="row-hdr">Reach 1+</td>
              {weeks.map((w,wi)=>{
                const cumR=rch(w.cumTrp)[0];
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,color:'var(--gold-dark)',fontWeight:wi===nW-1?700:400}}>{cumR.toFixed(1)}%</span>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}><span className="mono" style={{fontSize:10,fontWeight:700,color:'var(--gold-dark)'}}>{fp1(result.tot.r1)}</span></td>
            </tr>
            <tr>
              <td className="row-hdr">Reach 3+</td>
              {weeks.map((w,wi)=>{
                const cumR=rch(w.cumTrp)[1];
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,color:'var(--gold-light)',fontWeight:wi===nW-1?700:400}}>{cumR.toFixed(1)}%</span>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}><span className="mono" style={{fontSize:10,fontWeight:700,color:'var(--gold-light)'}}>{fp1(result.tot.r3)}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── YEAR TIMELINE MINI ──────────────────────────────────────────
function YearTimeline({flights,selectedId,onSelect}){
  const totalWeeks=52;
  return(
    <div style={{padding:'10px 14px',borderBottom:'1px solid var(--border)',background:'var(--surface)'}}>
      <div style={{fontSize:9,fontWeight:700,color:'var(--text3)',letterSpacing:'.09em',textTransform:'uppercase',marginBottom:6}}>Annual Overview</div>
      <div style={{display:'flex',marginBottom:3}}>
        <div style={{width:0}}/>
        {MONTHS.map((m)=>(
          <div key={m} style={{flex:4.33,fontSize:8,color:'var(--text3)',textAlign:'center'}}>{MN[m]}</div>
        ))}
      </div>
      <div style={{position:'relative',height:18,background:'var(--surface3)',borderRadius:3,overflow:'hidden',border:'1px solid var(--border)'}}>
        {MONTHS.map((_,i)=>(i>0&&
          <div key={i} style={{position:'absolute',left:(i/12*100)+'%',top:0,bottom:0,width:1,background:'var(--border)'}}/>
        ))}
        {flights.map(f=>{
          const left=((f.startWeek-1)/totalWeeks*100);
          const width=(f.weeks/totalWeeks*100);
          return(
            <div key={f.id} onClick={()=>onSelect(f.id)} title={f.label}
              style={{position:'absolute',left:left+'%',width:Math.max(.5,width)+'%',top:1,bottom:1,borderRadius:2,
                background:f.color,opacity:selectedId===f.id?1:.65,cursor:'pointer',
                border:selectedId===f.id?'1.5px solid var(--text)':'1px solid transparent',transition:'opacity .12s'}}
            />
          );
        })}
      </div>
    </div>
  );
}

// ─── FLIGHT LIST ITEM ────────────────────────────────────────────
function FlightItem({flight,result,isSelected,onSelect,onUpdate,onClone,onRemove,canRemove}){
  const[expanded,setExpanded]=useState(false);
  const tot=result&&result.tot;
  return(
    <div className={'flight-card'+(isSelected?' active':'')} style={{borderLeft:'3px solid '+flight.color}}>
      <div style={{padding:'8px 10px',display:'flex',alignItems:'center',gap:6}} onClick={()=>{onSelect(flight.id);setExpanded(e=>!e);}}>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:1}}>
            {flight.brand&&<span style={{fontSize:9,fontWeight:700,color:'var(--text3)',borderRight:'1px solid var(--border)',paddingRight:5,marginRight:2}}>{flight.brand}</span>}
            <span style={{fontWeight:600,fontSize:12,color:'var(--text)'}}>{flight.label}</span>
            <span className='bdg bdg-gold' style={{fontSize:9}}>{flight.mode==='trp'?'TRP':'€'}</span>
          </div>
          <div style={{fontSize:10,color:'var(--text3)'}}>
                    {flight.startDate&&flight.endDate?
                      new Date(flight.startDate).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit'})+'–'+new Date(flight.endDate).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit'}):
                      'W'+flight.startWeek+'–W'+(flight.startWeek+flight.weeks-1)
                    } · {flight.weeks}w · {flight.dm.map(d=>d.sh+'%×'+d.d+'"').join(' ')}
                  </div>
        </div>
        <div style={{textAlign:'right',flexShrink:0}}>
          {tot&&<>
            <div className="mono" style={{fontSize:11,fontWeight:600,color:'var(--gold-dark)'}}>{fe(tot.bud)}</div>
            <div style={{fontSize:10,color:'var(--text3)'}}>{f0(tot.wtrp)} TRP</div>
          </>}
        </div>
        <div style={{color:'var(--text3)',fontSize:10,marginLeft:4}}>{expanded?'▲':'▼'}</div>
      </div>
      {expanded&&(
        <div style={{padding:'8px 10px',borderTop:'1px solid var(--border)',background:'var(--gold-bg)'}}>
          {/* Label */}
          <div style={{display:'flex',gap:6,marginBottom:6,alignItems:'center'}}>
            <div style={{flex:1}}>
              <div className="lbl" style={{marginBottom:2}}>Brand</div>
              <input className="inp" value={flight.brand||'Brand 1'} style={{width:'100%',fontSize:10}} placeholder="Brand name" onChange={e=>onUpdate({brand:e.target.value})}/>
            </div>
            <div style={{flex:1}}>
              <div className="lbl" style={{marginBottom:2}}>Flight name</div>
              <div style={{display:'flex',gap:4,alignItems:'center'}}>
                <input className="inp" value={flight.label} style={{flex:1,fontSize:11}} onChange={e=>onUpdate({label:e.target.value})}/>
                <input type="color" value={flight.color} style={{width:24,height:24,border:'none',background:'none',cursor:'pointer',borderRadius:4}} onChange={e=>onUpdate({color:e.target.value})}/>
              </div>
            </div>
          </div>
          {/* Audience selector */}
          <div style={{marginBottom:8}}>
            <div className="lbl">Flight Audience</div>
            <select className="inn" style={{width:'100%'}} value={(flight.audId&&flight.audId!=='ba')?flight.audId:((window.__audiences||[]).filter(a=>!a.locked)[0]||{id:'brand1'}).id} onChange={e=>onUpdate({audId:e.target.value})}>
              {(window.__audiences||[]).filter(a=>!a.locked).map(a=>(
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          {/* Dates */}
          <div style={{marginBottom:6}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:4}}>
              <div>
                <div className="lbl">Start date</div>
                <input className="inp" type="date" value={flight.startDate||''} style={{fontSize:11}}
                  onChange={e=>{
                    const sd=e.target.value;
                    const sw=dateToWeek(sd)||flight.startWeek;
                    const ew=flight.endDate?dateToWeek(flight.endDate):null;
                    const wks=ew&&ew>=sw?ew-sw+1:flight.weeks;
                    onUpdate({startDate:sd,startWeek:sw,weeks:wks});
                  }}/>
              </div>
              <div>
                <div className="lbl">End date</div>
                <input className="inp" type="date" value={flight.endDate||''} style={{fontSize:11}}
                  onChange={e=>{
                    const ed=e.target.value;
                    const ew=dateToWeek(ed)||flight.startWeek+flight.weeks-1;
                    const wks=Math.max(1,ew-flight.startWeek+1);
                    onUpdate({endDate:ed,weeks:wks});
                  }}/>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
              <div>
                <div className="lbl">Start week (override)</div>
                <input className="inn" type="number" min={1} max={52} value={flight.startWeek} style={{width:'100%'}} onChange={e=>onUpdate({startWeek:Math.min(52,Math.max(1,+e.target.value)),startDate:''})}/>
              </div>
              <div>
                <div className="lbl">Duration (weeks)</div>
                <input className="inn" type="number" min={1} max={52} value={flight.weeks} style={{width:'100%'}} onChange={e=>onUpdate({weeks:Math.min(52,Math.max(1,+e.target.value)),endDate:''})}/>
              </div>
            </div>
          </div>
          {/* Mode */}
          <div style={{marginBottom:8}}>
            <ModeSw val={flight.mode} onChange={m=>onUpdate({mode:m})}/>
          </div>
          {flight.mode==='budget'&&(
            <div style={{marginBottom:8}}>
              <div className="lbl">Budget (EUR net)</div>
              <div style={{display:'flex',gap:4,alignItems:'center'}}>
                <input className="inn" type="number" min={1000} step={1000} value={flight.budget} style={{width:'100%'}} onChange={e=>onUpdate({budget:Math.max(0,+e.target.value)})}/>
              </div>
              <input type="range" min={5000} max={300000} step={5000} value={flight.budget} style={{marginTop:4}} onChange={e=>onUpdate({budget:+e.target.value})}/>
            </div>
          )}
          {flight.mode==='trp'&&(
            <div style={{marginBottom:8}}>
              <div className="lbl">Target 30"TRP</div>
              <div style={{display:'flex',gap:4,alignItems:'center'}}>
                <input className="inn" type="number" min={10} step={10} value={flight.trpTarget} style={{width:'100%'}} onChange={e=>onUpdate({trpTarget:Math.max(1,+e.target.value)})}/>
              </div>
              <input type="range" min={50} max={1000} step={10} value={flight.trpTarget} style={{marginTop:4}} onChange={e=>onUpdate({trpTarget:+e.target.value})}/>
              {tot&&<div style={{fontSize:10,color:'var(--gold-dark)',marginTop:3}}>Budget needed: {fe(tot.bud)}</div>}
            </div>
          )}
          {/* PT split */}
          <div style={{marginBottom:8,display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            <div>
              <div className="lbl">PT split {flight.ptSplit}%</div>
              <input type="range" min={50} max={100} step={5} value={flight.ptSplit} onChange={e=>onUpdate({ptSplit:+e.target.value})}/>
              <div style={{fontSize:9,color:'var(--text3)'}}>OT {100-flight.ptSplit}% · {100-flight.ptSplit>=22.5?<span style={{color:'var(--gold-dark)'}}>OP disc eligible</span>:<span style={{color:'var(--red)'}}>OP disc n/a</span>}</div>
            </div>
            <div>
              <div className="lbl">Duration mix</div>
              {flight.dm.map(x=>(
                <div key={x.d} style={{display:'flex',gap:3,alignItems:'center',marginBottom:2}}>
                  <select className="sel" value={x.d} style={{width:46}} onChange={e=>onUpdate({dm:flight.dm.map(d=>d.d===x.d?{...d,d:+e.target.value}:d)})}>
                    {DURO.map(d=><option key={d} value={d}>{d}"</option>)}
                  </select>
                  <input className="inn" type="number" min={0} max={100} step={5} value={x.sh} style={{width:44}} onChange={e=>onUpdate({dm:flight.dm.map(d=>d.d===x.d?{...d,sh:+e.target.value}:d)})}/>
                  <span style={{fontSize:9,color:'var(--text3)'}}>%</span>
                  {flight.dm.length>1&&<button className="btn btn-ghost btn-xs" style={{color:'var(--red)',borderColor:'var(--red-dim)',padding:'0 4px'}} onClick={e=>{e.stopPropagation();onUpdate({dm:flight.dm.filter(d=>d.d!==x.d)});}}>×</button>}
                </div>
              ))}
              {flight.dm.length<5&&<button className="btn btn-ghost btn-xs" style={{marginTop:2}} onClick={e=>{e.stopPropagation();const used=flight.dm.map(d=>d.d);const next=DURO.find(d=>!used.includes(d));if(next)onUpdate({dm:[...flight.dm,{d:next,sh:20}]});}}>+ dur</button>}
            </div>
          </div>
          {/* Positioning / Co-brand */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6,marginBottom:8}}>
            <div>
              <div className="lbl">Default positioning</div>
              <select className="sel" value={flight.pos} style={{width:'100%'}} onChange={e=>onUpdate({pos:e.target.value})}>
                {[['Any','Any (no preference)'],['First','First (+50%)'],['Second','Second (+30%)'],['Penultimate','Penultimate (+30%)'],['Last','Last (+50%)']].map(([v,l])=><option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div style={{gridColumn:'1 / -1'}}>
              <div className="lbl" style={{marginBottom:4}}>Exclude channels from this flight</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:3}}>
                {CH.map(ch=>{
                  const excl=(flight.excludedChannels||[]).includes(ch.id);
                  return(<div key={ch.id} style={{display:'inline-flex',flexDirection:'column',gap:1,marginBottom:3,marginRight:3}}>
                    <button style={{padding:'2px 7px',fontSize:9,borderRadius:4,cursor:'pointer',background:excl?'rgba(248,113,113,0.12)':'transparent',border:excl?'1px solid #f87171':'1px solid var(--border)',color:excl?'#f87171':'var(--text3)',textDecoration:excl?'line-through':'none'}} onClick={()=>{const cur=flight.excludedChannels||[];onUpdate({excludedChannels:excl?cur.filter(id=>id!==ch.id):[...cur,ch.id]});}}>{ch.n}</button>
                    {!excl&&<div style={{display:'flex',gap:1}}>{MONTHS.map((m,mi)=>{const isX=((flight.monthExclusions||{})[ch.id]||[]).includes(mi);return<button key={m} title={isX?'Re-include '+m:'Exclude '+m} onClick={()=>{const me={...(flight.monthExclusions||{})};const arr=me[ch.id]||[];me[ch.id]=isX?arr.filter(x=>x!==mi):[...arr,mi];onUpdate({monthExclusions:me});}} style={{padding:'1px 2px',fontSize:7,borderRadius:2,border:'1px solid '+(isX?'#c0392b':'var(--border)'),background:isX?'#3a1010':'var(--surface2)',color:isX?'#e74c3c':'var(--text3)',cursor:'pointer',minWidth:14,textAlign:'center'}}>{m.substring(0,1)}</button>;})}</div>}
                  </div>);
                })}
              </div>
              {(flight.excludedChannels||[]).length>0&&(<div style={{fontSize:9,color:'var(--amber)',marginTop:3}}>⚠ {(flight.excludedChannels||[]).length} channel(s) excluded</div>)}
            </div>
            <div>
              <div className="lbl">Co-brand (+brands)</div>
              <div style={{display:'flex',gap:3}}>
                {[0,1,2,3].map(n=><button key={n} className="btn btn-sm" style={{flex:1,background:flight.cb===n?'var(--gold)':'var(--surface2)',color:flight.cb===n?'#fff':'var(--text2)',border:'1px solid '+(flight.cb===n?'var(--gold)':'var(--border)'),fontSize:11}} onClick={e=>{e.stopPropagation();onUpdate({cb:n});}}>+{n}</button>)}
              </div>
            </div>
          </div>
          {/* Reach override */}
          <div style={{marginBottom:8}}>
            <div className="lbl" style={{marginBottom:4}}>Manual reach override (leave blank = auto)</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{fontSize:10,color:'var(--text3)',width:28}}>R1+</span>
                <input className="inn" type="number" min={0} max={100} step={0.1}
                  value={flight.reachOverride&&flight.reachOverride.r1!=null?flight.reachOverride.r1:''}
                  placeholder="auto" style={{width:'100%'}}
                  onChange={e=>onUpdate({reachOverride:{...flight.reachOverride,r1:e.target.value===''?null:+e.target.value}})}/>
                <span style={{fontSize:10,color:'var(--text3)'}}>%</span>
              </div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{fontSize:10,color:'var(--text3)',width:28}}>R3+</span>
                <input className="inn" type="number" min={0} max={100} step={0.1}
                  value={flight.reachOverride&&flight.reachOverride.r3!=null?flight.reachOverride.r3:''}
                  placeholder="auto" style={{width:'100%'}}
                  onChange={e=>onUpdate({reachOverride:{...flight.reachOverride,r3:e.target.value===''?null:+e.target.value}})}/>
                <span style={{fontSize:10,color:'var(--text3)'}}>%</span>
              </div>
            </div>
          </div>
          {/* Weekly distribution editor */}
          {flight.weeks>0&&flight.weeks<=20&&(
            <div style={{marginBottom:8}}>
              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                <div className="lbl" style={{marginBottom:0}}>Weekly distribution</div>
                <button className="btn btn-xs btn-ghost" onClick={e=>{e.stopPropagation();onUpdate({weeklyOverrides:{}});}}>Reset even</button>
              </div>
              <div style={{overflowX:'auto'}}>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
                  <thead><tr style={{background:'var(--gold-bg)'}}>
                    <th style={{padding:'3px 5px',color:'var(--text3)',fontWeight:600,fontSize:9,textAlign:'left',border:'1px solid var(--border)'}}>Wk</th>
                    {flight.mode==='budget'&&<th style={{padding:'3px 5px',color:'var(--text3)',fontWeight:600,fontSize:9,border:'1px solid var(--border)'}}>Budget €</th>}
                    <th style={{padding:'3px 5px',color:'var(--text3)',fontWeight:600,fontSize:9,border:'1px solid var(--border)'}}>30″TRP</th>
                  </tr></thead>
                  <tbody>
                    {Array.from({length:flight.weeks},(_,i)=>{
                      const ov=flight.weeklyOverrides&&flight.weeklyOverrides[i];
                      const rw=result&&result.weekly&&result.weekly[i];
                      const calcBud=rw?Math.round(rw.bud):Math.round((flight.budget||0)/Math.max(1,flight.weeks));
                      const calcTrp=rw?Math.round(rw.wtrp):Math.round((flight.mode==='trp'?flight.trpTarget:200)/Math.max(1,flight.weeks));
                      const dispBud=ov&&ov.bud!=null?ov.bud:calcBud;
                      const dispTrp=ov&&ov.trp!=null?ov.trp:calcTrp;
                      return(
                        <tr key={i}>
                          <td style={{padding:'2px 5px',border:'1px solid var(--border)',fontWeight:600,color:'var(--text3)',textAlign:'center',fontSize:9}}>
                            {flight.startDate?
                              new Date(new Date(flight.startDate).getTime()+i*7*86400000).toLocaleDateString('en-GB',{day:'2-digit',month:'2-digit'}):
                              'W'+(flight.startWeek+i)}
                          </td>
                          {flight.mode==='budget'&&<td style={{padding:'2px 3px',border:'1px solid var(--border)'}}>
                            <input className="inn" type="number" min={0} step={100}
                              value={dispBud} style={{width:'100%',fontSize:10,background:ov&&ov.bud!=null?'var(--gold-bg)':'transparent'}}
                              onChange={e=>{e.stopPropagation();const newBud=+e.target.value;
                                const cpp=tot&&tot.cpp30T>0?tot.cpp30T:null;
                                const derivedTrp=cpp?Math.round(newBud/cpp):null;
                                onUpdate({weeklyOverrides:{...(flight.weeklyOverrides||{}),[i]:{...(flight.weeklyOverrides&&flight.weeklyOverrides[i]||{}),bud:newBud,...(derivedTrp!=null?{trp:derivedTrp}:{})}}});}}/>
                          </td>}
                          <td style={{padding:'2px 3px',border:'1px solid var(--border)'}}>
                            <input className="inn" type="number" min={0} step={1}
                              value={dispTrp} style={{width:'100%',fontSize:10,background:ov&&ov.trp!=null?'var(--gold-bg)':'transparent'}}
                              onChange={e=>{e.stopPropagation();const newTrp=+e.target.value;
                                const cpp=tot&&tot.cpp30T>0?tot.cpp30T:null;
                                const derivedBud=cpp&&flight.mode==='budget'?Math.round(newTrp*cpp):null;
                                onUpdate({weeklyOverrides:{...(flight.weeklyOverrides||{}),[i]:{...(flight.weeklyOverrides&&flight.weeklyOverrides[i]||{}),trp:newTrp,...(derivedBud!=null?{bud:derivedBud}:{})}}});}}/>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div style={{display:'flex',gap:4}}>
            <button className="btn btn-ghost btn-sm" onClick={e=>{e.stopPropagation();onClone();}}>Clone</button>
            {canRemove&&<button className="btn btn-danger btn-sm" onClick={e=>{e.stopPropagation();onRemove();}}>Remove</button>}
          </div>
        </div>
      )}
    </div>
  );
}


// ─── EXCEL PRICE LIST UPLOAD ─────────────────────────────────────
// Parses Nova TV style price list Excel (SheetJS)
function PriceListUpload({setChPrices,setDiscScales,setSeasonCoeffs,onApplied,chPrices,discScales,seasonCoeffs}){
  const[status,setStatus]=useState('');
  const[preview,setPreview]=useState(null);
  const[parsed,setParsed]=useState(null);
  const fileRef=useRef();

  async function loadSheetJS(){
    if(window.XLSX)return window.XLSX;
    return new Promise((res,rej)=>{
      const s=document.createElement('script');
      s.src='https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      s.onload=()=>res(window.XLSX);
      s.onerror=()=>rej(new Error('Failed to load SheetJS'));
      document.head.appendChild(s);
    });
  }

  // Channel name normalizer — maps Excel names to CH ids
  const NAME_MAP={
    'cinema 1':'C1','cinema1':'C1',
    '7 tv':'7TV','7tv':'7TV','7 TV':'7TV',
    'tv8':'TV8','tv 8':'TV8',' tv8':'TV8',
    'star tv':'STA','star':'STA',
    'one tv':'ONE','one':'ONE',
    'next tv':'NEX','next':'NEX',
    'premiera tv':'PRE','premiera':'PRE',
    'exclusiv tv':'EXC','exclusiv':'EXC',
    'tv 9':'TV9','tv9':'TV9',
    'n4':'N4',
    'global 24':'G24','global24':'G24',
    'r live':'RLV','rlive':'RLV',
    'noroc tv':'NOR','noroc':'NOR',
    'vocea basarabiei':'VOC','vocea':'VOC',
    'we sport':'WSP','wesport':'WSP',
    'unu tv':'UNU','unu':'UNU',
    'jurnal tv':'JUR','jurnal':'JUR',
    'pro tv':'PTV','protv':'PTV',
    'moldova 1':'MD1','moldova1':'MD1',
    'moldova 2':'MD2','moldova2':'MD2',
  };
  function normCh(name){
    if(!name)return null;
    const k=name.toString().toLowerCase().trim().replace(/\s+/g,' ');
    if(NAME_MAP[k])return NAME_MAP[k];
    // try partial match
    for(const[n,id] of Object.entries(NAME_MAP)){
      if(k.includes(n)||n.includes(k))return id;
    }
    return null;
  }

  // Parse month seasonal from text like "- 20%, discount" or "+ 10%, extra charge" or number
  function parseSeasonPct(v){
    if(v==null||v==='')return 1;
    if(typeof v==='number')return 1+v;// already fraction e.g. 0.1 = +10%
    const s=v.toString();
    const m=s.match(/([+-])\s*(\d+)/);
    if(!m)return 1;
    const sign=m[1]==='+'?1:-1;
    return 1+sign*parseInt(m[2])/100;
  }

  async function handleFile(e){
    const file=e.target.files[0];
    if(!file)return;
    setStatus('Loading SheetJS...');
    try{
      const XLSX=await loadSheetJS();
      setStatus('Parsing...');
      const ab=await file.arrayBuffer();
      const wb=XLSX.read(ab,{type:'array'});
      const ws=wb.Sheets[wb.SheetNames[0]];
      const rows=XLSX.utils.sheet_to_json(ws,{header:1,defval:null,raw:true});

      const newChPrices={};
      const newSeasonCoeffs={};
      const newMS={};// channel group -> [[threshold,disc],...]
      const newTS={};// channel/SH -> [[threshold,disc],...]

      // === PARSE CHANNEL PRICES ===
      // Find rows where col[2] is a channel name and col[6] is a number (PT price)
      for(let i=0;i<rows.length;i++){
        const r=rows[i];
        const chId=normCh(r[2]);
        if(chId&&typeof r[6]==='number'&&r[6]>0){
          const pt=r[6];
          const ot=typeof r[7]==='number'&&r[7]>0?r[7]:pt*0.5;
          const otr=pt>0?ot/pt:0.5;
          newChPrices[chId]={pt,otr};
        }
      }

      // === PARSE SEASONAL COEFFICIENTS ===
      // Look for "Seasonal" section — find row with month names
      const MONTH_NAMES=['January','February','March','April','May','June','July','August','September','October','November','December'];
      const MONTH_KEYS=['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
      const MONTH_IDX={};MONTH_NAMES.forEach((m,i)=>{MONTH_IDX[m.toLowerCase()]=i;});

      for(let i=0;i<rows.length;i++){
        const r=rows[i];
        // Find seasonal section — rows with month name + value pattern
        // Row format: [null, null, null, null, 'January', '-20%, discount', 'July', '-20%, discount']
        if(r[4]&&typeof r[4]==='string'&&MONTH_IDX[r[4].toLowerCase()]!=null){
          const mi1=MONTH_IDX[r[4].toLowerCase()];
          const coeff1=parseSeasonPct(r[5]);
          if(coeff1!==null){
            // Apply to ALL channels (this is a global seasonal for this sales house)
            // We'll store as 'ALL' key and apply to all channels not individually set
            if(!newSeasonCoeffs['__global'])newSeasonCoeffs['__global']={};
            newSeasonCoeffs['__global'][MONTH_KEYS[mi1]]=coeff1;
          }
          // Second month on same row (col 6-7)
          if(r[6]&&typeof r[6]==='string'&&MONTH_IDX[r[6].toLowerCase()]!=null){
            const mi2=MONTH_IDX[r[6].toLowerCase()];
            const coeff2=parseSeasonPct(r[7]);
            if(!newSeasonCoeffs['__global'])newSeasonCoeffs['__global']={};
            newSeasonCoeffs['__global'][MONTH_KEYS[mi2]]=coeff2;
          }
        }
      }

      // === PARSE MONTHLY DISCOUNT SCALE ===
      // Find the monthly discount section — rows with [from, to, discount%] pattern
      // Two groups side by side (Cinema1 left, 7TV/TV8 right)
      let inMonthlyDisc=false;
      const msLeft=[],msRight=[];
      for(let i=0;i<rows.length;i++){
        const r=rows[i];
        const s=r[2]&&r[2].toString().toLowerCase();
        if(s&&(s.includes('monthly average budget')||s.includes('monthly avg'))){
          inMonthlyDisc=true;continue;
        }
        if(inMonthlyDisc){
          const fromL=typeof r[2]==='number'?r[2]:null;
          const toL=typeof r[3]==='number'?r[3]:null;
          const discL=typeof r[4]==='number'?r[4]:null;
          const fromR=typeof r[5]==='number'?r[5]:null;
          const toR=typeof r[6]==='number'?r[6]:null;
          const discR=typeof r[7]==='number'?r[7]:null;
          if(toL!==null&&discL!==null)msLeft.push([toL,discL]);
          if(toR!==null&&discR!==null)msRight.push([toR,discR]);
          // Stop when we hit the total budget section
          if(r[2]&&r[2].toString().toLowerCase().includes('annual'))break;
          if(r[2]&&r[2].toString().toLowerCase().includes('total budget'))break;
        }
      }

      // === PARSE TOTAL/ANNUAL DISCOUNT SCALE ===
      let inAnnualDisc=false;
      const tsRows=[];
      for(let i=0;i<rows.length;i++){
        const r=rows[i];
        const s=r[2]&&r[2].toString().toLowerCase();
        if(s&&(s.includes('annual budget')||s.includes('total budget'))){
          inAnnualDisc=true;continue;
        }
        if(inAnnualDisc){
          const fromV=typeof r[2]==='number'?r[2]:null;
          const toV=typeof r[4]==='number'?r[4]:null;
          const discV=typeof r[6]==='number'?r[6]:null;
          if(toV!==null&&discV!==null)tsRows.push([toV,discV]);
          if(tsRows.length>0&&r[2]&&typeof r[2]==='string'&&r[2].toLowerCase().includes('off prime'))break;
          if(tsRows.length>=12)break;
        }
      }

      // Determine which channels got left vs right MS group
      // Left group = Cinema 1 (C1), Right group = 7TV + TV8
      const msChLeft=Object.keys(newChPrices).filter(id=>id==='C1');
      const msChRight=Object.keys(newChPrices).filter(id=>id==='7TV'||id==='TV8');
      msChLeft.forEach(id=>{if(msLeft.length>0)newMS[id]=msLeft;});
      msChRight.forEach(id=>{if(msRight.length>0)newMS[id]=msRight;});
      msChLeft.forEach(id=>{if(tsRows.length>0)newTS[id]=tsRows;});
      msChRight.forEach(id=>{if(tsRows.length>0)newTS[id]=tsRows;});

      // Build preview
      const previewItems=[];
      Object.entries(newChPrices).forEach(([id,p])=>{
        const ch=CH.find(c=>c.id===id);
        previewItems.push({type:'price',label:(ch?ch.n:id)+': PT=€'+p.pt+', OTR='+p.otr.toFixed(2)});
      });
      if(newSeasonCoeffs['__global']){
        Object.entries(newSeasonCoeffs['__global']).forEach(([m,c])=>{
          previewItems.push({type:'season',label:'Season '+m+': ×'+c.toFixed(2)});
        });
      }
      if(msLeft.length>0)previewItems.push({type:'disc',label:'Monthly disc scale: '+msLeft.length+' tiers (C1)'});
      if(msRight.length>0)previewItems.push({type:'disc',label:'Monthly disc scale: '+msRight.length+' tiers (7TV/TV8)'});
      if(tsRows.length>0)previewItems.push({type:'disc',label:'Annual disc scale: '+tsRows.length+' tiers'});

      setParsed({newChPrices,newSeasonCoeffs,newMS,newTS});
      setPreview(previewItems);
      setStatus('✓ Parsed '+previewItems.length+' items. Review and apply.');
    }catch(err){
      setStatus('⚠ Error: '+err.message);
      console.error(err);
    }
    e.target.value='';
  }

  function applyParsed(){
    if(!parsed)return;
    const{newChPrices,newSeasonCoeffs,newMS,newTS}=parsed;
    if(Object.keys(newChPrices).length>0)setChPrices(p=>({...p,...newChPrices}));
    if(Object.keys(newMS).length>0)setDiscScales(p=>({...p,MS:{...p.MS,...newMS}}));
    if(Object.keys(newTS).length>0)setDiscScales(p=>({...p,TS:{...p.TS,...newTS}}));
    // Apply global seasonal to all channels
    if(newSeasonCoeffs['__global']){
      const g=newSeasonCoeffs['__global'];
      setSeasonCoeffs(p=>{
        const updated={...p};
        CH.forEach(ch=>{updated[ch.id]={...(updated[ch.id]||{}),...g};});
        return updated;
      });
    }
    setStatus('✓ Applied! Saving...');
    setPreview(null);setParsed(null);
    if(onApplied)setTimeout(onApplied,150);
  }

  return(
    <div style={{marginBottom:16,padding:12,border:'1px solid var(--gold-border)',borderRadius:8,background:'var(--gold-bg)'}}>
      <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:8,padding:'5px 8px',background:'var(--surface)',borderRadius:6,border:'1px solid var(--border)'}}>
        <span style={{fontSize:10,color:'var(--text2)',fontWeight:600}}>🌐 System Prices</span>
        <span style={{fontSize:9,color:'var(--text3)',flex:1}}>Shared default price list</span>
        <button className="btn btn-ghost btn-sm" style={{fontSize:9,padding:'2px 8px'}} onClick={async()=>{
          try{
            const r=await fetch('/api/tv/plans/system/prices');
            if(!r.ok){const e=await r.json();throw new Error(e.error||'Not found');}
            const d=await r.json();
            if(d.chPrices)setChPrices(q=>({...q,...d.chPrices}));
            if(d.discScales&&d.discScales.MS)setDiscScales(q=>({...q,MS:{...q.MS,...d.discScales.MS}}));
            if(d.discScales&&d.discScales.TS)setDiscScales(q=>({...q,TS:{...q.TS,...d.discScales.TS}}));
            if(d.seasonCoeffs)setSeasonCoeffs(q=>({...q,...d.seasonCoeffs}));
            setStatus('✓ System prices loaded ('+Object.keys(d.chPrices||{}).length+' channels)');
            if(onApplied)setTimeout(onApplied,150);
          }catch(e){setStatus('⚠ '+e.message);}
        }}>⬇ Load</button>
        <button className="btn btn-ghost btn-sm" style={{fontSize:9,padding:'2px 8px'}} onClick={async()=>{
          try{
            const tok=localStorage.getItem('plan_token');
            const r=await fetch('/api/tv/plans/system/prices',{method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+tok},body:JSON.stringify({chPrices,discScales,seasonCoeffs})});
            if(!r.ok){const e=await r.json();throw new Error(e.error||'Save failed');}
            setStatus('✓ Saved as system default!');
          }catch(e){setStatus('⚠ '+e.message);}
        }}>⬆ Set default</button>
      </div>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8}}>
        <div style={{fontWeight:700,fontSize:11,color:'var(--gold-dark)'}}>📂 Upload Price List (Excel)</div>
        <div style={{fontSize:10,color:'var(--text3)',flex:1}}>Supports Nova TV format (.xlsx). Extracts CPPs, discounts &amp; seasonal coefficients automatically.</div>
        <button className="btn btn-ghost btn-sm" onClick={()=>fileRef.current&&fileRef.current.click()}>Choose file</button>
        <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleFile}/>
      </div>
      {status&&<div style={{fontSize:10,color:status.startsWith('⚠')?'var(--red)':'var(--green)',marginBottom:6}}>{status}</div>}
      {preview&&preview.length>0&&(
        <div>
          <div style={{fontSize:10,color:'var(--text2)',marginBottom:6,fontWeight:600}}>Preview ({preview.length} items found):</div>
          <div style={{maxHeight:140,overflowY:'auto',marginBottom:8,display:'flex',flexWrap:'wrap',gap:4}}>
            {preview.map((item,i)=>(
              <span key={i} style={{fontSize:9,padding:'2px 6px',borderRadius:10,background:item.type==='price'?'var(--gold-bg)':item.type==='season'?'#1a3a5c':'var(--surface3)',color:item.type==='season'?'#90caf9':'var(--text2)',border:'1px solid var(--border)'}}>{item.label}</span>
            ))}
          </div>
          <div style={{display:'flex',gap:6}}>
            <button className="btn btn-primary btn-sm" onClick={applyParsed}>✓ Apply to plan</button>
            <button className="btn btn-ghost btn-sm" onClick={()=>{setPreview(null);setParsed(null);setStatus('');}}>Discard</button>
          </div>
        </div>
      )}
      <div style={{fontSize:9,color:'var(--text3)',marginTop:6}}>Expected format: Nova TV price list with channel names in col C, PT price in col G, OT price in col H. Monthly/annual discount tables parsed automatically.</div>
    </div>
  );
}

// ─── SEASONAL RATINGS TAB ────────────────────────────────────────
function SeasonalRatingsTab({audiences,audSeasonData,setAudSeasonData}){
  const[selAud,setSelAud]=useState('ba');
  const[pasteMsg,setPasteMsg]=useState('');
  const[pasteVal,setPasteVal]=useState('');

  const aud=audiences.find(a=>a.id===selAud)||audiences[0];

  // Excel paste parser for seasonal data
  // Format: Channel | Spring_PT | Spring_OT | Summer_PT | Summer_OT | Autumn_PT | Autumn_OT | Winter_PT | Winter_OT | Spring_SHR | Summer_SHR | Autumn_SHR | Winter_SHR
  function parsePaste(text){
    const lines=text.trim().split('\n').filter(l=>l.trim());
    let imported=0;
    const updates={...audSeasonData};
    if(!updates[selAud])updates[selAud]={};
    lines.forEach(line=>{
      const cols=line.split('\t').map(s=>s.trim().replace(',','.'));
      if(cols.length<3)return;
      const ch=CH.find(c=>c.n.toLowerCase()===cols[0].toLowerCase()||c.id.toLowerCase()===cols[0].toLowerCase());
      if(!ch)return;
      const p=(v,i)=>cols[i]&&!isNaN(parseFloat(cols[i]))?parseFloat(cols[i])/100:null;
      const d={};
      const fields=['spring_pt','spring_ot','summer_pt','summer_ot','autumn_pt','autumn_ot','winter_pt','winter_ot','spring_shr','summer_shr','autumn_shr','winter_shr'];
      fields.forEach((f,i)=>{const v=p(cols,i+1);if(v!==null)d[f]=v;});
      // Also keep legacy amr/shr/aff
      if(!updates[selAud][ch.id])updates[selAud][ch.id]={};
      updates[selAud][ch.id]={...updates[selAud][ch.id],...d};
      imported++;
    });
    if(imported>0){setAudSeasonData(updates);setPasteMsg('✓ Imported '+imported+' channels');setPasteVal('');}
    else setPasteMsg('⚠ No channels matched. Expected: Channel | Spring_PT% | Spring_OT% | ... (all as %)');
  }

  function updateCell(chId,field,val){
    setAudSeasonData(p=>{
      const updated={...p};
      if(!updated[selAud])updated[selAud]={};
      updated[selAud]={...updated[selAud],[chId]:{...(updated[selAud][chId]||{}),
        [field]:val===''||val==null?null:parseFloat(val)/100}};
      return updated;
    });
  }

  const COLS_AMR=[
    {key:'spring_pt',label:'Spr PT',color:'#4ade80'},
    {key:'spring_ot',label:'Spr OT',color:'#86efac'},
    {key:'summer_pt',label:'Sum PT',color:'#fbbf24'},
    {key:'summer_ot',label:'Sum OT',color:'#fde68a'},
    {key:'autumn_pt',label:'Aut PT',color:'#fb923c'},
    {key:'autumn_ot',label:'Aut OT',color:'#fdba74'},
    {key:'winter_pt',label:'Win PT',color:'#60a5fa'},
    {key:'winter_ot',label:'Win OT',color:'#93c5fd'},
  ];
  const COLS_SHR=[
    {key:'spring_shr',label:'Spr SHR%',color:'#4ade80'},
    {key:'summer_shr',label:'Sum SHR%',color:'#fbbf24'},
    {key:'autumn_shr',label:'Aut SHR%',color:'#fb923c'},
    {key:'winter_shr',label:'Win SHR%',color:'#60a5fa'},
  ];

  const selData=(audSeasonData&&audSeasonData[selAud])||{};

  return(
    <div>
      <div style={{fontSize:11,color:'var(--text3)',marginBottom:10}}>
        Set per-channel AMR% (PT &amp; Off-Prime) and SHR% by season. Used in flight calculations when a flight falls within a season.
        Leave blank to use the annual average from Audience Data tab.
      </div>

      {/* Audience selector */}
      <div style={{display:'flex',gap:6,marginBottom:12,alignItems:'center'}}>
        <span style={{fontSize:10,color:'var(--text3)'}}>Audience:</span>
        {audiences.map(a=>(
          <button key={a.id} className={'btn btn-xs '+(selAud===a.id?'btn-primary':'btn-ghost')} onClick={()=>setSelAud(a.id)}>
            {a.name}
          </button>
        ))}
      </div>

      {/* Paste import */}
      <div style={{marginBottom:12,padding:10,background:'var(--surface)',borderRadius:6,border:'1px solid var(--border)'}}>
        <div style={{fontSize:10,fontWeight:600,color:'var(--text2)',marginBottom:4}}>Paste from Excel</div>
        <div style={{fontSize:9,color:'var(--text3)',marginBottom:4}}>
          Format: Channel | Spr_PT% | Spr_OT% | Sum_PT% | Sum_OT% | Aut_PT% | Aut_OT% | Win_PT% | Win_OT% | Spr_SHR% | Sum_SHR% | Aut_SHR% | Win_SHR%
        </div>
        <textarea className="inn" value={pasteVal} onChange={e=>setPasteVal(e.target.value)}
          style={{width:'100%',height:50,fontSize:9,fontFamily:'monospace',marginBottom:4}}
          placeholder="Channel\tSpring_PT%\tSpring_OT%..."/>
        <div style={{display:'flex',gap:6,alignItems:'center'}}>
          <button className="btn btn-primary btn-sm" onClick={()=>parsePaste(pasteVal)}>Import</button>
          {pasteMsg&&<span style={{fontSize:10,color:pasteMsg.startsWith('✓')?'var(--green)':'var(--amber)'}}>{pasteMsg}</span>}
        </div>
      </div>

      {/* AMR table */}
      <div style={{marginBottom:16}}>
        <div className="lbl" style={{marginBottom:6}}>Seasonal AMR% (used for TRP calculation)</div>
        <div style={{overflowX:'auto'}}>
          <table>
            <thead>
              <tr>
                <th style={{minWidth:120}}>Channel</th>
                {COLS_AMR.map(c=><th key={c.key} style={{minWidth:56,fontSize:8,color:c.color}}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {CH.map(ch=>{
                const d=selData[ch.id]||{};
                const legacyAmr=((aud&&aud.data&&aud.data[ch.id])?aud.data[ch.id].amr:ch.defAmr)||0;
                return(
                  <tr key={ch.id}>
                    <td style={{fontWeight:600,fontSize:10}}>{ch.n}</td>
                    {COLS_AMR.map(col=>{
                      const v=d[col.key];
                      return(
                        <td key={col.key} style={{padding:'1px 2px'}}>
                          <input className="inn" type="number" min={0} max={100} step={0.01}
                            value={v!=null?(v*100).toFixed(2):''}
                            placeholder={(legacyAmr*100).toFixed(2)}
                            style={{width:52,fontSize:9,padding:'1px 3px',background:v!=null?col.color+'22':'transparent'}}
                            onChange={e=>updateCell(ch.id,col.key,e.target.value===''?null:e.target.value)}/>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* SHR table */}
      <div style={{marginBottom:16}}>
        <div className="lbl" style={{marginBottom:6}}>Seasonal Channel SHR% (overrides default mix weight by season)</div>
        <div style={{overflowX:'auto'}}>
          <table>
            <thead>
              <tr>
                <th style={{minWidth:120}}>Channel</th>
                {COLS_SHR.map(c=><th key={c.key} style={{minWidth:64,fontSize:8,color:c.color}}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {CH.map(ch=>{
                const d=selData[ch.id]||{};
                const legacyShr=((aud&&aud.data&&aud.data[ch.id])?aud.data[ch.id].shr:ch.defShr)||0;
                return(
                  <tr key={ch.id}>
                    <td style={{fontWeight:600,fontSize:10}}>{ch.n}</td>
                    {COLS_SHR.map(col=>{
                      const v=d[col.key];
                      return(
                        <td key={col.key} style={{padding:'1px 2px'}}>
                          <input className="inn" type="number" min={0} max={100} step={0.01}
                            value={v!=null?(v*100).toFixed(2):''}
                            placeholder={(legacyShr*100).toFixed(2)}
                            style={{width:60,fontSize:9,padding:'1px 3px',background:v!=null?col.color+'22':'transparent'}}
                            onChange={e=>updateCell(ch.id,col.key,e.target.value===''?null:e.target.value)}/>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <button className="btn btn-ghost btn-sm" style={{marginTop:6}} onClick={()=>{
          setAudSeasonData(p=>{const u={...p};delete u[selAud];return u;});
        }}>↺ Reset seasonal data for this audience</button>
      </div>
    </div>
  );
}

// ─── ACTUALS VIEW ────────────────────────────────────────────────
// Data structure: actuals[flightId][weekIdx] = { channels: {chId: {wtrp,bud,wgrp}}, total: {wtrp,bud,wgrp} }
// Upload format (per channel): Week | Channel | wTRP | Budget | wGRP
// Upload format (totals only): Week | wTRP | Budget | wGRP  (no channel name in col 2)
function ActualsView({flights,flightResults,baseline,actuals,setActuals,onSetBaseline}){
  const[uploadMsg,setUploadMsg]=useState('');
  const[pasteVal,setPasteVal]=useState('');
  const[selFlightId,setSelFlightId]=useState(flights[0]&&flights[0].id);
  const[viewMode,setViewMode]=useState('channels');// 'channels' | 'total'
  const fileRef=useRef();

  const selFlight=flights.find(f=>f.id===selFlightId)||flights[0];
  const selResult=selFlight&&flightResults[selFlight.id];
  const selActuals=(selFlight&&actuals[selFlight.id])||{};

  // Channel name normalizer (same as PriceListUpload)
  const CH_NAME_MAP={
    'cinema 1':'C1','cinema1':'C1',
    '7 tv':'7TV','7tv':'7TV',
    'tv8':'TV8','tv 8':'TV8',' tv8':'TV8',
    'star tv':'STA','star':'STA',
    'one tv':'ONE','one':'ONE',
    'next tv':'NEX','next':'NEX',
    'premiera tv':'PRE','premiera':'PRE',
    'exclusiv tv':'EXC','exclusiv':'EXC',
    'tv 9':'TV9','tv9':'TV9',
    'n4':'N4',
    'global 24':'G24','global24':'G24',
    'r live':'RLV','rlive':'RLV',
    'noroc tv':'NOR','noroc':'NOR',
    'vocea basarabiei':'VOC','vocea':'VOC',
    'we sport':'WSP','wesport':'WSP',
    'unu tv':'UNU','unu':'UNU',
    'jurnal tv':'JUR','jurnal':'JUR',
    'pro tv':'PTV','protv':'PTV',
    'moldova 1':'MD1','moldova1':'MD1',
    'moldova 2':'MD2','moldova2':'MD2',
  };
  function normCh(name){
    if(!name)return null;
    const k=name.toString().toLowerCase().trim().replace(/\s+/g,' ');
    if(CH_NAME_MAP[k])return CH_NAME_MAP[k];
    for(const[n,id] of Object.entries(CH_NAME_MAP)){if(k.includes(n)||n.includes(k))return id;}
    // try CH array
    const found=CH.find(c=>c.n.toLowerCase()===k||c.id.toLowerCase()===k);
    return found?found.id:null;
  }

  // Parse paste — supports both formats:
  //   Per-channel: Week | Channel | wTRP | Budget | wGRP
  //   Totals:      Week | wTRP | Budget | wGRP  (col2 is a number)
  function parseActualsPaste(text){
    const lines=text.trim().split('\n').filter(l=>l.trim());
    let importedRows=0;
    const updates=JSON.parse(JSON.stringify(actuals));
    if(!updates[selFlightId])updates[selFlightId]={};

    lines.forEach(line=>{
      const cols=line.split('\t').map(s=>s.trim().replace(',','.'));
      if(cols.length<2)return;
      const weekNum=parseInt(cols[0]);
      if(!weekNum||isNaN(weekNum))return;
      const wi=weekNum-1;

      // Detect format: if col[1] is a channel name → per-channel format
      const chId=normCh(cols[1]);
      if(chId){
        // Per-channel row: Week | Channel | wTRP | Budget | wGRP
        const wtrp=parseFloat(cols[2])||0;
        const bud=parseFloat(cols[3])||0;
        const wgrp=parseFloat(cols[4])||0;
        if(!updates[selFlightId][wi])updates[selFlightId][wi]={channels:{},total:{wtrp:0,bud:0,wgrp:0}};
        if(!updates[selFlightId][wi].channels)updates[selFlightId][wi].channels={};
        updates[selFlightId][wi].channels[chId]={wtrp,bud,wgrp};
        importedRows++;
      } else {
        // Totals row: Week | wTRP | Budget | wGRP
        const wtrp=parseFloat(cols[1])||0;
        const bud=parseFloat(cols[2])||0;
        const wgrp=parseFloat(cols[3])||0;
        if(!updates[selFlightId][wi])updates[selFlightId][wi]={channels:{},total:{wtrp:0,bud:0,wgrp:0}};
        updates[selFlightId][wi].total={wtrp,bud,wgrp};
        importedRows++;
      }
    });

    // After parsing, recalculate totals from channels where channels exist
    Object.keys(updates[selFlightId]).forEach(wi=>{
      const wk=updates[selFlightId][wi];
      if(wk.channels&&Object.keys(wk.channels).length>0){
        const tot=Object.values(wk.channels).reduce((s,c)=>({
          wtrp:s.wtrp+(c.wtrp||0),bud:s.bud+(c.bud||0),wgrp:s.wgrp+(c.wgrp||0)
        }),{wtrp:0,bud:0,wgrp:0});
        wk.total=tot;
      }
    });

    if(importedRows>0){setActuals(updates);setUploadMsg('✓ Imported '+importedRows+' rows');setPasteVal('');}
    else setUploadMsg('⚠ No data matched. Per-channel: Week | Channel | wTRP | Budget | wGRP. Totals: Week | wTRP | Budget | wGRP');
  }

  async function handleFile(e){
    const file=e.target.files[0];
    if(!file)return;
    setUploadMsg('Parsing...');
    try{
      if(!window.XLSX){
        await new Promise((res,rej)=>{
          const s=document.createElement('script');
          s.src='https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
          s.onload=()=>res();s.onerror=()=>rej(new Error('SheetJS failed'));
          document.head.appendChild(s);
        });
      }
      const XLSX=window.XLSX;
      const ab=await file.arrayBuffer();
      const wb=XLSX.read(ab,{type:'array'});
      const ws=wb.Sheets[wb.SheetNames[0]];
      const rows=XLSX.utils.sheet_to_json(ws,{header:1,defval:null});
      let text='';
      rows.forEach(r=>{
        if(r[0]&&!isNaN(parseInt(r[0]))){
          text+=r.slice(0,5).join('\t')+'\n';
        }
      });
      parseActualsPaste(text);
    }catch(err){setUploadMsg('⚠ '+err.message);}
    e.target.value='';
  }

  // Get total for a week (from channel sum or direct total)
  function weekTotal(wi){
    const wk=selActuals[wi];
    if(!wk)return null;
    if(wk.total&&(wk.total.wtrp||wk.total.bud))return wk.total;
    return null;
  }

  // Get channel value for a week
  function weekCh(wi,chId){
    const wk=selActuals[wi];
    if(!wk||!wk.channels)return null;
    return wk.channels[chId]||null;
  }

  // Redistribution using totals
  function computeRedistribution(){
    if(!selResult||!selFlight)return null;
    const weekly=selResult.weekly;
    const nW=weekly.length;
    const actualWeeks=Object.keys(selActuals).map(Number).sort((a,b)=>a-b);
    if(actualWeeks.length===0)return null;
    const lastActualWi=Math.max(...actualWeeks);
    let actBud=0,actTrp=0;
    for(let i=0;i<=lastActualWi;i++){const t=weekTotal(i);if(t){actBud+=t.bud||0;actTrp+=t.wtrp||0;}}
    const totalPlanBud=selResult.tot.bud;
    const totalPlanTrp=selResult.tot.wtrp;
    const remBud=totalPlanBud-actBud;
    const remTrp=totalPlanTrp-actTrp;
    const futureWks=[];
    for(let i=lastActualWi+1;i<nW;i++)futureWks.push(i);
    const nFuture=futureWks.length||1;
    // Channel-level redistribution based on planned mix
    const chRedist={};
    selResult.chs.forEach(r=>{
      const chActBud=Object.keys(selActuals).reduce((s,wi)=>{const c=weekCh(+wi,r.ch.id);return s+(c?c.bud||0:0);},0);
      const chActTrp=Object.keys(selActuals).reduce((s,wi)=>{const c=weekCh(+wi,r.ch.id);return s+(c?c.wtrp||0:0);},0);
      const chRemBud=r.b-chActBud;
      const chRemTrp=r.wtrp-chActTrp;
      chRedist[r.ch.id]={remBud:chRemBud,remTrp:chRemTrp,perWkBud:chRemBud/nFuture,perWkTrp:chRemTrp/nFuture};
    });
    return{lastActualWi,actBud,actTrp,remBud,remTrp,futureWks,redistBudPerWk:remBud/nFuture,redistTrpPerWk:remTrp/nFuture,totalPlanBud,totalPlanTrp,chRedist};
  }

  const redist=computeRedistribution();

  if(!selFlight||!selResult){
    return<div style={{padding:20,color:'var(--text3)',textAlign:'center'}}>No flights found.</div>;
  }

  const nW=selResult.weekly.length;
  const planWeeks=selResult.weekly;
  const actChs=selResult.chs;// channels in this flight

  // Check if we have any channel-level actuals
  const hasChannelActuals=Object.values(selActuals).some(wk=>wk.channels&&Object.keys(wk.channels).length>0);

  return(
    <div>
      {/* Header */}
      <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
        <div style={{fontWeight:700,fontSize:12,color:'var(--text)'}}>📌 Actuals vs Plan</div>
        {baseline
          ? <span style={{fontSize:10,color:'var(--green)',background:'var(--green-dim)',padding:'2px 8px',borderRadius:10}}>✓ Baseline locked</span>
          : <span style={{fontSize:10,color:'var(--amber)',background:'var(--amber-dim)',padding:'2px 8px',borderRadius:10}}>No baseline set</span>
        }
        {!baseline&&<button className="btn btn-primary btn-sm" onClick={onSetBaseline}>🔒 Lock current plan as baseline</button>}
        <div style={{flex:1}}/>
        <div className="mode-sw">
          <button className={'mode-btn'+(viewMode==='channels'?' on':'')} onClick={()=>setViewMode('channels')}>By Channel</button>
          <button className={'mode-btn'+(viewMode==='total'?' on':'')} onClick={()=>setViewMode('total')}>Totals only</button>
        </div>
        <select className="inn" value={selFlightId||''} onChange={e=>setSelFlightId(e.target.value)} style={{width:140,fontSize:11}}>
          {flights.map(f=><option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
      </div>

      {/* Upload */}
      <div style={{marginBottom:14,padding:12,background:'var(--surface)',borderRadius:8,border:'1px solid var(--border)'}}>
        <div style={{fontWeight:600,fontSize:11,color:'var(--gold-dark)',marginBottom:4}}>Upload actuals for: {selFlight.label}</div>
        <div style={{fontSize:9,color:'var(--text3)',marginBottom:6}}>
          <strong>Per-channel:</strong> Week# | Channel name | wTRP | Budget | wGRP &nbsp;|&nbsp;
          <strong>Totals:</strong> Week# | wTRP | Budget | wGRP
        </div>
        <div style={{display:'flex',gap:6,marginBottom:6,alignItems:'flex-start'}}>
          <textarea className="inn" value={pasteVal} onChange={e=>setPasteVal(e.target.value)}
            style={{flex:1,height:70,fontSize:9,fontFamily:'monospace'}}
            placeholder={"Per-channel example:\n1\tCinema 1\t245\t31850\n1\t7 TV\t120\t15600\n2\tCinema 1\t238\t30940\n..."}/>
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
            <button className="btn btn-primary btn-sm" onClick={()=>parseActualsPaste(pasteVal)}>Import paste</button>
            <button className="btn btn-ghost btn-sm" onClick={()=>fileRef.current&&fileRef.current.click()}>📂 Excel file</button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleFile}/>
          </div>
        </div>
        {uploadMsg&&<div style={{fontSize:10,color:uploadMsg.startsWith('✓')?'var(--green)':'var(--amber)'}}>{uploadMsg}</div>}
      </div>

      {/* Redistribution summary */}
      {redist&&(
        <div style={{marginBottom:14,padding:12,background:'var(--gold-bg)',borderRadius:8,border:'1px solid var(--gold-border)'}}>
          <div style={{fontWeight:600,fontSize:11,color:'var(--gold-dark)',marginBottom:8}}>Redistribution forecast</div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap',marginBottom:6}}>
            {[
              {l:'Actual Budget delivered',v:'€'+Math.round(redist.actBud).toLocaleString(),c:'var(--blue)'},
              {l:'Actual TRP delivered',v:Math.round(redist.actTrp)+' TRP',c:'var(--purple)'},
              {l:'Remaining Budget',v:'€'+Math.round(redist.remBud).toLocaleString(),c:'var(--amber)'},
              {l:'Remaining TRP',v:Math.round(redist.remTrp)+' TRP',c:'var(--amber)'},
              {l:'Weeks remaining',v:redist.futureWks.length,c:'var(--text2)'},
              {l:'Forecast Budget/wk',v:'€'+Math.round(redist.redistBudPerWk).toLocaleString(),c:'var(--green)'},
              {l:'Forecast TRP/wk',v:Math.round(redist.redistTrpPerWk)+' TRP',c:'var(--green)'},
            ].map(k=>(
              <div key={k.l} style={{padding:'4px 8px',background:'var(--surface)',borderRadius:5,border:'1px solid var(--border)'}}>
                <div style={{fontSize:8,color:'var(--text3)',textTransform:'uppercase',marginBottom:1}}>{k.l}</div>
                <div className="mono" style={{fontSize:11,fontWeight:700,color:k.c}}>{k.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main table */}
      <div style={{overflowX:'auto'}}>
        <table className="fc-table">
          <thead>
            <tr>
              <th className="row-hdr" style={{width:160}}>Channel / Metric</th>
              {planWeeks.map(w=><th key={w.week} style={{minWidth:48,fontSize:9}}>W{w.week}</th>)}
              <th style={{background:'var(--text)',color:'var(--gold)',minWidth:68}}>TOTAL</th>
            </tr>
          </thead>
          <tbody>

            {/* ── PER CHANNEL SECTION ── */}
            {viewMode==='channels'&&actChs.map((r,ci)=>{
              const chColor=CH_COLORS[ci%20];
              const planChTrpPerWk=r.wtrp/nW;// even distribution baseline
              const planChBudPerWk=r.b/nW;
              return(
                <React.Fragment key={r.ch.id}>
                  {/* Channel header */}
                  <tr>
                    <td colSpan={nW+2} style={{padding:'3px 8px',background:chColor+'22',borderLeft:'3px solid '+chColor,fontSize:9,fontWeight:700,color:'var(--text)'}}>
                      {r.ch.n} &nbsp;<span style={{fontWeight:400,color:'var(--text3)'}}>Plan: {Math.round(r.wtrp)} TRP · €{Math.round(r.b/1000)}K</span>
                    </td>
                  </tr>
                  {/* Planned TRP per channel per week */}
                  <tr>
                    <td className="row-hdr" style={{paddingLeft:14,fontSize:9}}>Planned wTRP</td>
                    {planWeeks.map(w=>{
                      const wChPlan=w.chs?w.chs.find(c=>c.id===r.ch.id):null;
                      const v=wChPlan?Math.round(wChPlan.wtrp):Math.round(planChTrpPerWk);
                      return<td key={w.week} style={{padding:0}}>
                        <div className="fc-cell" style={{background:chColor+'33',color:'var(--text)',height:20,fontSize:9}}>{v||'—'}</div>
                      </td>;
                    })}
                    <td style={{padding:'2px 4px',textAlign:'center'}}><span className="mono" style={{fontSize:9,fontWeight:600}}>{Math.round(r.wtrp)}</span></td>
                  </tr>
                  {/* Actual TRP */}
                  <tr>
                    <td className="row-hdr" style={{paddingLeft:14,fontSize:9,color:'var(--blue)'}}>Actual wTRP</td>
                    {planWeeks.map(w=>{
                      const wi=w.week-1;
                      const a=weekCh(wi,r.ch.id);
                      const wChPlan=w.chs?w.chs.find(c=>c.id===r.ch.id):null;
                      const plan=wChPlan?wChPlan.wtrp:planChTrpPerWk;
                      const actual=a?a.wtrp:null;
                      const diff=actual!=null?actual-plan:null;
                      const isOver=diff!=null&&diff>plan*0.05;
                      const isUnder=diff!=null&&diff<-plan*0.05;
                      return<td key={w.week} style={{padding:0}}>
                        <div className="fc-cell" style={{
                          background:actual!=null?(isOver?'var(--green-dim)':isUnder?'var(--red-dim)':'var(--blue-dim)'):'transparent',
                          color:actual!=null?'var(--text)':'var(--border)',height:20,fontSize:9
                        }}>{actual!=null?Math.round(actual):'·'}</div>
                      </td>;
                    })}
                    <td style={{padding:'2px 4px',textAlign:'center'}}>
                      <span className="mono" style={{fontSize:9,color:'var(--blue)',fontWeight:600}}>
                        {Object.keys(selActuals).reduce((s,wi)=>{const c=weekCh(+wi,r.ch.id);return s+(c?c.wtrp||0:0);},0)>0
                          ?Math.round(Object.keys(selActuals).reduce((s,wi)=>{const c=weekCh(+wi,r.ch.id);return s+(c?c.wtrp||0:0);},0)):'—'}
                      </span>
                    </td>
                  </tr>
                  {/* Planned Budget per channel per week */}
                  <tr>
                    <td className="row-hdr" style={{paddingLeft:14,fontSize:9}}>Planned Budget</td>
                    {planWeeks.map(w=>{
                      const wChPlan=w.chs?w.chs.find(c=>c.id===r.ch.id):null;
                      // Budget proportional to TRP share
                      const v=wChPlan?Math.round(r.b*(wChPlan.wtrp/r.wtrp)/1000):Math.round(planChBudPerWk/1000);
                      return<td key={w.week} style={{padding:0}}>
                        <div className="fc-cell" style={{background:chColor+'22',color:'var(--text)',height:20,fontSize:9}}>{v>0?v+'K':'—'}</div>
                      </td>;
                    })}
                    <td style={{padding:'2px 4px',textAlign:'center'}}><span className="mono" style={{fontSize:9,fontWeight:600}}>{Math.round(r.b/1000)}K</span></td>
                  </tr>
                  {/* Actual Budget */}
                  <tr>
                    <td className="row-hdr" style={{paddingLeft:14,fontSize:9,color:'var(--blue)'}}>Actual Budget</td>
                    {planWeeks.map(w=>{
                      const wi=w.week-1;
                      const a=weekCh(wi,r.ch.id);
                      const actual=a?a.bud:null;
                      const wChPlan=w.chs?w.chs.find(c=>c.id===r.ch.id):null;
                      const plan=wChPlan?r.b*(wChPlan.wtrp/r.wtrp):planChBudPerWk;
                      const diff=actual!=null?actual-plan:null;
                      const isOver=diff!=null&&diff>plan*0.05;
                      const isUnder=diff!=null&&diff<-plan*0.05;
                      return<td key={w.week} style={{padding:0}}>
                        <div className="fc-cell" style={{
                          background:actual!=null?(isOver?'var(--green-dim)':isUnder?'var(--red-dim)':'var(--blue-dim)'):'transparent',
                          color:actual!=null?'var(--text)':'var(--border)',height:20,fontSize:9
                        }}>{actual!=null?Math.round(actual/1000)+'K':'·'}</div>
                      </td>;
                    })}
                    <td style={{padding:'2px 4px',textAlign:'center'}}>
                      <span className="mono" style={{fontSize:9,color:'var(--blue)',fontWeight:600}}>
                        {Object.keys(selActuals).reduce((s,wi)=>{const c=weekCh(+wi,r.ch.id);return s+(c?c.bud||0:0);},0)>0
                          ?Math.round(Object.keys(selActuals).reduce((s,wi)=>{const c=weekCh(+wi,r.ch.id);return s+(c?c.bud||0:0);},0)/1000)+'K':'—'}
                      </span>
                    </td>
                  </tr>
                  {/* TRP Variance per channel */}
                  <tr>
                    <td className="row-hdr" style={{paddingLeft:14,fontSize:9,color:'var(--text3)'}}>Δ TRP variance</td>
                    {planWeeks.map(w=>{
                      const wi=w.week-1;
                      const a=weekCh(wi,r.ch.id);
                      if(!a)return<td key={w.week} style={{padding:'2px 4px',textAlign:'center'}}><span style={{color:'var(--text3)',fontSize:8}}>—</span></td>;
                      const wChPlan=w.chs?w.chs.find(c=>c.id===r.ch.id):null;
                      const plan=wChPlan?wChPlan.wtrp:planChTrpPerWk;
                      const delta=Math.round(a.wtrp-plan);
                      return<td key={w.week} style={{padding:'2px 4px',textAlign:'center'}}>
                        <span className="mono" style={{fontSize:8,fontWeight:600,color:delta>0?'var(--green)':delta<0?'var(--red)':'var(--text3)'}}>{delta>0?'+':''}{delta}</span>
                      </td>;
                    })}
                    <td/>
                  </tr>
                  {/* Forecast/redistribution for this channel */}
                  {redist&&redist.chRedist[r.ch.id]&&(
                    <tr>
                      <td className="row-hdr" style={{paddingLeft:14,fontSize:9,color:'var(--green)'}}>Forecast TRP/wk</td>
                      {planWeeks.map(w=>{
                        const wi=w.week-1;
                        const isActual=weekCh(wi,r.ch.id)!=null;
                        const isFuture=redist.futureWks.includes(wi);
                        const val=isActual?Math.round(weekCh(wi,r.ch.id).wtrp):isFuture?Math.round(redist.chRedist[r.ch.id].perWkTrp):null;
                        return<td key={w.week} style={{padding:0}}>
                          <div className="fc-cell" style={{
                            background:isActual?'var(--blue-dim)':isFuture?'var(--green-dim)':'transparent',
                            color:'var(--text)',height:20,fontSize:9,
                            border:isFuture?'1px dashed var(--green)':'none'
                          }}>{val!=null?val:'·'}</div>
                        </td>;
                      })}
                      <td/>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}

            {/* ── TOTALS SECTION ── */}
            <tr>
              <td colSpan={nW+2} style={{padding:'4px 8px',background:'var(--text)',color:'var(--gold)',fontSize:9,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',borderTop:'3px solid var(--gold)'}}>
                TOTAL — ALL CHANNELS
              </td>
            </tr>
            <tr>
              <td className="row-hdr">Planned wTRP</td>
              {planWeeks.map(w=>(
                <td key={w.week} style={{padding:0}}>
                  <div className="fc-cell" style={{background:'var(--gold-light)',color:'var(--text)',height:22,fontSize:9,fontWeight:700}}>{Math.round(w.wtrp)||'—'}</div>
                </td>
              ))}
              <td style={{padding:0}}><div className="fc-cell active-dark" style={{height:22,fontWeight:800}}>{Math.round(selResult.tot.wtrp)}</div></td>
            </tr>
            <tr>
              <td className="row-hdr" style={{color:'var(--blue)'}}>Actual wTRP</td>
              {planWeeks.map(w=>{
                const wi=w.week-1;
                const t=weekTotal(wi);
                const plan=w.wtrp;
                const actual=t?t.wtrp:null;
                const diff=actual!=null?actual-plan:null;
                const isOver=diff!=null&&diff>plan*0.05;
                const isUnder=diff!=null&&diff<-plan*0.05;
                return<td key={w.week} style={{padding:0}}>
                  <div className="fc-cell" style={{
                    background:actual!=null?(isOver?'var(--green-dim)':isUnder?'var(--red-dim)':'var(--blue-dim)'):'transparent',
                    color:actual!=null?'var(--text)':'var(--border)',height:22,fontSize:9,fontWeight:700
                  }}>{actual!=null?Math.round(actual):'·'}</div>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}>
                <span className="mono" style={{fontSize:10,fontWeight:700,color:'var(--blue)'}}>
                  {Object.keys(selActuals).reduce((s,wi)=>{const t=weekTotal(+wi);return s+(t?t.wtrp||0:0);},0)>0
                    ?Math.round(Object.keys(selActuals).reduce((s,wi)=>{const t=weekTotal(+wi);return s+(t?t.wtrp||0:0);},0)):'—'}
                </span>
              </td>
            </tr>
            {redist&&(
              <tr>
                <td className="row-hdr" style={{color:'var(--green)'}}>Forecast (redistributed)</td>
                {planWeeks.map(w=>{
                  const wi=w.week-1;
                  const t=weekTotal(wi);
                  const isFuture=redist.futureWks.includes(wi);
                  const val=t?Math.round(t.wtrp):isFuture?Math.round(redist.redistTrpPerWk):null;
                  return<td key={w.week} style={{padding:0}}>
                    <div className="fc-cell" style={{
                      background:t?'var(--blue-dim)':isFuture?'var(--green-dim)':'transparent',
                      color:'var(--text)',height:22,fontSize:9,border:isFuture?'1px dashed var(--green)':'none'
                    }}>{val!=null?val:'·'}</div>
                  </td>;
                })}
                <td/>
              </tr>
            )}
            <tr>
              <td className="row-hdr">Planned Budget</td>
              {planWeeks.map(w=>(
                <td key={w.week} style={{padding:0}}>
                  <div className="fc-cell" style={{background:'var(--gold-light)',color:'var(--text)',height:22,fontSize:9,fontWeight:700}}>{Math.round(w.bud/1000)+'K'}</div>
                </td>
              ))}
              <td style={{padding:0}}><div className="fc-cell active-dark" style={{height:22,fontWeight:800}}>{Math.round(selResult.tot.bud/1000)+'K'}</div></td>
            </tr>
            <tr>
              <td className="row-hdr" style={{color:'var(--blue)'}}>Actual Budget</td>
              {planWeeks.map(w=>{
                const wi=w.week-1;
                const t=weekTotal(wi);
                const actual=t?t.bud:null;
                const plan=w.bud;
                const diff=actual!=null?actual-plan:null;
                const isOver=diff!=null&&diff>plan*0.05;
                const isUnder=diff!=null&&diff<-plan*0.05;
                return<td key={w.week} style={{padding:0}}>
                  <div className="fc-cell" style={{
                    background:actual!=null?(isOver?'var(--green-dim)':isUnder?'var(--red-dim)':'var(--blue-dim)'):'transparent',
                    color:actual!=null?'var(--text)':'var(--border)',height:22,fontSize:9,fontWeight:700
                  }}>{actual!=null?Math.round(actual/1000)+'K':'·'}</div>
                </td>;
              })}
              <td style={{padding:'3px 6px',textAlign:'center'}}>
                <span className="mono" style={{fontSize:10,fontWeight:700,color:'var(--blue)'}}>
                  {Object.keys(selActuals).reduce((s,wi)=>{const t=weekTotal(+wi);return s+(t?t.bud||0:0);},0)>0
                    ?Math.round(Object.keys(selActuals).reduce((s,wi)=>{const t=weekTotal(+wi);return s+(t?t.bud||0:0);},0)/1000)+'K':'—'}
                </span>
              </td>
            </tr>
            <tr>
              <td className="row-hdr">Δ TRP variance</td>
              {planWeeks.map(w=>{
                const wi=w.week-1;const t=weekTotal(wi);
                if(!t)return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}><span style={{color:'var(--text3)',fontSize:9}}>—</span></td>;
                const delta=Math.round(t.wtrp-w.wtrp);
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,fontWeight:700,color:delta>0?'var(--green)':delta<0?'var(--red)':'var(--text3)'}}>{delta>0?'+':''}{delta}</span>
                </td>;
              })}
              <td/>
            </tr>
            <tr>
              <td className="row-hdr">Δ Budget variance</td>
              {planWeeks.map(w=>{
                const wi=w.week-1;const t=weekTotal(wi);
                if(!t)return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}><span style={{color:'var(--text3)',fontSize:9}}>—</span></td>;
                const delta=Math.round(t.bud-w.bud);
                return<td key={w.week} style={{padding:'3px 6px',textAlign:'center'}}>
                  <span className="mono" style={{fontSize:9,fontWeight:700,color:delta<0?'var(--green)':delta>0?'var(--red)':'var(--text3)'}}>
                    {delta>0?'+':''}{Math.round(delta/1000)+'K'}
                  </span>
                </td>;
              })}
              <td/>
            </tr>
          </tbody>
        </table>
      </div>

      {Object.keys(selActuals).length>0&&(
        <button className="btn btn-ghost btn-sm" style={{marginTop:8,color:'var(--red)'}}
          onClick={()=>{if(window.confirm('Clear actuals for this flight?'))setActuals(p=>{const u={...p};delete u[selFlightId];return u;});}}>
          ↺ Clear actuals for {selFlight.label}
        </button>
      )}
    </div>
  );
}

// ─── INPUTS DRAWER ───────────────────────────────────────────────
function InputsDrawer({open,onClose,audiences,setAudiences,pcData,setPcData,globalMix,setGlobalMix,planDiscRows,setPlanDiscRows,globalChannels,setGlobalChannels,flights,flightResults,mixInputMode,setMixInputMode,chPrices,setChPrices,discScales,setDiscScales,seasonCoeffs,setSeasonCoeffs,audSeasonData,setAudSeasonData,chBuyAudSel,setChBuyAudSel,onPriceListApplied}){
  const[drawerTab,setDrawerTab]=useState('data');
  const[pasteVal,setPasteVal]=useState('');
  const[pasteMsg,setPasteMsg]=useState('');
  const[discOpen,setDiscOpen]=useState(null);
  const[pcOpen,setPcOpen]=useState(null);

  function parsePaste(text){
    const lines=text.trim().split('\n').filter(l=>l.trim());
    let imported=0;
    const newAuds=audiences.map(a=>({...a,data:{...a.data}}));
    lines.forEach(line=>{
      const cols=line.split('\t').map(s=>s.trim().replace(',','.'));
      if(cols.length<3)return;
      const chName=cols[0].toLowerCase();
      const ch=CH.find(c=>c.n.toLowerCase()===chName||c.id.toLowerCase()===chName);
      if(!ch)return;
      // Columns: Channel | SHR% | BA_AMR% | BA_Aff | B1_AMR% | B1_Aff
      const shr=parseFloat(cols[1])/100||ch.defShr;
      const baAmr=parseFloat(cols[2])/100||ch.defAmr;
      const baAff=parseFloat(cols[3])||ch.defAff;
      const b1Amr=cols[4]?parseFloat(cols[4])/100:baAmr*.85;
      const b1Aff=cols[5]?parseFloat(cols[5]):+(b1Amr/baAmr*100).toFixed(2);
      const ba=newAuds.find(a=>a.id==='ba');
      const b1=newAuds.find(a=>a.id==='brand1');
      if(ba)ba.data[ch.id]={amr:baAmr,shr,aff:baAff,edited:true};
      if(b1)b1.data[ch.id]={amr:b1Amr,shr,aff:b1Aff,edited:true};
      imported++;
    });
    if(imported>0){setAudiences(newAuds);setPasteMsg('✓ Imported '+imported+' channels');setPasteVal('');}
    else setPasteMsg('⚠ No channels matched. Expected: Channel | SHR% | BA_AMR% | BA_Aff | B1_AMR% | B1_Aff');
  }

  const activeAud=audiences[0];// BA for display

  // Compute refreshed discount rows for display using flight results
  const dispDiscRows=useMemo(()=>{
    const out={};
    CH.forEach(ch=>{
      if(!flights||!flights.length){out[ch.id]=planDiscRows[ch.id]||mkDiscRows(ch.id);return;}
      let totalChBud=0,activeMonths=new Set(),nF=0;
      flights.forEach(f=>{
        const r=flightResults&&flightResults[f.id];
        if(!r)return;
        const cr=r.chs&&r.chs.find(x=>x.ch.id===ch.id);
        if(cr&&cr.b>0){
          totalChBud+=cr.b;nF++;
          // Mark which calendar months this flight covers
          const sw=f.startWeek||1;const ew=sw+(f.nWeeks||f.weeks||4)-1;
          for(let w=sw;w<=ew;w++){activeMonths.add(Math.ceil(w/4.33));}
        }
      });
      const totalMonths=Math.max(1,activeMonths.size);
      const ptSpl=((flights[0]&&flights[0].ptSplit)||75)/100;
      const baseRows=planDiscRows[ch.id]||mkDiscRows(ch.id);
      out[ch.id]=refreshDisc(baseRows,ch.id,totalChBud,totalMonths,1-ptSpl);
    });
    return out;
  },[flights,flightResults,planDiscRows]);

  return(
    <>
      <div className={'drawer-overlay'+(open?' open':'')} onClick={onClose}/>
      <div className={'drawer'+(open?' open':'')}>
        <div style={{padding:'14px 16px',borderBottom:'2px solid var(--gold-border)',display:'flex',justifyContent:'space-between',alignItems:'center',flexShrink:0,background:'var(--surface)'}}>
          <span style={{fontWeight:700,fontSize:13}}>Inputs & Configuration</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕ Close</button>
        </div>
        <div className="tabs" style={{borderBottom:'1px solid var(--border)',flexShrink:0}}>
          {[['data','📊 Audience Data'],['channels','📺 Channels & Mix'],['seasonal','🌿 Seasonal Ratings'],['rates','💰 Rates & Discounts'],['pricelists','📌 Price Lists']].map(([t,l])=>(
            <button key={t} className={'tab-btn'+(drawerTab===t?' on':'')} onClick={()=>setDrawerTab(t)}>{l}</button>
          ))}
        </div>
        <div style={{flex:1,overflow:'auto',padding:'14px 16px'}}>

          {/* ── AUDIENCE DATA TAB ── */}
          {drawerTab==='data'&&(
            <div>
              <div style={{marginBottom:12}}>
                {/* Audience list */}
                <div style={{padding:'8px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6,marginBottom:12}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                    <div style={{fontWeight:700,fontSize:11,color:'var(--gold-dark)'}}>Audiences</div>
                    <button className="btn btn-xs btn-ghost" onClick={()=>{
                      const id='brand'+audiences.length;
                      setAudiences(p=>[...p,{id,name:'Brand '+p.length,label:'',color:'#C19A6B',locked:false,data:Object.fromEntries(CH.map(c=>[c.id,{amr:c.defAmr,shr:c.defShr,aff:c.defAff,edited:false}]))}]);
                    }}>+ Add audience</button>
                  </div>
                  {audiences.map((a,ai)=>(
                    <div key={a.id} style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,padding:'4px 6px',background:'var(--surface)',borderRadius:4}}>
                      <div style={{width:10,height:10,borderRadius:'50%',background:'var(--gold)',flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:10,fontWeight:600}}>{a.name}</div>
                        {a.locked&&<div style={{fontSize:9,color:'var(--text3)'}}>Channels sell GRP against this audience. SHR &amp; CPP are referenced here.</div>}
                        {!a.locked&&<div style={{fontSize:9,color:'var(--text3)'}}>Brand audience: set per-channel AMR% &amp; affinity. Assign to flights.</div>}
                      </div>
                      {!a.locked&&(
                        <>
                          <input className="inn" value={a.name} style={{width:110,fontSize:10}} onChange={e=>setAudiences(p=>p.map((x,i)=>i===ai?{...x,name:e.target.value}:x))}/>
                          <input className="inn" value={a.label||''} placeholder="label" style={{width:70,fontSize:10}} onChange={e=>setAudiences(p=>p.map((x,i)=>i===ai?{...x,label:e.target.value}:x))}/>
                          <button className="btn btn-xs" style={{color:'var(--red)',border:'1px solid var(--red)',background:'transparent'}} onClick={()=>setAudiences(p=>p.filter((_,i)=>i!==ai))}>✕</button>
                        </>
                      )}
                      {a.locked&&<div className="bdg bdg-gold" style={{fontSize:9}}>BA (base)</div>}
                    </div>
                  ))}
                </div>
                <div className="lbl" style={{marginBottom:4}}>Paste audience data from Excel</div>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:6}}>
                  Expected columns (tab-separated):<br/>
                  <span className="mono" style={{color:'var(--text2)'}}>Channel | SHR% | BA_AMR% | BA_Aff | Brand1_AMR% | Brand1_Aff</span>
                </div>
                <textarea className="paste-area" value={pasteVal} onChange={e=>setPasteVal(e.target.value)} placeholder={'Cinema 1\t5.28\t1.14\t54.5\t0.90\t78.9\nTV 7\t2.34\t0.51\t75.0\t0.40\t78.4\n...'}/>
                <div style={{display:'flex',gap:6,marginTop:6,alignItems:'center'}}>
                  <button className="btn btn-primary btn-sm" onClick={()=>parsePaste(pasteVal)}>Import</button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setPasteVal('');setPasteMsg('');}}>Clear</button>
                  {pasteMsg&&<span style={{fontSize:11,color:pasteMsg.startsWith('✓')?'var(--green)':'var(--amber)'}}>{pasteMsg}</span>}
                </div>
              </div>
              <div style={{borderTop:'1px solid var(--border)',paddingTop:12}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div className="lbl">Per-channel audience data</div>
                  <button className="btn btn-ghost btn-xs" onClick={()=>setAudiences(mkAudData())}>Reset to defaults</button>
                </div>
                <div style={{overflowX:'auto'}}>
                  {(()=>{
                    const ba=audiences.find(a=>a.id==='ba');
                    const brandAuds=audiences.filter(a=>!a.locked);
                    const nCols=3+brandAuds.length*3;
                    return(
                    <table className="data-grid" style={{fontSize:10,borderCollapse:'collapse',width:'100%'}}>
                      <thead>
                        <tr style={{background:'var(--gold-bg)'}}>
                          <th style={{textAlign:'left',padding:'5px 8px',color:'var(--text3)',fontWeight:600,fontSize:9,textTransform:'uppercase',minWidth:90}}>Channel</th>
                          <th style={{textAlign:'right',color:'var(--gold-dark)',padding:'5px 6px',fontSize:9,textTransform:'uppercase'}} title="Channel share % (Buying Audience)">SHR%</th>
                          <th style={{textAlign:'right',color:'var(--gold-dark)',padding:'5px 6px',fontSize:9,textTransform:'uppercase'}} title="Buying Audience AMR% — audience used by channels to sell GRP">BA AMR%</th>

                          {brandAuds.map(a=>(
                            <React.Fragment key={a.id}>
                              <th style={{textAlign:'right',color:'var(--purple)',padding:'5px 6px',fontSize:9,textTransform:'uppercase',borderLeft:'2px solid var(--border)'}} title={a.name+' SHR% (from Nielsen)'}>{a.name.slice(0,10)} SHR%</th>
                              <th style={{textAlign:'right',color:'var(--purple)',padding:'5px 6px',fontSize:9,textTransform:'uppercase'}} title={a.name+' AMR%'}>{a.name.slice(0,10)} AMR%</th>
                              <th style={{textAlign:'right',color:'var(--purple)',padding:'5px 6px',fontSize:9,textTransform:'uppercase'}} title={a.name+' Affinity = Brand AMR / BA AMR × 100'}>{a.name.slice(0,10)} Aff</th>
                            </React.Fragment>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {SH_LIST.map(sh=>(
                          <React.Fragment key={sh}>
                            <tr><td colSpan={nCols} style={{padding:'4px 8px',background:'var(--surface)',fontSize:9,fontWeight:700,color:'var(--text3)',letterSpacing:'.06em',textTransform:'uppercase'}}>{sh}</td></tr>
                            {CH.filter(c=>c.sh===sh).map(ch=>{
                              const baD=ba&&ba.data[ch.id];
                              const baAmr=(baD&&baD.amr)||ch.defAmr;
                              return(
                                <tr key={ch.id}>
                                  <td style={{padding:'3px 8px',fontWeight:500,color:'var(--text)'}}>{ch.n}</td>
                                  <td>
                                    <input type="number" step="0.01" value={baD?(baD.shr*100).toFixed(2):(ch.defShr*100).toFixed(2)} style={{width:56}}
                                      onChange={e=>{const v=+e.target.value/100;setAudiences(a=>a.map(aud=>({...aud,data:{...aud.data,[ch.id]:{...(aud.data[ch.id]||{}),shr:v,edited:true}}})));}}/>   
                                  </td>
                                  <td>
                                    <input type="number" step="0.001" value={baD?(baD.amr*100).toFixed(3):(ch.defAmr*100).toFixed(3)} style={{width:60}}
                                      onChange={e=>{const v=+e.target.value/100;setAudiences(a=>a.map(aud=>{if(aud.id!=='ba')return aud;return{...aud,data:{...aud.data,[ch.id]:{...(aud.data[ch.id]||{}),amr:v,edited:true}}};return aud;}));}}/>
                                  </td>
                                  
                                  {brandAuds.map(bAud=>{
                                    const bD=bAud.data&&bAud.data[ch.id];
                                    const defAmr=ch.defAmr*.85;
                                    const defAff=+(defAmr/baAmr*100).toFixed(1);
                                    return(
                                      <React.Fragment key={bAud.id}>
                                        <td style={{borderLeft:'2px solid var(--border)'}}>
                                          <input type="number" step="0.01" value={bD&&bD.shr!=null?(bD.shr*100).toFixed(2):(ch.defShr*100).toFixed(2)} style={{width:56}} title="Brand audience SHR% (from Nielsen)"
                                            onChange={e=>{const v=+e.target.value/100;setAudiences(a=>a.map(aud=>{if(aud.id!==bAud.id)return aud;return{...aud,data:{...aud.data,[ch.id]:{...(aud.data[ch.id]||{}),shr:v,edited:true}}};return aud;}));}}/>
                                        </td>
                                        <td>
                                          <input type="number" step="0.001" value={bD?(bD.amr*100).toFixed(3):(defAmr*100).toFixed(3)} style={{width:60}}
                                            onChange={e=>{const v=+e.target.value/100;setAudiences(a=>a.map(aud=>{if(aud.id!==bAud.id)return aud;return{...aud,data:{...aud.data,[ch.id]:{...(aud.data[ch.id]||{}),amr:v,aff:+(v/baAmr*100).toFixed(2),edited:true}}};return aud;}));}}/>
                                        </td>
                                        <td style={{padding:'3px 6px'}}>
                                          <input type="number" step="0.1" value={bD?(bD.aff).toFixed(1):defAff.toFixed(1)} style={{width:48}}
                                            onChange={e=>{const v=+e.target.value;setAudiences(a=>a.map(aud=>{if(aud.id!==bAud.id)return aud;return{...aud,data:{...aud.data,[ch.id]:{...(aud.data[ch.id]||{}),aff:v,edited:true}}};return aud;}));}}/>
                                        </td>
                                      </React.Fragment>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {/* ── CHANNELS & MIX TAB ── */}
          {drawerTab==='channels'&&(
            <div>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div>
                  <div className="lbl">Global Channel Mix</div>
                  <div style={{fontSize:11,color:'var(--text3)'}}>Toggle channels on/off, then set weight by raw SHR, Budget %, or TRP %. Per-flight overrides in flight settings.</div>
                </div>
<div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
                  {/* Mix input mode */}
                  {['shr','bud','trp'].map(mode=>(
                    <button key={mode} className={'btn btn-xs'+(mixInputMode===mode?' btn-primary':' btn-ghost')}
                      onClick={()=>setMixInputMode(mode)}>
                      {mode==='shr'?'Raw SHR':mode==='bud'?'Budget %':'TRP %'}
                    </button>
                  ))}
                  <button className="btn btn-ghost btn-xs" onClick={()=>{
                    const o={};CH.forEach(ch=>{o[ch.id]=ch.defShr*100;});setGlobalMix(o);
                  }}>↺ Reset SHR</button>
                  <button className="btn btn-ghost btn-xs" title="Recalculate mix weights from active brand audience SHR data" onClick={()=>{
                    const activeBA=audiences.find(a=>!a.locked)||audiences[0];
                    if(!activeBA||!activeBA.data)return;
                    const o={};CH.forEach(ch=>{
                      const d=activeBA.data[ch.id];
                      o[ch.id]=(d&&d.shr?d.shr:ch.defShr)*100;
                    });
                    setGlobalMix(o);
                  }}>↺ From brand SHR</button>
                  <button className="btn btn-ghost btn-xs" onClick={()=>{
                    // Normalize current weights to sum=100 for Budget% mode
                    const active=CH.filter(ch=>globalChannels[ch.id]!==false);
                    const total=active.reduce((s,ch)=>{const w=globalMix[ch.id]!=null?globalMix[ch.id]:ch.defShr*100;return s+w;},0);
                    if(!total)return;
                    const o={};active.forEach(ch=>{const w=globalMix[ch.id]!=null?globalMix[ch.id]:ch.defShr*100;o[ch.id]=+(w/total*100).toFixed(3);});
                    setGlobalMix(p=>({...p,...o}));
                  }}>⊕ Normalise 100%</button>
                </div>
              </div>
              {SH_LIST.map(sh=>(
                <div key={sh} style={{marginBottom:12}}>
                  <div style={{fontSize:9,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{sh}</div>
                  {CH.filter(c=>c.sh===sh).map(ch=>{
                    const on=globalChannels[ch.id]!==false;
                    const w=globalMix[ch.id]!=null?globalMix[ch.id]:ch.defShr*100;
                    return(
                      <div key={ch.id} style={{display:'flex',alignItems:'center',gap:8,padding:'5px 0',borderBottom:'1px solid var(--border)'}}>
                        <Tog on={on} cb={()=>setGlobalChannels(p=>({...p,[ch.id]:!on}))}/>
                        <span style={{flex:1,fontSize:11,color:on?'var(--text)':'var(--text3)',fontWeight:500}}>{ch.n}</span>
                        <span style={{fontSize:9,color:'var(--text3)',width:60,flexShrink:0}}>SHR {fp2(ch.defShr*100)}</span>
                        <span style={{fontSize:9,color:'var(--text3)',marginLeft:4,flexShrink:0}}>Buy aud:</span>
                        <select className="sel" value={chBuyAudSel[ch.id]||'ba'} style={{fontSize:9,width:110}} onChange={e=>setChBuyAudSel(p=>({...p,[ch.id]:e.target.value}))}>
                          {audiences.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        {/* Live share display */}
                        {(()=>{
                          const activeWts=CH.filter(x=>globalChannels[x.id]!==false).reduce((s,x)=>s+(globalMix[x.id]!=null?globalMix[x.id]:x.defShr*100),0)||1;
                          const sharePct=on?(w/activeWts*100):0;
                          return <span className="mono" style={{fontSize:10,width:44,color:'var(--gold-dark)',textAlign:'right',opacity:on?1:.4}}>{sharePct.toFixed(1)}%</span>;
                        })()}
                        <input className="inn" type="number" min={0} step={mixInputMode==='shr'?0.001:0.1}
                          value={(()=>{
                            if(mixInputMode==='shr')return w.toFixed(3);
                            const activeWts=CH.filter(x=>globalChannels[x.id]!==false).reduce((s,x)=>s+(globalMix[x.id]!=null?globalMix[x.id]:x.defShr*100),0)||1;
                            if(mixInputMode==='bud')return (w/activeWts*100).toFixed(1);
                            // TRP%: weight by aff to get TRP share estimate
                            const aff=(audiences&&audiences[0]&&audiences[0].data&&audiences[0].data[ch.id]?audiences[0].data[ch.id].aff:ch.defAff)/100||1;
                            const trpW=w*aff;
                            const totalTrpW=CH.filter(x=>globalChannels[x.id]!==false).reduce((s,x)=>{const a=(audiences&&audiences[0]&&audiences[0].data&&audiences[0].data[x.id]?audiences[0].data[x.id].aff:x.defAff)/100||1;const xw=globalMix[x.id]!=null?globalMix[x.id]:x.defShr*100;return s+xw*a;},0)||1;
                            return (trpW/totalTrpW*100).toFixed(1);
                          })()}
                          style={{width:68,opacity:on?1:.4}} disabled={!on}
                          onChange={e=>{
                            const v=+e.target.value;
                            if(mixInputMode==='shr'){setGlobalMix(p=>({...p,[ch.id]:v}));return;}
                            const activeWts=CH.filter(x=>globalChannels[x.id]!==false&&x.id!==ch.id).reduce((s,x)=>s+(globalMix[x.id]!=null?globalMix[x.id]:x.defShr*100),0)||0.001;
                            if(mixInputMode==='bud'){
                              // v is new budget% for this channel; scale others to fill remaining
                              const newW=activeWts*(v/(100-v));
                              setGlobalMix(p=>({...p,[ch.id]:+newW.toFixed(4)}));
                            } else {
                              // TRP%: convert via aff
                              const aff=(audiences&&audiences[0]&&audiences[0].data&&audiences[0].data[ch.id]?audiences[0].data[ch.id].aff:ch.defAff)/100||1;
                              const otherTrpW=CH.filter(x=>globalChannels[x.id]!==false&&x.id!==ch.id).reduce((s,x)=>{const a=(audiences&&audiences[0]&&audiences[0].data&&audiences[0].data[x.id]?audiences[0].data[x.id].aff:x.defAff)/100||1;const xw=globalMix[x.id]!=null?globalMix[x.id]:x.defShr*100;return s+xw*a;},0)||0.001;
                              const targetTrpW=otherTrpW*(v/(100-v));
                              const newW=targetTrpW/aff;
                              setGlobalMix(p=>({...p,[ch.id]:+newW.toFixed(4)}));
                            }
                          }}/>
                        <span style={{fontSize:9,color:'var(--text3)',width:28}}>
                          {mixInputMode==='shr'?'wt':mixInputMode==='bud'?'bud%':'trp%'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* ── RATES & DISCOUNTS TAB ── */}
          {drawerTab==='rates'&&(
            <div>
              <div style={{fontSize:11,color:'var(--text3)',marginBottom:8}}>
                Positioning and co-brand coefficients multiply gross CPP. Discount stacks apply on top. AUTO rows recalculate from flight budget.
              </div>
              {/* Apply-to-all toolbar */}
              <div style={{display:'flex',gap:6,alignItems:'center',padding:'8px 12px',background:'var(--gold-bg)',border:'1px solid var(--gold-border)',borderRadius:6,marginBottom:12,flexWrap:'wrap'}}>
                <span style={{fontSize:10,fontWeight:700,color:'var(--gold-dark)',textTransform:'uppercase',letterSpacing:'.07em'}}>Apply to all channels:</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>{
                  // Copy Cinema 1's manual discount rows to all other channels
                  const src=planDiscRows['C1']||mkDiscRows('C1');
                  const newRows={};
                  CH.forEach(ch=>{
                    const existing=planDiscRows[ch.id]||mkDiscRows(ch.id);
                    // Copy manual rows (non-auto) from C1 to this channel, keep auto rows
                    const merged=existing.map(r=>{
                      if(r.auto)return r; // keep channel-specific auto rows
                      const srcR=src.find(s=>s.id===r.id);
                      return srcR?{...r,on:srcR.on,v:srcR.v,label:srcR.label}:r;
                    });
                    newRows[ch.id]=merged;
                  });
                  setPlanDiscRows(newRows);
                }}>⊕ Copy C1 discounts → all</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{
                  // Toggle all manual rows on for all channels
                  const newRows={};
                  CH.forEach(ch=>{
                    const rows=planDiscRows[ch.id]||mkDiscRows(ch.id);
                    newRows[ch.id]=rows.map(r=>({...r,on:true}));
                  });
                  setPlanDiscRows(newRows);
                }}>✓ All ON</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{
                  // Turn off all manual rows for all channels
                  const newRows={};
                  CH.forEach(ch=>{
                    const rows=planDiscRows[ch.id]||mkDiscRows(ch.id);
                    newRows[ch.id]=rows.map(r=>r.auto?r:{...r,on:false});
                  });
                  setPlanDiscRows(newRows);
                }}>○ Manual OFF</button>
                <button className="btn btn-ghost btn-sm" onClick={()=>{
                  // Reset all to defaults
                  const newRows={};
                  CH.forEach(ch=>{newRows[ch.id]=mkDiscRows(ch.id);});
                  setPlanDiscRows(newRows);
                }}>↺ Reset all</button>
                <span style={{fontSize:10,color:'var(--text3)',marginLeft:4}}>
                  {CH.length} channels · manual changes per channel still possible below
                </span>
              </div>
              {SH_LIST.map(sh=>(
                <div key={sh} style={{marginBottom:10}}>
                  <div style={{fontSize:9,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:4}}>{sh}</div>
                  {CH.filter(c=>c.sh===sh).map(ch=>{
                    const ispc=pcOpen===ch.id;
                    const isdisc=discOpen===ch.id;
                    const pd=pcData[ch.id]||defPcData();
                    const rows=planDiscRows[ch.id]||mkDiscRows(ch.id);
                    const dispRows=dispDiscRows[ch.id]||rows;
                    const fin=stackDisc(dispRows);
                    return(
                      <div key={ch.id} className="card2" style={{marginBottom:4,overflow:'hidden'}}>
                        <div style={{padding:'6px 10px',display:'flex',alignItems:'center',gap:8}}>
                          <span style={{fontWeight:600,fontSize:11,flex:1}}>{ch.n}</span>
                          <span style={{fontSize:9,color:'var(--text3)'}}>PT €{ch.pt} OT €{Math.round(ch.pt*ch.otr)}</span>
                          <Bdg v={'disc '+fp(fin*100)} t={fin>.4?'green':fin>.2?'amber':'gray'}/>
                          <button className="btn btn-ghost btn-xs" style={{color:ispc?'var(--blue)':'var(--text3)',borderColor:ispc?'var(--blue)':'var(--border)'}} onClick={()=>setPcOpen(ispc?null:ch.id)}>Pos/CB</button>
                          <button className="btn btn-ghost btn-xs" style={{color:isdisc?'var(--blue)':'var(--text3)',borderColor:isdisc?'var(--blue)':'var(--border)'}} onClick={()=>setDiscOpen(isdisc?null:ch.id)}>Discounts</button>
                        </div>
                        {ispc&&(
                          <div style={{padding:'10px',background:'var(--gold-bg)',display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,borderTop:'1px solid var(--border)'}}>
                            <div>
                              <div className="lbl" style={{marginBottom:4}}>Positioning ×CPP</div>
                              {Object.entries(pd.pos).map(([k,v])=>(
                                <div key={k} style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                                  <span style={{width:64,fontSize:10,color:'var(--text2)'}}>{k}</span>
                                  <input className="inn" type="number" min={0} step={0.01} value={v.toFixed(2)} style={{width:58}} onChange={e=>setPcData(p=>({...p,[ch.id]:{...pd,pos:{...pd.pos,[k]:+e.target.value}}}))}/>
                                  <span style={{fontSize:9,color:v>1?'var(--amber)':'var(--text3)'}}>{v===1?'base':(v>1?'+':'')+((v-1)*100).toFixed(0)+'%'}</span>
                                </div>
                              ))}
                              <button className="btn btn-ghost btn-xs" style={{marginTop:4}} onClick={()=>setPcData(p=>({...p,[ch.id]:defPcData()}))}>Reset</button>
                            </div>
                            <div>
                              <div className="lbl" style={{marginBottom:4}}>Co-brand ×CPP</div>
                              {Object.entries(pd.cb).map(([k,v])=>(
                                <div key={k} style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                                  <span style={{width:64,fontSize:10,color:'var(--text2)'}}>+{k} brand{+k!==1?'s':''}</span>
                                  <input className="inn" type="number" min={0} step={0.01} value={v.toFixed(2)} style={{width:58}} onChange={e=>setPcData(p=>({...p,[ch.id]:{...pd,cb:{...pd.cb,[k]:+e.target.value}}}))}/>
                                  <span style={{fontSize:9,color:v>1?'var(--amber)':'var(--text3)'}}>{v===1?'base':(v>1?'+':'')+((v-1)*100).toFixed(0)+'%'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {isdisc&&(
                          <div style={{padding:'10px',background:'var(--surface)',borderTop:'1px solid var(--border)'}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                              <thead><tr>
                                <th style={{width:24,padding:'3px 6px',color:'var(--text3)',fontSize:9}}>On</th>
                                <th style={{padding:'3px 6px',color:'var(--text3)',fontSize:9,textAlign:'left'}}>Discount</th>
                                <th style={{width:80,padding:'3px 6px',color:'var(--text3)',fontSize:9}}>Value</th>
                                <th style={{width:40,padding:'3px 6px',color:'var(--text3)',fontSize:9}}>Type</th>
                                <th style={{width:100,padding:'3px 6px',color:'var(--text3)',fontSize:9}}>Note</th>
                              </tr></thead>
                              <tbody>{dispRows.map(r=>(
                                <tr key={r.id} style={{opacity:r.on?1:.4}}>
                                  <td style={{padding:'2px 6px'}}><input type="checkbox" checked={!!r.on} disabled={r.auto&&r.id!=='offprime'} onChange={e=>!r.auto&&setPlanDiscRows(s=>({...s,[ch.id]:s[ch.id].map(x=>x.id===r.id?{...x,on:e.target.checked}:x)}))}/></td>
                                  <td style={{padding:'2px 6px',color:'var(--text2)'}}>{r.auto&&r.id!=='offprime'?r.label:<input className="inp" value={r.label} style={{fontSize:10}} onChange={e=>setPlanDiscRows(s=>({...s,[ch.id]:s[ch.id].map(x=>x.id===r.id?{...x,label:e.target.value}:x)}))}/>}</td>
                                  <td style={{padding:'2px 6px',textAlign:'right'}}>{r.auto&&r.id!=='offprime'?<span className="mono" style={{color:'var(--gold-dark)',fontWeight:700}}>{fp(r.v*100)}</span>:<div style={{display:'flex',alignItems:'center',gap:2}}><input className="inn" type="number" min={0} max={100} step={0.1} value={(r.v*100).toFixed(1)} style={{width:50}} onChange={e=>setPlanDiscRows(s=>({...s,[ch.id]:s[ch.id].map(x=>x.id===r.id?{...x,v:Math.min(1,+e.target.value/100)}:x)}))}/><span style={{fontSize:9,color:'var(--text3)'}}>%</span></div>}</td>
                                  <td style={{padding:'2px 6px'}}>{r.auto?<span style={{fontSize:9,background:'var(--gold-bg)',color:'var(--gold-dark)',border:'1px solid var(--gold-border)',borderRadius:3,padding:'1px 4px'}}>AUTO</span>:<span style={{fontSize:9,background:'var(--gold-bg)',color:'var(--gold-accent)',borderRadius:3,padding:'1px 4px'}}>MAN</span>}</td>
                                  <td style={{padding:'2px 6px',fontSize:9,color:'var(--text3)'}}>{r.note}</td>
                                </tr>
                              ))}</tbody>
                            </table>
                            <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8}}>
                              <button className="btn btn-ghost btn-xs" onClick={()=>setPlanDiscRows(s=>({...s,[ch.id]:[...s[ch.id],{id:'c'+Date.now(),label:'Custom',auto:false,on:false,v:0,note:'',custom:true}]}))}>+ Row</button>
                              <span style={{fontSize:11,color:'var(--text3)'}}>Final disc: <span className="mono" style={{color:'var(--gold-dark)',fontWeight:600}}>{fp(fin*100)}</span></span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {/* ── PRICE LISTS TAB ── */}

          {/* ── SEASONAL RATINGS TAB ── */}
          {drawerTab==='seasonal'&&(
            <SeasonalRatingsTab
              audiences={audiences}
              audSeasonData={audSeasonData}
              setAudSeasonData={setAudSeasonData}
            />
          )}
          {drawerTab==='pricelists'&&(
            <div>
              <div style={{fontSize:11,color:'var(--text3)',marginBottom:8}}>
                Edit base CPPs, discount scales, and seasonal coefficients. Changes apply to all flights immediately.
              </div>
              {/* Excel Upload */}
              <PriceListUpload
                setChPrices={setChPrices}
                setDiscScales={setDiscScales}
                setSeasonCoeffs={setSeasonCoeffs}
                onApplied={onPriceListApplied} chPrices={chPrices} discScales={discScales} seasonCoeffs={seasonCoeffs}
              />

              {/* Base CPP editor */}
              <div style={{marginBottom:16}}>
                <div className="lbl" style={{marginBottom:6}}>Base CPP — PT (Prime Time) &amp; OT (Off Time)</div>
                <div style={{overflowX:'auto'}}>
                  <table>
                    <thead><tr>
                      <th>Channel</th><th>Sales House</th>
                      <th>PT CPP (€)</th><th>OT ratio</th><th>OT CPP (€)</th>
                    </tr></thead>
                    <tbody>
                      {CH.map(ch=>(
                        <tr key={ch.id}>
                          <td style={{fontWeight:600}}>{ch.n}</td>
                          <td style={{fontSize:10,color:'var(--text3)'}}>{ch.sh}</td>
                          <td><input className="inn" type="number" min={1} step={1}
                            value={chPrices[ch.id]?chPrices[ch.id].pt:ch.pt}
                            style={{width:70}}
                            onChange={e=>setChPrices(p=>({...p,[ch.id]:{...(p[ch.id]||{pt:ch.pt,otr:ch.otr}),pt:+e.target.value}}))}/></td>
                          <td><input className="inn" type="number" min={0.1} max={1} step={0.01}
                            value={chPrices[ch.id]?chPrices[ch.id].otr:ch.otr}
                            style={{width:60}}
                            onChange={e=>setChPrices(p=>({...p,[ch.id]:{...(p[ch.id]||{pt:ch.pt,otr:ch.otr}),otr:+e.target.value}}))}/></td>
                          <td className="mono" style={{color:'var(--text3)',fontSize:10}}>
                            €{Math.round((chPrices[ch.id]?chPrices[ch.id].pt:ch.pt)*(chPrices[ch.id]?chPrices[ch.id].otr:ch.otr))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-ghost btn-sm" style={{marginTop:6}} onClick={()=>setChPrices({})}>&#8635; Reset to defaults</button>
              </div>

              {/* Monthly avg discount scale (MS) */}
              <div style={{marginBottom:16}}>
                <div className="lbl" style={{marginBottom:4}}>Monthly Avg Budget Discount Scale (MS)</div>
                <div style={{fontSize:10,color:'var(--text3)',marginBottom:8}}>
                  Budget threshold = minimum avg monthly spend to unlock that tier. Tiers stack upward.
                </div>
                <div style={{overflowX:'auto'}}>
                  <table>
                    <thead><tr>
                      <th>Channel</th>
                      {Array.from({length:16},(_,i)=><th key={i} style={{fontSize:8,minWidth:68}}>T{i+1}</th>)}
                    </tr></thead>
                    <tbody>
                      {CH.map(ch=>{
                        const scale=(discScales.MS[ch.id]||(MS[ch.id]||MS.DEF)).map(x=>[...x]);
                        return(
                          <tr key={ch.id}>
                            <td style={{fontWeight:600,minWidth:90,fontSize:10}}>{ch.n}</td>
                            {scale.map(([t,p],ti)=>(
                              <td key={ti} style={{padding:'2px 2px'}}>
                                <div style={{display:'flex',flexDirection:'column',gap:1}}>
                                  <input className="inn" type="number" min={0} step={100} value={t} style={{width:55,fontSize:9,padding:'1px 3px'}}
                                    onChange={e=>{const ns=(discScales.MS[ch.id]||(MS[ch.id]||MS.DEF)).map(x=>[...x]);ns[ti][0]=+e.target.value;setDiscScales(p=>({...p,MS:{...p.MS,[ch.id]:ns}}));}}/>
                                  <input className="inn" type="number" min={0} max={100} step={1} value={Math.round(p*100)} style={{width:55,fontSize:9,padding:'1px 3px'}} placeholder="%"
                                    onChange={e=>{const ns=(discScales.MS[ch.id]||(MS[ch.id]||MS.DEF)).map(x=>[...x]);ns[ti][1]=+e.target.value/100;setDiscScales(p=>({...p,MS:{...p.MS,[ch.id]:ns}}));}}/>
                                </div>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-ghost btn-sm" style={{marginTop:6}} onClick={()=>setDiscScales(p=>({...p,MS:{}}))}>&#8635; Reset MS</button>
              </div>

              {/* Total budget discount scale (TS) */}
              <div style={{marginBottom:16}}>
                <div className="lbl" style={{marginBottom:4}}>Total Budget Discount Scale (TS)</div>
                <div style={{fontSize:10,color:'var(--text3)',marginBottom:8}}>
                  Nova TV: per-channel budget. MSH / Exclusive / Mediadivision: cumulative SH budget (same discount applied to all channels in house).
                </div>
                <div style={{overflowX:'auto'}}>
                  <table>
                    <thead><tr>
                      <th>Channel / SH</th>
                      {Array.from({length:11},(_,i)=><th key={i} style={{fontSize:8,minWidth:68}}>T{i+1}</th>)}
                    </tr></thead>
                    <tbody>
                      {['C1','7TV','TV8','DEF',...SH_GROUP_TS].map(key=>{
                        const scale=(discScales.TS[key]||(TS[key]||TS.DEF)).map(x=>[...x]);
                        const label=key==='DEF'?'Default (others)':key;
                        return(
                          <tr key={key}>
                            <td style={{fontWeight:600,minWidth:120,fontSize:10}}>{label}</td>
                            {scale.map(([t,p],ti)=>(
                              <td key={ti} style={{padding:'2px 2px'}}>
                                <div style={{display:'flex',flexDirection:'column',gap:1}}>
                                  <input className="inn" type="number" min={0} step={1000} value={t} style={{width:60,fontSize:9,padding:'1px 3px'}}
                                    onChange={e=>{const ns=(discScales.TS[key]||(TS[key]||TS.DEF)).map(x=>[...x]);ns[ti][0]=+e.target.value;setDiscScales(p=>({...p,TS:{...p.TS,[key]:ns}}));}}/>
                                  <input className="inn" type="number" min={0} max={100} step={1} value={Math.round(p*100)} style={{width:60,fontSize:9,padding:'1px 3px'}} placeholder="%"
                                    onChange={e=>{const ns=(discScales.TS[key]||(TS[key]||TS.DEF)).map(x=>[...x]);ns[ti][1]=+e.target.value/100;setDiscScales(p=>({...p,TS:{...p.TS,[key]:ns}}));}}/>
                                </div>
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-ghost btn-sm" style={{marginTop:6}} onClick={()=>setDiscScales(p=>({...p,TS:{}}))}>&#8635; Reset TS</button>
              </div>

              {/* Seasonal coefficients */}
              <div style={{marginBottom:16}}>
                <div className="lbl" style={{marginBottom:4}}>Seasonal Coefficients (per channel, monthly)</div>
                <div style={{fontSize:10,color:'var(--text3)',marginBottom:8}}>
                  Multiplied onto gross CPP for each month. 1.00 = no adjustment.
                </div>
                <div style={{overflowX:'auto'}}>
                  <table>
                    <thead><tr>
                      <th>Channel</th>
                      {MONTHS.map(m=><th key={m} style={{fontSize:8,minWidth:40}}>{MN[m]}</th>)}
                    </tr></thead>
                    <tbody>
                      {CH.map(ch=>(
                        <tr key={ch.id}>
                          <td style={{fontWeight:600,fontSize:10}}>{ch.n}</td>
                          {MONTHS.map(m=>(
                            <td key={m} style={{padding:'2px 2px'}}>
                              <input className="inn" type="text" inputMode="decimal"
                                value={(seasonCoeffs[ch.id]&&seasonCoeffs[ch.id][m]!=null)?seasonCoeffs[ch.id][m]:1}
                                style={{width:42,fontSize:9,padding:'1px 3px'}}
                                onChange={e=>setSeasonCoeffs(p=>({...p,[ch.id]:{...(p[ch.id]||{}),[m]:e.target.value}}))}
                                onBlur={e=>{const v=parseFloat(e.target.value);if(!isNaN(v)&&v>0)setSeasonCoeffs(p=>({...p,[ch.id]:{...(p[ch.id]||{}),[m]:Math.round(v*100)/100}}));else setSeasonCoeffs(p=>({...p,[ch.id]:{...(p[ch.id]||{}),[m]:1}}));}}/>
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <button className="btn btn-ghost btn-sm" style={{marginTop:6}} onClick={()=>setSeasonCoeffs({})}>&#8635; Reset seasonal to 1.00</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ─── MAIN APP ────────────────────────────────────────────────────
// ── Quick Setup Wizard Form ───────────────────────────────────────────
function QuickWizardForm({onDone,onCancel}){
  const[totalBudget,setTotalBudget]=React.useState(300000);
  const[numBrands,setNumBrands]=React.useState(2);
  const[flightsPerBrand,setFlightsPerBrand]=React.useState('4');

  function generate(){
    const budget=parseFloat(totalBudget)||300000;
    const brands=Math.max(1,Math.min(10,parseInt(numBrands)||2));
    const fpb=flightsPerBrand;
    const brandBudget=budget/brands;
    const newFlights=[];
    let idx=0;

    for(let b=1;b<=brands;b++){
      const brandName='Brand '+b;
      const brandColor=FLIGHT_COLORS[(b-1)%FLIGHT_COLORS.length];

      if(fpb==='allyear'){
        // One flight spanning W1-W52
        const f=mkFlight(idx++,{
          brand:brandName,label:'All Year',
          color:brandColor,
          startWeek:1,weeks:52,
          budget:brandBudget,
          dm:[{d:30,sh:100}],pos:'Any',cb:0
        });
        newFlights.push(f);
      } else {
        const n=parseInt(fpb)||4;
        const flightBudget=brandBudget/n;
        // Evenly space n flights of 4 weeks across 52 weeks
        const gap=Math.floor((52-n*4)/(n+1));
        for(let fi=0;fi<n;fi++){
          const startWeek=1+gap*(fi+1)+4*fi;
          const f=mkFlight(idx++,{
            brand:brandName,
            label:'Flight '+(fi+1),
            color:brandColor,
            startWeek:Math.max(1,Math.min(49,startWeek)),
            weeks:4,
            budget:flightBudget,
            dm:[{d:30,sh:100}],pos:'Any',cb:0
          });
          newFlights.push(f);
        }
      }
    }
    onDone(newFlights);
  }

  const inp={width:'100%',padding:'6px 10px',borderRadius:6,border:'1px solid var(--border)',background:'var(--surface2)',color:'var(--text)',fontSize:12};
  const lbl={fontSize:11,color:'var(--text2)',marginBottom:4,display:'block',fontWeight:600};

  return(
    <div>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:16}}>
        <div>
          <label style={lbl}>Total Budget (€)</label>
          <input style={inp} type="number" value={totalBudget} onChange={e=>setTotalBudget(e.target.value)} min={0} step={10000}/>
        </div>
        <div>
          <label style={lbl}>Number of Brands</label>
          <input style={inp} type="number" value={numBrands} onChange={e=>setNumBrands(e.target.value)} min={1} max={10}/>
        </div>
      </div>
      <div style={{marginBottom:20}}>
        <label style={lbl}>Flights per Brand</label>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {[['allyear','All year (continuous)'],['1','1 flight'],['2','2 flights'],['3','3 flights'],['4','4 flights']].map(([v,l])=>(
            <button key={v} onClick={()=>setFlightsPerBrand(v)}
              style={{padding:'6px 12px',borderRadius:6,fontSize:11,cursor:'pointer',border:'1px solid '+(flightsPerBrand===v?'var(--gold)':'var(--border)'),background:flightsPerBrand===v?'var(--gold)':'var(--surface2)',color:flightsPerBrand===v?'#fff':'var(--text2)',fontWeight:flightsPerBrand===v?700:400}}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div style={{fontSize:10,color:'var(--text3)',marginBottom:16,padding:'8px 10px',background:'var(--surface2)',borderRadius:6}}>
        ℹ {parseInt(numBrands)||2} brand(s) × {flightsPerBrand==='allyear'?'1 all-year flight':flightsPerBrand+' × 4w flights'} = {flightsPerBrand==='allyear'?parseInt(numBrands)||2:(parseInt(numBrands)||2)*(parseInt(flightsPerBrand)||4)} total flights — €{Math.round((parseFloat(totalBudget)||0)/((parseInt(numBrands)||2)*(flightsPerBrand==='allyear'?1:(parseInt(flightsPerBrand)||4)))/1000)}K per flight
      </div>
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={onCancel} style={{padding:'7px 16px',borderRadius:6,border:'1px solid var(--border)',background:'transparent',color:'var(--text2)',fontSize:12,cursor:'pointer'}}>Cancel</button>
        <button onClick={generate} style={{padding:'7px 20px',borderRadius:6,border:'none',background:'var(--gold)',color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>⚡ Generate</button>
      </div>
    </div>
  );
}
export default function PlanningEngine({ initialData, onSave, planMeta, readOnly }) {
  // ── Deserialize initialData from DB ──────────────────────────────────────
  const _d = initialData || {};
  const[flights,setFlights]=useState(()=>_d.flights||[]);
  const[showWizard,setShowWizard]=useState(false);
  const[selectedId,setSelectedId]=useState(null);
  const[outputTab,setOutputTab]=useState('timeline');
  const[drawerOpen,setDrawerOpen]=useState(false);
  const[planName,setPlanName]=useState(_d.planName||planMeta?.name||'New Plan');
  const[audiences,setAudiences]=useState(()=>_d.audiences||mkAudData());
  const[globalMix,setGlobalMix]=useState(()=>_d.globalMix||(() => {const o={};CH.forEach(c=>{o[c.id]=c.defShr*100;});return o;})());
  const[mixAudId,setMixAudId]=useState(()=>_d.mixAudId||'brand1'); // audience used for mix SHR base
  const[globalChannels,setGlobalChannels]=useState(()=>_d.globalChannels||(() => {const o={};CH.forEach(c=>{o[c.id]=true;});return o;})());
  const[pcData,setPcData]=useState(()=>_d.pcData||initPc());
  const[planDiscRows,setPlanDiscRows]=useState(()=>_d.planDiscRows||(() => {const o={};CH.forEach(c=>{o[c.id]=mkDiscRows(c.id);});return o;})());
  const[activeAudId,setActiveAudId]=useState('brand1');
  const[showOpt,setShowOpt]=useState(false);
  const[mixInputMode,setMixInputMode]=useState('shr'); // 'shr'|'bud'|'trp'
  const[chPrices,setChPrices]=useState(()=>_d.chPrices||{});
  const[chBuyAudSel,setChBuyAudSel]=useState(()=>_d.chBuyAudSel||{});
  const[discScales,setDiscScales]=useState(()=>_d.discScales||{MS:{},TS:{}});
  const[seasonCoeffs,setSeasonCoeffs]=useState(()=>_d.seasonCoeffs||{});
  const[audSeasonData,setAudSeasonData]=useState(()=>_d.audSeasonData||{});
  const[actuals,setActuals]=useState(()=>_d.actuals||{});// {flightId: {weekIdx: {bud,wtrp,wgrp}}}
  const[baseline,setBaseline]=useState(()=>_d.baseline||null);// snapshot of flights+results when launched
  const[optResult,setOptResult]=useState(null);

  // Load system default prices for new plans (no saved prices)
  useEffect(()=>{
    if(_d.chPrices&&Object.keys(_d.chPrices).length>0)return;
    const tok=localStorage.getItem('plan_token');
    fetch('/api/tv/plans/system/prices',{headers:tok?{'Authorization':'Bearer '+tok}:{}})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!d)return;
        if(d.chPrices&&Object.keys(d.chPrices).length>0)setChPrices(d.chPrices);
        if(d.discScales)setDiscScales(d.discScales);
        if(d.seasonCoeffs&&Object.keys(d.seasonCoeffs).length>0)setSeasonCoeffs(d.seasonCoeffs);
      }).catch(()=>{});
  },[]);

  // Load system default prices for new plans (no saved prices)
  useEffect(()=>{
    if(_d.chPrices&&Object.keys(_d.chPrices).length>0)return; // already has saved prices
    const tok=localStorage.getItem('plan_token');
    fetch('/api/tv/plans/system/prices',{headers:tok?{'Authorization':'Bearer '+tok}:{}})
      .then(r=>r.ok?r.json():null)
      .then(d=>{
        if(!d)return;
        if(d.chPrices&&Object.keys(d.chPrices).length>0)setChPrices(d.chPrices);
        if(d.discScales)setDiscScales(d.discScales);
        if(d.seasonCoeffs&&Object.keys(d.seasonCoeffs).length>0)setSeasonCoeffs(d.seasonCoeffs);
      }).catch(()=>{});
  },[]);
  const[wiMode,setWiMode]=useState(false);
  const[tlMode,setTlMode]=useState('annual');
  const[wiFlights,setWiFlights]=useState(null);

  useEffect(()=>{window.__audiences=audiences;},[audiences]);
  const activeAud=useMemo(()=>audiences.find(a=>a.id===activeAudId)||audiences[0],[audiences,activeAudId]);
  const audData=activeAud?activeAud.data:{};

  // Compute effective globalMix (only on channels)
  const effMix=useMemo(()=>{
    const o={};CH.forEach(c=>{if(globalChannels[c.id]!==false)o[c.id]=globalMix[c.id]!=null?globalMix[c.id]:c.defShr*100;});
    return o;
  },[globalMix,globalChannels]);

  // Compute per-flight results
  const flightResults=useMemo(()=>{
    const res={};
    const extras={chPrices,discScales,seasonCoeffs,audSeasonData,chBuyAudSel,audiences};
    flights.forEach(f=>{
      const fAud=audiences.find(a=>a.id===(f.audId||'ba'))||audiences[0];
      const fAudData=fAud?fAud.data:audData;
      res[f.id]=calcFlight(f,effMix,fAudData,pcData,planDiscRows,globalChannels,extras);
    });
    return res;
  },[flights,effMix,audiences,pcData,planDiscRows,globalChannels,chPrices,discScales,seasonCoeffs,audSeasonData,chBuyAudSel]);

  // What If results
  const wiResults=useMemo(()=>{
    if(!wiFlights)return null;
    const res={};
    const extras2={chPrices,discScales,seasonCoeffs,audSeasonData,chBuyAudSel,audiences};
    wiFlights.forEach(f=>{
      const fAud=audiences.find(a=>a.id===(f.audId||'ba'))||audiences[0];
      const fAudData=fAud?fAud.data:audData;
      res[f.id]=calcFlight(f,effMix,fAudData,pcData,planDiscRows,globalChannels,extras2);
    });
    return res;
  },[wiFlights,effMix,audData,pcData,planDiscRows]);

  // Plan totals
  const planTot=useMemo(()=>{
    const t=Object.values(flightResults).filter(Boolean).reduce((a,r)=>({
      bud:a.bud+r.tot.bud,wtrp:a.wtrp+r.tot.wtrp,wgrp:a.wgrp+r.tot.wgrp,trp:a.trp+(r.tot.trp||0),grp:a.grp+(r.tot.grp||0),
    }),{bud:0,wtrp:0,wgrp:0,trp:0,grp:0});
    const[r1,r3]=rch(t.wtrp);
    const aff=t.wgrp>0?t.wtrp/t.wgrp*100:0;
    return{...t,r1,r3,aff};
  },[flightResults]);
  const planCpp=planTot.wtrp>0?planTot.bud/planTot.wtrp:0;
  const planCppGrp=planTot.wgrp>0?planTot.bud/planTot.wgrp:0;

  const selectedFlight=selectedId?flights.find(f=>f.id===selectedId):flights[0];
  const selResult=selectedFlight?flightResults[selectedFlight.id]:null;

  // Auto-save listener (used by price list apply)
  useEffect(()=>{
    const _autoSave=()=>{
      if(onSave&&!readOnly){
        // Use a small timeout to let React flush all state updates first
        setTimeout(()=>{
          const btn=document.querySelector('[data-autosave]');
          if(btn)btn.click();
        },300);
      }
    };
    window.addEventListener('tvplan:autoSave',_autoSave);
    return()=>window.removeEventListener('tvplan:autoSave',_autoSave);
  },[onSave,readOnly]);

  // Auto-save listener (used by price list apply)
  useEffect(()=>{
    const _autoSave=()=>{
      if(onSave&&!readOnly){
        // Use a small timeout to let React flush all state updates first
        setTimeout(()=>{
          const btn=document.querySelector('[data-autosave]');
          if(btn)btn.click();
        },300);
      }
    };
    window.addEventListener('tvplan:autoSave',_autoSave);
    return()=>window.removeEventListener('tvplan:autoSave',_autoSave);
  },[onSave,readOnly]);

  function addFlight(){
    const f=mkFlight(flights.length);
    setFlights(p=>[...p,f]);
    setSelectedId(f.id);
  }
  function cloneFlight(id){
    const src=flights.find(f=>f.id===id);
    if(!src)return;
    const f={...JSON.parse(JSON.stringify(src)),id:'f'+Date.now(),label:src.label+' (copy)',color:FLIGHT_COLORS[flights.length%FLIGHT_COLORS.length]};
    setFlights(p=>[...p,f]);
    setSelectedId(f.id);
  }
  function updateFlight(id,upd){setFlights(p=>p.map(f=>f.id===id?{...f,...upd}:f));}
  function removeFlight(id){setFlights(p=>p.filter(f=>f.id!==id));if(selectedId===id)setSelectedId(null);}

  function runOptimise(){
    if(!selectedFlight)return;
    const r=optimiseMix(selectedFlight,effMix,audData,pcData,planDiscRows,globalChannels,{chPrices,discScales,seasonCoeffs});
    setOptResult(r);setShowOpt(true);
  }
  function applyOpt(){
    if(!optResult||!selectedFlight)return;
    // Only apply weights for channels that are currently enabled
    const filtered={};
    Object.entries(optResult.suggested).forEach(([id,w])=>{
      if(globalChannels[id]!==false)filtered[id]=w;
    });
    setGlobalMix(p=>({...p,...filtered}));
    setShowOpt(false);setOptResult(null);
  }

  function startWhatIf(){
    setWiFlights(JSON.parse(JSON.stringify(flights)));
    setWiMode(true);
    setOutputTab('whatif');
  }
  function updateWiFlight(id,upd){setWiFlights(p=>p.map(f=>f.id===id?{...f,...upd}:f));}

  // Monthly budget breakdown
  const monthlyData=useMemo(()=>{
    const data={};MONTHS.forEach(m=>{data[m]={bud:0,wtrp:0,flights:[]};});
    flights.forEach(f=>{
      const r=flightResults[f.id];if(!r)return;
      // Simple: distribute weeks over calendar months
      const weekPerMonth=f.weeks/12;// rough
      const startM=Math.min(11,Math.floor((f.startWeek-1)/4.33));
      const endM=Math.min(11,Math.floor((f.startWeek+f.weeks-2)/4.33));
      for(let m=startM;m<=endM;m++){
        const mn=MONTHS[m];
        const overlap=Math.min(4.33,f.startWeek+f.weeks-1-m*4.33)-Math.max(0,f.startWeek-1-m*4.33);
        const frac=Math.max(0,overlap/f.weeks);
        data[mn].bud+=r.tot.bud*frac;
        data[mn].wtrp+=r.tot.wtrp*frac;
        data[mn].flights.push({f,frac});
      }
    });
    return data;
  },[flights,flightResults]);

  const selF=selectedFlight;

  return(<>
    <div className="app">
      {/* HEADER */}
      <div className="header">
        <div style={{fontWeight:700,fontSize:13,color:'var(--text)',display:'flex',alignItems:'center',gap:8}}>
          <span style={{background:'var(--gold)',color:'#fff',width:24,height:24,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:800,flexShrink:0}}>TV</span>
          <input value={planName} onChange={e=>setPlanName(e.target.value)} style={{background:'transparent',border:'none',color:'var(--text)',fontFamily:'DM Sans,sans-serif',fontWeight:700,fontSize:13,outline:'none',background:'transparent',minWidth:160}}/>
        </div>
        <div style={{width:1,height:20,background:'#3A3A3A'}}/>
        {/* Brand Audience pills */}
        <div style={{display:'flex',gap:4,alignItems:'center'}}>
          <span style={{fontSize:10,color:'var(--text3)'}}>Brand Audience:</span>
          {audiences.filter(a=>!a.locked).map(a=>(
            <button key={a.id} onClick={()=>setActiveAudId(a.id)} style={{padding:'3px 9px',borderRadius:20,fontSize:11,fontWeight:500,cursor:'pointer',border:'1px solid '+(activeAudId===a.id?'var(--gold)':'var(--border)'),background:activeAudId===a.id?'var(--gold-bg)':'transparent',color:activeAudId===a.id?'var(--gold-dark)':'var(--text3)',display:'flex',alignItems:'center',gap:4}}>
              <span style={{width:6,height:6,borderRadius:'50%',background:a.color||'var(--gold)',flexShrink:0}}/>
              {a.name}
            </button>
          ))}
        </div>
        <div style={{flex:1}}/>
        <button className="btn btn-sm" onClick={()=>{if(wiMode){setOutputTab('whatif');}else{startWhatIf();}}} style={{color:'var(--gold-dark)',border:'1px solid var(--gold-border)',background:'transparent',cursor:'pointer'}}>What If</button>
        <button className="btn btn-sm" onClick={()=>setDrawerOpen(true)} style={{background:'var(--gold)',color:'#fff',border:'1px solid var(--gold-dark)'}}>⚙ Inputs</button>
        {onSave&&!readOnly&&(
          <button data-autosave="1" className="btn btn-sm" onClick={()=>onSave({
            planName,flights,audiences,globalMix,globalChannels,
            pcData,planDiscRows,chPrices,discScales,seasonCoeffs,
            audSeasonData,actuals,baseline
          })} style={{background:'#3FB950',color:'#fff',border:'none',marginLeft:6}}>
            💾 Save
          </button>
        )}
        {readOnly&&<span style={{fontSize:11,color:'var(--amber)',marginLeft:8}}>👁 View only</span>}
      </div>

      {/* KPI STRIP */}
      <div className="kpi-strip">
        {[
          {l:'Total Budget',v:fe(planTot.bud),c:'var(--gold-accent)'},
          {l:'Total TRP',v:f0(planTot.trp||planTot.wtrp),c:'var(--gold-accent)'},
          {l:'Total GRP',v:f0(planTot.grp||planTot.wgrp),c:'var(--gold-light)'},
          {l:'Total 30\"TRP',v:f0(planTot.wtrp),c:'var(--gold-accent)'},
          {l:'Total 30\"GRP',v:f0(planTot.wgrp),c:'var(--gold-light)'},
          {l:'CPP/30\"TRP',v:fe2(planCpp),c:'var(--gold-accent)'},
          {l:'CPP/30\"GRP',v:fe2(planCppGrp),c:'var(--gold-light)'},
          {l:'Avg AFF',v:fp1(planTot.aff),c:'var(--text2)'},
          {l:'Avg RCH 1+',v:fp1(planTot.r1),c:'var(--gold-accent)'},
          {l:'Avg RCH 3+',v:fp1(planTot.r3),c:'var(--teal)'},
        ].map((k,i)=>(
          <div key={k.l} className="kpi-item" style={{borderRight:'1px solid var(--border)'}}>
            <div className="kpi-label">{k.l}</div>
            <div className="kpi-val" style={{color:k.c||'var(--gold-accent)'}}>{k.v}</div>
          </div>
        ))}
        
      </div>

      {/* BODY */}
      <div className="body">
        {/* LEFT PANEL */}
        <div className="left-panel">
          <div style={{padding:'10px 10px 6px',borderBottom:'2px solid var(--gold-border)',background:'var(--gold-bg)',flexShrink:0}}>
            <div style={{display:'flex',flexDirection:"column",gap:4,marginBottom:6}}>
              <span style={{fontSize:10,fontWeight:700,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.07em',whiteSpace:'nowrap'}}>Brands &amp; Flights ({flights.length})</span>
              <div style={{display:'flex',gap:4}}>
                <button className="btn btn-ghost btn-xs" title="Bulk create" onClick={()=>{
                  const qs=[[1,'Q1 Campaign',10,4],[2,'Q2 Campaign',23,3],[3,'Q3 Campaign',36,4],[4,'Q4 Campaign',48,4]];
                  const newF=qs.map((q,i)=>mkFlight(flights.length+i,{label:q[1],startWeek:q[2],weeks:q[3],budget:50000,color:FLIGHT_COLORS[(flights.length+i)%FLIGHT_COLORS.length]}));
                  setFlights(p=>[...p,...newF]);
                }}>+ Quarterly</button>
                <button className="btn btn-ghost btn-xs" onClick={()=>{
                  const brandNum=(new Set(flights.map(f=>f.brand||'Brand 1'))).size+1;
                  const f=mkFlight(flights.length,{brand:'Brand '+brandNum,label:'Flight 1'});
                  setFlights(p=>[...p,f]);setSelectedId(f.id);
                }}>+ Brand</button>
                <button className="btn btn-primary btn-xs" onClick={addFlight}>+ Flight</button><button className="btn btn-ghost btn-xs" style={{background:"var(--gold)",color:"#fff",border:"none"}} onClick={()=>setShowWizard(true)}>⚡ Quick</button>
              </div>
            </div>
          </div>
          <div style={{flex:1,overflowY:'auto',padding:'4px 6px',paddingBottom:80}}>
            {flights.length===0&&<div style={{padding:'20px 12px',textAlign:'center',color:'var(--text3)',fontSize:11}}>No flights yet.<br/>Click + Flight to begin.</div>}
            {(()=>{
              // Group flights by brand
              const brandOrder=[];const brandMap={};
              flights.forEach((f,fi)=>{
                const b=f.brand||'Brand 1';
                if(!brandMap[b]){brandMap[b]=[];brandOrder.push(b);}
                brandMap[b].push({f,fi});
              });
              return brandOrder.map(brand=>(
                <div key={brand} style={{marginBottom:6}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'4px 8px',background:'var(--surface)',borderRadius:4,border:'1px solid var(--gold-border)',marginBottom:3}}>
                    <span style={{fontSize:10,fontWeight:700,color:'var(--gold-dark)',letterSpacing:'.04em'}}>{brand}</span>
                    <button className="btn btn-xs btn-primary" style={{padding:'1px 6px',fontSize:9}} onClick={()=>{
                      const f=mkFlight(flights.length,{brand});
                      setFlights(p=>[...p,f]);setSelectedId(f.id);
                    }}>+ Flight</button>
                  </div>
                  {brandMap[brand].map(({f,fi})=>(
                    <FlightItem key={f.id} flight={f} result={flightResults[f.id]}
                      isSelected={selectedId===f.id||(selectedId===null&&fi===0)}
                      onSelect={id=>setSelectedId(id)}
                      onUpdate={upd=>updateFlight(f.id,upd)}
                      onClone={()=>cloneFlight(f.id)}
                      onRemove={()=>removeFlight(f.id)}
                      canRemove={flights.length>1}
                    />
                  ))}
                </div>
              ));
            })()}
          </div>
        </div>

        {/* MAIN OUTPUT */}
        <div className="main">
          {/* Output tabs */}
          <div style={{background:'var(--gold-bg)',borderBottom:'1px solid var(--gold-border)',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 12px',height:38,flexShrink:0}}>
            <div className="tabs" style={{padding:0}}>
              {[['timeline','Timeline'],['breakdown','Brand/Flight detail'],['summary','Summary'],['monthly','Monthly'],['actuals','📌 Actuals'],['whatif','What If']].map(([t,l])=>(
                <button key={t} className={'tab-btn'+(outputTab===t?' on':'')} onClick={()=>setOutputTab(t)}>{l}</button>
              ))}
            </div>
            {outputTab!=='whatif'&&selF&&(
              <div style={{display:'flex',gap:6,alignItems:'center'}}>
                <span style={{fontSize:10,color:'var(--text3)'}}>Viewing:</span>
                <span style={{display:'flex',alignItems:'center',gap:4,fontSize:11,fontWeight:600,color:selF.color}}>
                  <span style={{width:8,height:8,borderRadius:2,background:selF.color}}/>
                  {selF.label}
                </span>
                <button className="btn btn-ghost btn-xs" style={{color:'var(--gold-dark)',borderColor:'#A0C8B4'}} onClick={runOptimise}>✦ Optimise mix</button>
              </div>
            )}
          </div>

          <div className="output-area">

            {/* ── OPTIMISE SUGGESTION ── */}
            {showOpt&&optResult&&(
              <div className="card" style={{padding:'12px 14px',marginBottom:12,border:'1px solid var(--green-dim)'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontWeight:700,fontSize:12,color:'var(--gold-dark)'}}>✦ Suggested Mix Optimisation — {selF&&selF.label}</span>
                  <div style={{display:'flex',gap:6}}>
                    <button className='btn btn-ghost btn-sm' style={{borderColor:'var(--gold-border)'}} onClick={applyOpt}>Apply suggestion</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setShowOpt(false);setOptResult(null);}}>Dismiss</button>
                  </div>
                </div>
                <div style={{fontSize:11,color:'var(--text3)',marginBottom:8}}>Channels ranked by CPP/30"TRP (cheapest first). Suggested mix shifts weight toward most efficient channels while preserving your active channel selection.</div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))',gap:4}}>
                  {optResult.cpps.map((item,i)=>{
                    const ch=CH.find(c=>c.id===item.id);
                    const curW=effMix[item.id]||0;
                    const sugW=optResult.suggested[item.id]||0;
                    const diff=sugW-curW;
                    return(
                      <div key={item.id} className="opt-row">
                        <span style={{fontSize:9,fontWeight:700,color:'var(--gold-dark)',width:14}}>#{i+1}</span>
                        <span style={{flex:1,fontSize:10,fontWeight:500}}>{ch&&ch.n}</span>
                        <span className="mono" style={{fontSize:9,color:'var(--text3)'}}>{fe2(item.cpp)}</span>
                        <span className="mono" style={{fontSize:9,color:diff>0?'var(--green)':diff<0?'var(--red)':'var(--text3)'}}>{diff>0?'+':''}{diff.toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── TIMELINE VIEW ── */}
            {outputTab==='timeline'&&(
              <div>
                {/* View mode toggle: annual vs flight */}
                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:10,flexWrap:'wrap'}}>
                  <div className="mode-sw">
                    <button className={'mode-btn'+(tlMode==='annual'?' on':'')} onClick={()=>setTlMode('annual')}>Annual view (52w)</button>
                    <button className={'mode-btn'+(tlMode==='flight'?' on':'')} onClick={()=>setTlMode('flight')}>Flight detail</button>
                  </div>
                  {tlMode==='flight'&&selF&&(
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                      <span style={{fontWeight:600,fontSize:12,color:'var(--gold-dark)'}}>{selF.label}</span>
                      <Bdg v={'W'+selF.startWeek+'–W'+(selF.startWeek+selF.weeks-1)} t="gold"/>
                      <Bdg v={selF.weeks+'w'} t="gray"/>
                      <Bdg v={selF.mode==='trp'?'TRP-driven':'Budget-driven'} t='gold'/>
                      <Bdg v={selF.pos+(selF.cb>0?' +'+selF.cb+'CB':'')} t="amber"/>
                    </div>
                  )}
                  {tlMode==='annual'&&<span style={{fontSize:10,color:'var(--text3)'}}>All flights · click a week column to select flight</span>}
                </div>

                {/* KPI strip for selected flight */}
                {selF&&selResult&&tlMode==='flight'&&(
                  <div style={{display:'flex',gap:5,flexWrap:'wrap',marginBottom:10}}>
                    {[
                      {l:'Budget',v:fe(selResult.tot.bud),c:'var(--gold-accent)'},
                      {l:'30″ TRP',v:f0(selResult.tot.wtrp),c:'var(--purple)'},
                      {l:'30″ GRP',v:f0(selResult.tot.wgrp),c:'var(--teal)'},
                      {l:'raw TRP',v:f0(selResult.tot.trp),c:'var(--text2)'},
                      {l:'CPP/30″TRP',v:fe2(selResult.tot.cpp30T),c:'var(--amber)'},
                      {l:'CPP/30″GRP',v:fe2(selResult.tot.cpp30G),c:'var(--amber)'},
                      {l:'Avg AFF',v:selResult.tot.wtrp>0?fp1(selResult.tot.wgrp/selResult.tot.wtrp*100):'—',c:'var(--text2)'},
                      {l:'Reach 1+',v:fp1(selResult.tot.r1),c:'var(--gold-accent)'},
                      {l:'Reach 3+',v:fp1(selResult.tot.r3),c:'var(--teal)'},
                    ].map(k=>(
                      <div key={k.l} style={{padding:'5px 10px',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:6,minWidth:90,boxShadow:'var(--shadow)'}}>
                        <div style={{fontSize:9,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.06em',marginBottom:2}}>{k.l}</div>
                        <div className="mono" style={{fontSize:13,fontWeight:700,color:k.c}}>{k.v}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* FLOWCHART */}
                <div className="card" style={{padding:'12px',overflow:'hidden'}}>
                  {tlMode==='annual'
                    ? <FlightFlowchart result={selResult} flight={selF} flights={flights} allResults={flightResults} viewMode="annual"/>
                    : selF&&selResult
                      ? <FlightFlowchart result={selResult} flight={selF} flights={flights} allResults={flightResults} viewMode="flight"/>
                      : <div style={{color:'var(--text3)',padding:20,textAlign:'center'}}>{selF?'Calculating...':'Select a flight from the left panel'}</div>
                  }
                </div>
              </div>
            )}

            {/* ── BRAND / FLIGHT DETAIL VIEW ── */}
            {outputTab==='breakdown'&&(()=>{
              const bdOrder=[];const bdMap={};
              flights.forEach(f=>{
                const b=f.brand||'Brand 1';
                if(!bdMap[b]){bdMap[b]={bud:0,wtrp:0,wgrp:0,flights:[]};bdOrder.push(b);}
                const r=flightResults[f.id];
                if(r){bdMap[b].bud+=r.tot.bud;bdMap[b].wtrp+=r.tot.wtrp;bdMap[b].wgrp+=r.tot.wgrp;}
                bdMap[b].flights.push(f);
              });
              let gdBud=0,gdTrp=0;
              Object.values(bdMap).forEach(b=>{gdBud+=b.bud;gdTrp+=b.wtrp;});
              return(
                <div>
                  {bdOrder.map(brand=>{
                    const bd=bdMap[brand];
                    const bCppTrp=bd.wtrp>0?bd.bud/bd.wtrp:0;
                    const bCppGrp=bd.wgrp>0?bd.bud/bd.wgrp:0;
                    const bAff=bd.wgrp>0?bd.wtrp/bd.wgrp*100:0;
                    const[bR1,bR3]=rch(bd.wtrp);
                    return(
                      <div key={brand} style={{marginBottom:16}}>
                        <div style={{background:'var(--text)',borderRadius:'8px 8px 0 0',padding:'8px 14px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <span style={{fontWeight:800,fontSize:13,color:'var(--gold-accent)'}}>{brand}</span>
                          <span style={{fontSize:10,color:'var(--text3)'}}>{bd.flights.length} flight{bd.flights.length!==1?'s':''}</span>
                        </div>
                        <div style={{display:'flex',borderBottom:'2px solid var(--gold-border)',background:'var(--gold-bg)'}}>
                          {[
                            {l:'Budget',v:fe(bd.bud),c:'var(--gold-dark)'},
                            {l:'Bud%',v:gdBud>0?(bd.bud/gdBud*100).toFixed(1)+'%':'—',c:'var(--text2)'},
                            {l:'wTRP',v:f0(bd.wtrp),c:'var(--text)'},
                            {l:'CPP/TRP',v:fe2(bCppTrp),c:'var(--gold)'},
                            {l:'wGRP',v:f0(bd.wgrp),c:'var(--text2)'},
                            {l:'CPP/GRP',v:fe2(bCppGrp),c:'var(--gold)'},
                            {l:'Avg AFF',v:fp1(bAff),c:'var(--text2)'},
                            {l:'Reach 1+',v:fp1(bR1),c:'var(--gold-accent)'},
                            {l:'Reach 3+',v:fp1(bR3),c:'var(--teal)'},
                          ].map(k=>(
                            <div key={k.l} style={{flex:1,padding:'6px 8px',borderRight:'1px solid var(--border)',textAlign:'center'}}>
                              <div style={{fontSize:8,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>{k.l}</div>
                              <div className="mono" style={{fontSize:12,fontWeight:700,color:k.c}}>{k.v}</div>
                            </div>
                          ))}
                        </div>
                        {bd.flights.map(f=>{
                          const r=flightResults[f.id];if(!r)return null;
                          return(
                            <div key={f.id} style={{marginBottom:2,overflow:'hidden',borderTop:'3px solid '+f.color,background:'var(--surface)'}}>
                              <div style={{padding:'8px 14px',borderBottom:'1px solid var(--border)',display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                                <div style={{width:8,height:8,borderRadius:2,background:f.color,flexShrink:0}}/>
                                <span style={{fontWeight:700,fontSize:12,color:f.color}}>{f.label}</span>
                                <Bdg v={'W'+f.startWeek+'–W'+(f.startWeek+f.weeks-1)} t="gray"/>
                                <Bdg v={f.mode==='trp'?'TRP-driven':'Budget-driven'} t='gold'/>
                                <div style={{marginLeft:'auto',display:'flex',gap:10}}>
                                  {[
                                    {l:'Budget',v:fe(r.tot.bud),c:'var(--gold-dark)'},
                                    {l:'wTRP',v:f0(r.tot.wtrp),c:'var(--purple)'},
                                    {l:'CPP/TRP',v:fe2(r.tot.cpp30T),c:'var(--amber)'},
                                    {l:'Reach 1+',v:fp1(r.tot.r1),c:'var(--gold-accent)'},
                                    {l:'Reach 3+',v:fp1(r.tot.r3),c:'var(--teal)'},
                                  ].map(k=>(
                                    <div key={k.l} style={{textAlign:'right'}}>
                                      <div style={{fontSize:9,color:'var(--text3)',textTransform:'uppercase'}}>{k.l}</div>
                                      <div className="mono" style={{fontSize:12,fontWeight:600,color:k.c}}>{k.v}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <table style={{width:'100%'}}>
                                <thead><tr>
                                  <th>Channel</th><th>Budget</th>
                                  <th style={{color:'var(--gold-accent)'}}>wTRP</th>
                                  <th style={{color:'var(--gold-light)'}}>wGRP</th>
                                  <th style={{color:'var(--gold-accent)'}}>CPP/TRP</th>
                                  <th style={{color:'var(--gold)'}}>Affinity</th>
                                  <th>Disc</th><th style={{color:'var(--text3)'}}>Pos</th>
                                </tr></thead>
                                <tbody>
                                  {r.chs.map((cr,ci)=>(
                                    <tr key={cr.ch.id}>
                                      <td><div style={{fontWeight:500,fontSize:11}}>{cr.ch.n}</div><div style={{fontSize:9,color:'var(--text3)'}}>{cr.ch.sh}</div></td>
                                      <td className="mono" style={{color:'var(--gold-dark)'}}>{fe(cr.b)}</td>
                                      <td className="mono" style={{color:'var(--gold-accent)',fontWeight:600}}>{f0(cr.wtrp)}</td>
                                      <td className="mono" style={{color:'var(--gold-light)'}}>{f0(cr.wgrp)}</td>
                                      <td className="mono" style={{color:'var(--gold-accent)'}}>{fe2(cr.cpp30T)}</td>
                                      <td><span className="bdg bdg-gold">{f0(cr.aff*100)}</span></td>
                                      <td><Bdg v={fp(cr.disc*100)} t='gold'/></td>
                                      <td style={{fontSize:10,color:'var(--text3)'}}>{f.posOverrides[cr.ch.id]||f.pos}</td>
                                    </tr>
                                  ))}
                                  <tr className="tr-total">
                                    <td style={{fontWeight:700}}>TOTAL</td>
                                    <td className="mono" style={{color:'var(--gold-dark)',fontWeight:700}}>{fe(r.tot.bud)}</td>
                                    <td className="mono" style={{color:'var(--gold-accent)',fontWeight:700}}>{f0(r.tot.wtrp)}</td>
                                    <td className="mono" style={{color:'var(--gold-light)'}}>{f0(r.tot.wgrp)}</td>
                                    <td className="mono" style={{color:'var(--gold-accent)'}}>{fe2(r.tot.cpp30T)}</td>
                                    <td colSpan={3}/>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── SUMMARY VIEW ── */}
            {outputTab==='summary'&&(()=>{
              // Per-brand aggregates
              const bOrder=[];const bMap={};
              flights.forEach(f=>{
                const b=f.brand||'Brand 1';
                if(!bMap[b]){bMap[b]={bud:0,wtrp:0,wgrp:0,flights:[]};bOrder.push(b);}
                const r=flightResults[f.id];
                if(r){bMap[b].bud+=r.tot.bud;bMap[b].wtrp+=r.tot.wtrp;bMap[b].wgrp+=r.tot.wgrp;}
                bMap[b].flights.push(f);
              });
              // Grand totals + per-channel
              const allChs={};
              CH.forEach(ch=>{allChs[ch.id]={n:ch.n,sh:ch.sh,bud:0,wtrp:0,wgrp:0};});
              let grandBud=0,grandTrp=0,grandGrp=0;
              flights.forEach(f=>{
                const r=flightResults[f.id];if(!r)return;
                r.chs.forEach(cr=>{allChs[cr.ch.id].bud+=cr.b;allChs[cr.ch.id].wtrp+=cr.wtrp;allChs[cr.ch.id].wgrp+=cr.wgrp;});
                grandBud+=r.tot.bud;grandTrp+=r.tot.wtrp;grandGrp+=r.tot.wgrp;
              });
              // Per-brand channel aggregates
              const brandChData={};
              bOrder.forEach(b=>{
                const ca={};CH.forEach(ch=>{ca[ch.id]={n:ch.n,sh:ch.sh,bud:0,wtrp:0,wgrp:0};});
                bMap[b].flights.forEach(f=>{
                  const r=flightResults[f.id];if(!r)return;
                  r.chs.forEach(cr=>{ca[cr.ch.id].bud+=cr.b;ca[cr.ch.id].wtrp+=cr.wtrp;ca[cr.ch.id].wgrp+=cr.wgrp;});
                });
                brandChData[b]=ca;
              });
              // Sales house groups
              const shData={};SH_LIST.forEach(sh=>{shData[sh]={bud:0,wtrp:0,wgrp:0,chs:[]};});
              Object.values(allChs).forEach(ch=>{
                if(ch.bud<=0)return;
                shData[ch.sh].bud+=ch.bud;shData[ch.sh].wtrp+=ch.wtrp;shData[ch.sh].wgrp+=ch.wgrp;shData[ch.sh].chs.push(ch);
              });
              const grandCppTrp=grandTrp>0?grandBud/grandTrp:0;
              const grandCppGrp=grandGrp>0?grandBud/grandGrp:0;
              const grandAff=grandGrp>0?grandTrp/grandGrp*100:0;
              return(
                <div>
                  {/* Grand total KPI row */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(110px,1fr))',gap:6,marginBottom:14}}>
                    {[
                      {l:'Total Budget',v:fe(grandBud)},
                      {l:'Total wTRP',v:f0(grandTrp)},
                      {l:'Total wGRP',v:f0(grandGrp)},
                      {l:'CPP/wTRP',v:fe2(grandCppTrp)},
                      {l:'CPP/wGRP',v:fe2(grandCppGrp)},
                      {l:'Avg Affinity',v:f0(grandAff)},
                      {l:'Flights',v:flights.length},
                    ].map(k=>(
                      <div key={k.l} style={{padding:'7px 10px',background:'var(--text)',borderRadius:8,border:'2px solid var(--gold-border)'}}>
                        <div style={{fontSize:9,color:'var(--gold-light)',textTransform:'uppercase',letterSpacing:'.07em',marginBottom:2}}>{k.l}</div>
                        <div className="mono" style={{fontSize:14,fontWeight:700,color:'var(--gold-accent)'}}>{k.v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Full breakdown table */}
                  <div className="card" style={{overflow:'hidden',marginBottom:14}}>
                    <div style={{padding:'8px 14px',borderBottom:'2px solid var(--gold-border)',fontWeight:700,fontSize:11,color:'var(--gold-dark)',background:'var(--gold-bg)'}}>
                      Deal Summary — by Sales House & Channel
                    </div>
                    <div style={{overflowX:'auto'}}>
                      <table>
                        <thead><tr>
                          <th style={{minWidth:160}}>Channel</th>
                          <th>Budget</th><th>Bud%</th>
                          <th>wGRP</th><th>wGRP%</th><th>CPP/wGRP</th>
                          <th>wTRP</th><th>wTRP%</th><th>CPP/wTRP</th>
                          <th>Affinity</th>
                        </tr></thead>
                        <tbody>
                          {SH_LIST.map(sh=>{
                            const shd=shData[sh];if(!shd||shd.bud<=0)return null;
                            const shChs=shd.chs.sort((a,b)=>b.bud-a.bud);
                            return(
                              <React.Fragment key={sh}>
                                {/* Sales house sub-header */}
                                <tr>
                                  <td colSpan={10} style={{padding:'5px 10px',background:'var(--text)',color:'var(--gold-accent)',fontWeight:700,fontSize:10,letterSpacing:'.08em',textTransform:'uppercase'}}>
                                    {sh}
                                  </td>
                                </tr>
                                {shChs.map((ch,ci)=>{
                                  const cppTrp=ch.wtrp>0?ch.bud/ch.wtrp:0;
                                  const cppGrp=ch.wgrp>0?ch.bud/ch.wgrp:0;
                                  const aff=ch.wgrp>0?ch.wtrp/ch.wgrp*100:0;
                                  const fullCh=CH.find(x=>x.n===ch.n);
                                  return(
                                    <tr key={ch.n} style={{background:ci%2===0?'var(--surface)':'var(--surface2)'}}>
                                      <td style={{fontWeight:500,paddingLeft:20}}>
                                        <div>{ch.n}</div>
                                      </td>
                                      <td className="mono" style={{fontWeight:600,color:'var(--gold-dark)'}}>{fe(ch.bud)}</td>
                                      <td className="mono" style={{color:'var(--text2)'}}>{grandBud>0?(ch.bud/grandBud*100).toFixed(1)+'%':'—'}</td>
                                      <td className="mono">{f0(ch.wgrp)}</td>
                                      <td className="mono" style={{color:'var(--text2)'}}>{grandGrp>0?(ch.wgrp/grandGrp*100).toFixed(1)+'%':'—'}</td>
                                      <td className="mono" style={{color:'var(--gold)'}}>{fe2(cppGrp)}</td>
                                      <td className="mono" style={{fontWeight:600}}>{f0(ch.wtrp)}</td>
                                      <td className="mono" style={{color:'var(--text2)'}}>{grandTrp>0?(ch.wtrp/grandTrp*100).toFixed(1)+'%':'—'}</td>
                                      <td className="mono" style={{color:'var(--gold)'}}>{fe2(cppTrp)}</td>
                                      <td><span className="bdg bdg-gold">{f0(aff)}</span></td>
                                    </tr>
                                  );
                                })}
                                {/* Sales house subtotal */}
                                <tr style={{background:'var(--gold-bg)',fontWeight:600}}>
                                  <td style={{paddingLeft:14,fontWeight:700,fontSize:11}}>{sh} total</td>
                                  <td className="mono" style={{fontWeight:700,color:'var(--gold-dark)'}}>{fe(shd.bud)}</td>
                                  <td className="mono" style={{color:'var(--text2)'}}>{grandBud>0?(shd.bud/grandBud*100).toFixed(1)+'%':'—'}</td>
                                  <td className="mono" style={{fontWeight:700}}>{f0(shd.wgrp)}</td>
                                  <td className="mono" style={{color:'var(--text2)'}}>{grandGrp>0?(shd.wgrp/grandGrp*100).toFixed(1)+'%':'—'}</td>
                                  <td className="mono" style={{color:'var(--gold)'}}>{fe2(shd.wgrp>0?shd.bud/shd.wgrp:0)}</td>
                                  <td className="mono" style={{fontWeight:700}}>{f0(shd.wtrp)}</td>
                                  <td className="mono" style={{color:'var(--text2)'}}>{grandTrp>0?(shd.wtrp/grandTrp*100).toFixed(1)+'%':'—'}</td>
                                  <td className="mono" style={{color:'var(--gold)'}}>{fe2(shd.wtrp>0?shd.bud/shd.wtrp:0)}</td>
                                  <td><span className="bdg bdg-gold">{shd.wgrp>0?f0(shd.wtrp/shd.wgrp*100):'—'}</span></td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                          {/* Grand total */}
                          <tr className="tr-total">
                            <td style={{fontWeight:800,fontSize:12}}>GRAND TOTAL</td>
                            <td className="mono" style={{fontWeight:800,color:'var(--gold-dark)'}}>{fe(grandBud)}</td>
                            <td className="mono">100%</td>
                            <td className="mono" style={{fontWeight:800}}>{f0(grandGrp)}</td>
                            <td className="mono">100%</td>
                            <td className="mono" style={{color:'var(--gold)',fontWeight:700}}>{fe2(grandCppGrp)}</td>
                            <td className="mono" style={{fontWeight:800}}>{f0(grandTrp)}</td>
                            <td className="mono">100%</td>
                            <td className="mono" style={{color:'var(--gold)',fontWeight:700}}>{fe2(grandCppTrp)}</td>
                            <td><span className="bdg bdg-gold">{f0(grandAff)}</span></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Per-Brand summary cards */}
                  {bOrder.map(brand=>{
                    const bd=bMap[brand];
                    const bCppTrp=bd.wtrp>0?bd.bud/bd.wtrp:0;
                    const bCppGrp=bd.wgrp>0?bd.bud/bd.wgrp:0;
                    const bAff=bd.wgrp>0?bd.wtrp/bd.wgrp*100:0;
                    const[bR1,bR3]=rch(bd.wtrp);
                    const bShData={};SH_LIST.forEach(sh=>{bShData[sh]={bud:0,wtrp:0,wgrp:0,chs:[]};});
                    Object.values(brandChData[brand]||{}).forEach(ch=>{
                      if(ch.bud<=0)return;
                      bShData[ch.sh].bud+=ch.bud;bShData[ch.sh].wtrp+=ch.wtrp;bShData[ch.sh].wgrp+=ch.wgrp;bShData[ch.sh].chs.push(ch);
                    });
                    return(
                      <div key={brand} className="card" style={{overflow:'hidden',marginBottom:14}}>
                        <div style={{padding:'8px 14px',borderBottom:'2px solid var(--gold-border)',background:'var(--gold-bg)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                          <span style={{fontWeight:800,fontSize:12,color:'var(--gold-dark)'}}>{brand}</span>
                          <span style={{fontSize:10,color:'var(--text3)'}}>{bd.flights.length} flight{bd.flights.length!==1?'s':''}</span>
                        </div>
                        <div style={{display:'flex',borderBottom:'1px solid var(--border)'}}>
                          {[
                            {l:'Budget',v:fe(bd.bud),c:'var(--gold-dark)'},
                            {l:'Bud%',v:grandBud>0?(bd.bud/grandBud*100).toFixed(1)+'%':'—',c:'var(--text2)'},
                            {l:'wTRP',v:f0(bd.wtrp),c:'var(--text)'},
                            {l:'CPP/TRP',v:fe2(bCppTrp),c:'var(--gold)'},
                            {l:'wGRP',v:f0(bd.wgrp),c:'var(--text2)'},
                            {l:'CPP/GRP',v:fe2(bCppGrp),c:'var(--gold)'},
                            {l:'Avg AFF',v:fp1(bAff),c:'var(--text2)'},
                            {l:'Reach 1+',v:fp1(bR1),c:'var(--gold-accent)'},
                            {l:'Reach 3+',v:fp1(bR3),c:'var(--teal)'},
                          ].map(k=>(
                            <div key={k.l} style={{flex:1,padding:'6px 8px',borderRight:'1px solid var(--border)',textAlign:'center'}}>
                              <div style={{fontSize:8,color:'var(--text3)',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:2}}>{k.l}</div>
                              <div className="mono" style={{fontSize:12,fontWeight:700,color:k.c}}>{k.v}</div>
                            </div>
                          ))}
                        </div>
                        <div style={{overflowX:'auto'}}>
                          <table>
                            <thead><tr>
                              <th style={{minWidth:160}}>Channel</th>
                              <th>Budget</th><th>Bud%</th>
                              <th>wGRP</th><th>wGRP%</th><th>CPP/wGRP</th>
                              <th>wTRP</th><th>wTRP%</th><th>CPP/wTRP</th>
                              <th>Affinity</th>
                            </tr></thead>
                            <tbody>
                              {SH_LIST.map(sh=>{
                                const shd=bShData[sh];if(!shd||shd.bud<=0)return null;
                                return(
                                  <React.Fragment key={sh}>
                                    <tr><td colSpan={10} style={{padding:'4px 10px',background:'var(--text)',color:'var(--gold-accent)',fontWeight:700,fontSize:10,letterSpacing:'.08em',textTransform:'uppercase'}}>{sh}</td></tr>
                                    {shd.chs.sort((a,b)=>b.bud-a.bud).map((ch,ci)=>{
                                      const cppTrp=ch.wtrp>0?ch.bud/ch.wtrp:0;
                                      const cppGrp=ch.wgrp>0?ch.bud/ch.wgrp:0;
                                      const aff=ch.wgrp>0?ch.wtrp/ch.wgrp*100:0;
                                      return(
                                        <tr key={ch.n} style={{background:ci%2===0?'var(--surface)':'var(--surface2)'}}>
                                          <td style={{fontWeight:500,paddingLeft:20}}>{ch.n}</td>
                                          <td className="mono" style={{fontWeight:600,color:'var(--gold-dark)'}}>{fe(ch.bud)}</td>
                                          <td className="mono" style={{color:'var(--text2)'}}>{bd.bud>0?(ch.bud/bd.bud*100).toFixed(1)+'%':'—'}</td>
                                          <td className="mono">{f0(ch.wgrp)}</td>
                                          <td className="mono" style={{color:'var(--text2)'}}>{bd.wgrp>0?(ch.wgrp/bd.wgrp*100).toFixed(1)+'%':'—'}</td>
                                          <td className="mono" style={{color:'var(--gold)'}}>{fe2(cppGrp)}</td>
                                          <td className="mono" style={{fontWeight:600}}>{f0(ch.wtrp)}</td>
                                          <td className="mono" style={{color:'var(--text2)'}}>{bd.wtrp>0?(ch.wtrp/bd.wtrp*100).toFixed(1)+'%':'—'}</td>
                                          <td className="mono" style={{color:'var(--gold)'}}>{fe2(cppTrp)}</td>
                                          <td><span className="bdg bdg-gold">{f0(aff)}</span></td>
                                        </tr>
                                      );
                                    })}
                                    <tr style={{background:'var(--gold-bg)',fontWeight:600}}>
                                      <td style={{paddingLeft:14,fontWeight:700,fontSize:11}}>{sh} total</td>
                                      <td className="mono" style={{fontWeight:700,color:'var(--gold-dark)'}}>{fe(shd.bud)}</td>
                                      <td className="mono" style={{color:'var(--text2)'}}>{bd.bud>0?(shd.bud/bd.bud*100).toFixed(1)+'%':'—'}</td>
                                      <td className="mono" style={{fontWeight:700}}>{f0(shd.wgrp)}</td>
                                      <td className="mono" style={{color:'var(--text2)'}}>{bd.wgrp>0?(shd.wgrp/bd.wgrp*100).toFixed(1)+'%':'—'}</td>
                                      <td className="mono" style={{color:'var(--gold)'}}>{fe2(shd.wgrp>0?shd.bud/shd.wgrp:0)}</td>
                                      <td className="mono" style={{fontWeight:700}}>{f0(shd.wtrp)}</td>
                                      <td className="mono" style={{color:'var(--text2)'}}>{bd.wtrp>0?(shd.wtrp/bd.wtrp*100).toFixed(1)+'%':'—'}</td>
                                      <td className="mono" style={{color:'var(--gold)'}}>{fe2(shd.wtrp>0?shd.bud/shd.wtrp:0)}</td>
                                      <td><span className="bdg bdg-gold">{shd.wgrp>0?f0(shd.wtrp/shd.wgrp*100):'—'}</span></td>
                                    </tr>
                                  </React.Fragment>
                                );
                              })}
                              <tr className="tr-total">
                                <td style={{fontWeight:800}}>{brand} TOTAL</td>
                                <td className="mono" style={{fontWeight:800,color:'var(--gold-dark)'}}>{fe(bd.bud)}</td>
                                <td className="mono">100%</td>
                                <td className="mono" style={{fontWeight:800}}>{f0(bd.wgrp)}</td>
                                <td className="mono">100%</td>
                                <td className="mono" style={{color:'var(--gold)',fontWeight:700}}>{fe2(bCppGrp)}</td>
                                <td className="mono" style={{fontWeight:800}}>{f0(bd.wtrp)}</td>
                                <td className="mono">100%</td>
                                <td className="mono" style={{color:'var(--gold)',fontWeight:700}}>{fe2(bCppTrp)}</td>
                                <td><span className="bdg bdg-gold">{f0(bAff)}</span></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── MONTHLY VIEW ── */}
            {outputTab==='monthly'&&(
              <div>
                <div style={{marginBottom:10,fontWeight:600,fontSize:12,color:'var(--text2)'}}>Monthly Budget & TRP Overview</div>
                <div style={{marginBottom:16}}>
                  {/* Budget bar chart */}
                  <div className="card" style={{padding:'14px',marginBottom:10}}>
                    <div style={{fontWeight:600,fontSize:11,color:'var(--gold-dark)',marginBottom:10}}>Budget distribution by month</div>
                    <div style={{display:'flex',gap:6,alignItems:'flex-end',height:80}}>
                      {MONTHS.map(m=>{
                        const d=monthlyData[m];
                        const maxB=Math.max(...Object.values(monthlyData).map(x=>x.bud),1);
                        const h=Math.max(2,(d.bud/maxB)*72);
                        return(
                          <div key={m} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center'}}>
                            <div style={{width:'100%',height:72,display:'flex',flexDirection:'column',justifyContent:'flex-end'}}>
                              {d.flights.filter(x=>x.frac>0).map(({f,frac},fi)=>(
                                <div key={f.id} style={{width:'100%',height:(frac*h)+'px',background:f.color,borderRadius:fi===0?'3px 3px 0 0':0}}/>
                              ))}
                            </div>
                            <div style={{fontSize:8,color:'var(--text3)',marginTop:2}}>{MN[m]}</div>
                            {d.bud>0&&<div className="mono" style={{fontSize:8,color:'var(--text3)'}}>{Math.round(d.bud/1000)}K</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="card" style={{overflow:'hidden'}}>
                  <table>
                    <thead><tr>
                      <th>Month</th><th>K coeff</th><th style={{color:'var(--gold-dark)'}}>Budget</th>
                      <th style={{color:'var(--gold-accent)'}}>30"TRP</th><th>Flights</th>
                    </tr></thead>
                    <tbody>
                      {MONTHS.map((m,mi)=>{
                        const d=monthlyData[m];
                        const K=MW[m]?MW[m]/.0881-1:0;// relative to March baseline
                        return(
                          <tr key={m} style={{opacity:d.bud>0?1:.35}}>
                            <td style={{fontWeight:500}}>{MN[m]}</td>
                            <td><Bdg v={(K>=0?'+':'')+Math.round(K*100)+'%'} t={K>.1?'amber':K<-.1?'blue':'gray'}/></td>
                            <td className="mono" style={{color:'var(--gold-dark)',fontWeight:d.bud>0?600:400}}>{d.bud>0?fe(d.bud):'—'}</td>
                            <td className="mono" style={{color:'var(--gold-accent)'}}>{d.wtrp>0?f0(d.wtrp):'—'}</td>
                            <td>{d.flights.filter(x=>x.frac>0).map(({f})=>(
                              <span key={f.id} style={{display:'inline-flex',alignItems:'center',gap:3,marginRight:4,fontSize:10,color:'var(--text2)'}}>
                                <span style={{width:6,height:6,borderRadius:2,background:f.color}}/>
                                {f.label}
                              </span>
                            ))}</td>
                          </tr>
                        );
                      })}
                      <tr className="tr-total">
                        <td>TOTAL</td><td/>
                        <td className="mono" style={{color:'var(--gold-dark)'}}>{fe(planTot.bud)}</td>
                        <td className="mono" style={{color:'var(--gold-accent)'}}>{f0(planTot.wtrp)}</td>
                        <td>{flights.length} flights</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}


            {/* ── ACTUALS VIEW ── */}
            {outputTab==='actuals'&&(
              <ActualsView
                flights={flights}
                flightResults={flightResults}
                baseline={baseline}
                actuals={actuals}
                setActuals={setActuals}
                onSetBaseline={()=>{
                  const snap={};
                  flights.forEach(f=>{const r=flightResults[f.id];if(r)snap[f.id]={...r,flightLabel:f.label,flightColor:f.color};});
                  setBaseline(snap);
                  alert('Baseline locked! Current plan is now the approved baseline.');
                }}
              />
            )}

            {/* ── WHAT IF VIEW ── */}
            {outputTab==='whatif'&&(
              <div>
                <div style={{display:'flex',gap:8,alignItems:'center',marginBottom:12}}>
                  <span style={{fontWeight:600,fontSize:12,color:'var(--text2)'}}>What If — modify flights and compare vs current plan</span>
                  {!wiMode&&<button className="btn btn-ghost btn-sm" style={{color:'var(--gold-accent)',borderColor:'var(--purple-dim)'}} onClick={startWhatIf}>Start What If</button>}
                  {wiMode&&<>
                    <button className='btn btn-ghost btn-sm' style={{borderColor:'var(--gold-border)'}} onClick={()=>setWiMode(false)}>✓ Keep current</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>{setWiFlights(JSON.parse(JSON.stringify(flights)));setWiMode(true);}}>Reset</button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--red)',borderColor:'var(--red-dim)'}} onClick={()=>{setWiMode(false);setWiFlights(null);}}>✕ Cancel</button>
                  </>}
                </div>
                {!wiMode&&<div style={{color:'var(--text3)',fontSize:11}}>Click "Start What If" to create a parallel scenario you can modify without affecting the main plan.</div>}
                      {wiMode&&wiFlights&&wiResults&&(
                  <div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                      {[['Current Plan',flights,flightResults,false],['What If Scenario',wiFlights,wiResults,true]].map(([lbl,fls,res,isWi])=>(
                        <div key={lbl}>
                          <div style={{fontWeight:600,fontSize:11,color:isWi?'var(--purple)':'var(--blue)',marginBottom:6,paddingBottom:4,borderBottom:'1px solid var(--border)'}}>{lbl}</div>
                          {fls.map(f=>{
                            const r=res[f.id];if(!r)return null;
                            return(
                              <div key={f.id} className="card2" style={{padding:'8px 10px',marginBottom:6,borderLeft:'2px solid '+f.color}}>
                                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:isWi?6:0}}>
                                  <span style={{fontWeight:600,fontSize:11}}>{f.label}</span>
                                  <div style={{display:'flex',gap:8}}>
                                    <span className="mono" style={{fontSize:11,color:'var(--gold-dark)'}}>{fe(r.tot.bud)}</span>
                                    <span className="mono" style={{fontSize:11,color:'var(--gold-accent)'}}>{f0(r.tot.wtrp)} TRP</span>
                                  </div>
                                </div>
                                {isWi&&(
                                  <div style={{display:'flex',gap:6}}>
                                    <ModeSw val={f.mode} onChange={m=>updateWiFlight(f.id,{mode:m})}/>
                                    {f.mode==='budget'&&<input className="inn" type="number" step={1000} value={f.budget} style={{width:80}} onChange={e=>updateWiFlight(f.id,{budget:+e.target.value})}/>}
                                    {f.mode==='trp'&&<><input className="inn" type="number" step={10} value={f.trpTarget} style={{width:70}} onChange={e=>updateWiFlight(f.id,{trpTarget:+e.target.value})}/><span style={{fontSize:9,color:'var(--text3)'}}>TRP</span></>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div style={{display:'flex',gap:12,padding:'8px 10px',background:'var(--gold-bg)',borderRadius:6,marginTop:4}}>
                            {[{l:'Total Budget',v:fe(Object.values(res).filter(Boolean).reduce((s,r)=>s+r.tot.bud,0)),c:'var(--blue)'},{l:'Total 30"TRP',v:f0(Object.values(res).filter(Boolean).reduce((s,r)=>s+r.tot.wtrp,0)),c:'var(--purple)'}].map(k=>(
                              <div key={k.l}>
                                <div style={{fontSize:9,color:'var(--text3)',textTransform:'uppercase'}}>{k.l}</div>
                                <div className="mono" style={{fontSize:13,fontWeight:700,color:k.c}}>{k.v}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Delta table */}
                    <div className="card" style={{overflow:'hidden'}}>
                      <div style={{padding:'8px 14px',borderBottom:'1px solid var(--border)',fontWeight:600,fontSize:11,color:'var(--text2)'}}>Delta: What If vs Current Plan (per flight)</div>
                      <table>
                        <thead><tr><th>Flight</th><th>Budget delta</th><th>TRP delta</th><th>CPP delta</th></tr></thead>
                        <tbody>
                          {flights.map(f=>{
                            const cur=flightResults[f.id]&&flightResults[f.id].tot;
                            const wi=wiResults[f.id]&&wiResults[f.id].tot;
                            if(!cur||!wi)return null;
                            const dBud=wi.bud-cur.bud,dTrp=wi.wtrp-cur.wtrp,dCpp=wi.cpp30T-cur.cpp30T;
                            return(
                              <tr key={f.id}>
                                <td><span style={{color:f.color,fontWeight:500}}>{f.label}</span></td>
                                <td className="mono" style={{color:dBud<0?'var(--green)':dBud>0?'var(--red)':'var(--text3)'}}>{dBud===0?'—':(dBud>0?'+':'')+fe(dBud)}</td>
                                <td className="mono" style={{color:dTrp>0?'var(--green)':dTrp<0?'var(--red)':'var(--text3)'}}>{dTrp===0?'—':(dTrp>0?'+':'')+f0(dTrp)+' TRP'}</td>
                                <td className="mono" style={{color:dCpp<0?'var(--green)':dCpp>0?'var(--red)':'var(--text3)'}}>{dCpp===0?'—':(dCpp>0?'+':'')+fe2(dCpp)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* INPUTS DRAWER */}
      <InputsDrawer open={drawerOpen} onClose={()=>setDrawerOpen(false)}
        audiences={audiences} setAudiences={setAudiences}
        pcData={pcData} setPcData={setPcData}
        globalMix={globalMix} setGlobalMix={setGlobalMix}
        planDiscRows={planDiscRows} setPlanDiscRows={setPlanDiscRows}
        globalChannels={globalChannels} setGlobalChannels={setGlobalChannels}
        flights={flights} flightResults={flightResults}
        mixInputMode={mixInputMode} setMixInputMode={setMixInputMode}
        chPrices={chPrices} setChPrices={setChPrices}
        discScales={discScales} setDiscScales={setDiscScales}
        onPriceListApplied={()=>{ window.dispatchEvent(new CustomEvent('tvplan:autoSave')); }}
        seasonCoeffs={seasonCoeffs} setSeasonCoeffs={setSeasonCoeffs}
        audSeasonData={audSeasonData} setAudSeasonData={setAudSeasonData}
        chBuyAudSel={chBuyAudSel} setChBuyAudSel={setChBuyAudSel} audiences={audiences}
      />
    </div>
    {showWizard&&(
      <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,background:"rgba(0,0,0,0.7)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={e=>{if(e.target===e.currentTarget)setShowWizard(false);}}>
        <div style={{background:"var(--surface)",border:"1px solid var(--gold-border)",borderRadius:12,padding:28,width:440,maxWidth:"90vw"}}>
          <div style={{fontWeight:700,fontSize:15,color:"var(--gold)",marginBottom:4}}>&#9889; Quick Setup</div>
          <div style={{fontSize:11,color:"var(--text3)",marginBottom:20}}>Auto-generate brands and flights. Existing flights will be replaced.</div>
          <QuickWizardForm onDone={(newFlights)=>{setFlights(newFlights);setShowWizard(false);}} onCancel={()=>setShowWizard(false)} />
        </div>
      </div>
    )}
  </>);
}


